import { useState, useEffect, useCallback, useMemo, useRef, Fragment } from "react";
import { puedeEditarModulo, puedeMarcarRevisionBacklog } from "./permisos";

// ─────────────────────────────────────────
// Paleta — colores de marca Claro (igual que el resto de la app) +
// paleta de estado / categórica validada para accesibilidad (dataviz)
// ─────────────────────────────────────────
const CLARO_RED   = "#E30613";
const CLARO_RED_DEEP = "#a5050f"; // variante oscura: degradados, foco, hover
const CLARO_DARK  = "#0f172a";
const CLARO_MUTED = "#64748b";
const CLARO_MUTED2 = "#97a1b3"; // gris claro secundario (placeholders, iconos neutros)
const SURFACE     = "#fcfcfb";
const BORDER      = "#e6e9f0";
const BORDER_SOFT = "#eef0f4";
const GOOD        = "#15803d";   // cumple SLA
const CRITICAL    = "#dc2626";   // no cumple SLA
const AMBER       = "#b45309";
const BLUE        = "#2563eb";
const CAT_COLORS  = ["#2a78d6", "#eb6834", "#1baf7a", "#eda100", "#e87ba4", "#008300", "#4a3aa7", "#e34948"];
// Sombras de dos capas (tarjetas contenedoras) vs. una sola capa muy sutil
// (tarjetas KPI "planas"), para que no todo en la pantalla se vea igual.
const SHADOW_SM = "0 1px 2px rgba(15,23,42,0.05)";
const SHADOW_MD = "0 1px 2px rgba(15,23,42,0.04), 0 8px 20px -10px rgba(15,23,42,0.14)";
const FOCUS_RING = "0 0 0 3px rgba(227,6,19,0.16)";
const FONT_FAMILY = '"Plus Jakarta Sans",-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,Helvetica,Arial,sans-serif';

// ─────────────────────────────────────────
// Set de iconos de línea (SVG inline, sin dependencias) — mismo lenguaje
// visual en toda la pantalla: iconos de trazo simple en "currentColor".
// ─────────────────────────────────────────
const ICON_PATHS = {
  grid: <><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></>,
  bars: <><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M3 9h18M9 21V9" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" /></>,
  dash: <><circle cx="12" cy="12" r="9" /><path d="M8 12h8" /></>,
  check: <path d="M20 6 9 17l-5-5" />,
  alert: <><path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
  badgeCheck: <><path d="m9 12 2 2 4-4" /><path d="M12 3a9 9 0 1 0 9 9" /><path d="M21 3v6h-6" /></>,
  arrowRight: <><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>,
  flag: <><path d="M4 21V4" /><path d="M4 4h14l-3 4.5L18 13H4" /></>,
  ban: <><circle cx="12" cy="12" r="9" /><path d="m5.5 5.5 13 13" /></>,
  target: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4.5" /><circle cx="12" cy="12" r=".6" fill="currentColor" /></>,
  search: <><circle cx="11" cy="11" r="7" /><path d="m21 21-4.3-4.3" /></>,
  x: <><circle cx="12" cy="12" r="9" /><path d="m9 9 6 6m0-6-6 6" /></>,
  folder: <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2Z" />,
  trendUp: <><path d="m3 17 6-6 4 4 8-8" /><path d="M15 7h6v6" /></>,
  trendDown: <><path d="m3 7 6 6 4-4 8 8" /><path d="M15 17h6v-6" /></>,
  layers: <><path d="M3 3v18h18" /><path d="M7 15v3M12 11v7M17 7v11" /></>,
  history: <><path d="M3 12a9 9 0 1 0 3-6.7" /><path d="M3 4v5h5" /><path d="M12 7v5l3 3" /></>,
  download: <><path d="M12 3v12" /><path d="m7 10 5 5 5-5" /><path d="M5 21h14" /></>,
};
function Ic({ name, size = 16, color, style }) {
  const body = ICON_PATHS[name];
  if (!body) return null;
  return (
    <svg
      viewBox="0 0 24 24" width={size} height={size}
      stroke={color || "currentColor"} fill="none" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, verticalAlign: "-3px", ...style }}
    >
      {body}
    </svg>
  );
}

// Normaliza el texto de "trabajo_cumple_sla" (mayúsculas + sin espacios extra) antes
// de compararlo, porque el Excel puede traer "Cumple"/"CUMPLE"/"cumple " indistinto.
const normalizaSla = (v) => (v || "").toString().trim().toUpperCase();
const SLA_CUMPLE_VALORES = ["CUMPLE", "SI", "SI CUMPLE"];
const SLA_NO_CUMPLE_VALORES = ["NO CUMPLE", "NO"];
const slaCumple = (v) => SLA_CUMPLE_VALORES.includes(normalizaSla(v));
const slaNoCumple = (v) => SLA_NO_CUMPLE_VALORES.includes(normalizaSla(v));

// ─────────────────────────────────────────
// Validación estructurada del backlog (columna "Categoría" + "Sub-estado" +
// "Observaciones"), editable solo por Admin / Jefe de Zona. Las categorías
// agrupan el texto libre que antes traían los aliados en observaciones.
//
// El catálogo real vive en la base de datos (tabla backlog_categorias_catalogo,
// vía /backlog-categorias) para que el Administrador pueda agregar categorías
// o sub-categorías nuevas desde la misma página, sin tocar código. Esta lista
// es solo el respaldo inicial mientras carga esa consulta la primera vez.
// ─────────────────────────────────────────
const CATALOGO_RESPALDO = [
  ["Canceladas", "OT Cancelada"],
  ["Cerradas", "OT Cerrada"],
  ["En Ejecucion", "En Tratamiento"], ["En Ejecucion", "OT Conveniente"],
  ["Sin Gestion", "OT Agendada"], ["Sin Gestion", "OT Suspendida"],
  ["Sin Gestion", "Sin Capacidad Operativa"],
  ["Sin Gestion", "Sin visual en WF - Bandeja Aliado"],
  ["Sin Gestion", "Espera en Clareo de alarmas por energia"],
  ["Pendiente", "Actualizacion Zonificacion Jefaturas OTC"], ["Pendiente", "Caso Especial Terremoto"],
  ["Pendiente", "Control de Calidad x NOC"],
  ["Pendiente", "Escalamiento Gestion Trasversal"], ["Pendiente", "Escalamiento Tercero"],
  ["Pendiente", "Falta de Materiales"],
  ["Pendiente", "OT Duplicada"], ["Pendiente", "OT En gestion de Otro Aliado"],
  ["Pendiente", "Pendiente ZO/SO Owner de Zona"], ["Pendiente", "Permisos Administracion"],
  ["Pendiente", "Proceso de Certificacion x Preventivo"], ["Pendiente", "Soporte Helix / WF"],
  ["Pendiente", "Trabajo Preventivo Pendiente"],
].map(([categoria, sub_estado]) => ({ categoria, sub_estado }));

const CATEGORIA_COLOR = {
  "Sin Gestion": "#94a3b8",
  "En Ejecucion": "#2a78d6",
  "Pendiente": "#eb6834",
  "Canceladas": CRITICAL,
  "Cerradas": GOOD,
};

// Colores/íconos fijos para el campo "Estado" de seguimiento de la pestaña
// "OT en Revisión" — mismos 4 valores fijos que ESTADOS_REVISION.
const ESTADO_REVISION_COLOR = {
  "Sin escalar": "#94a3b8",
  "Escalado": "#eda100",
  "Resuelto": GOOD,
  "Otro": "#2a78d6",
};
const ESTADO_REVISION_ICONO = {
  "Sin escalar": "dash",
  "Escalado": "flag",
  "Resuelto": "check",
  "Otro": "layers",
};

// ─────────────────────────────────────────
// Pestaña "Comparación": qué cambió entre el corte anterior y el vigente
// ─────────────────────────────────────────
const TIPO_CAMBIO_INFO = {
  nuevo:       { label: "Nuevo",       color: "#2a78d6" },
  actualizado: { label: "Actualizado", color: "#eda100" },
  cerrado:     { label: "Cerrado",     color: GOOD },
  sin_cambio:  { label: "Sin cambio",  color: "#94a3b8" },
};

// ─────────────────────────────────────────
// Pestaña "Pronóstico": proyección de cierre de mes usando antiguedad_dias
// (sube 1 por cada día que la OT sigue abierta) contra sla_dia (la meta)
// ─────────────────────────────────────────
const PROYECCION_INFO = {
  ya_incumple:        { label: "Ya incumple",        color: CRITICAL },
  en_riesgo:          { label: "En riesgo",          color: "#eda100" },
  atencion:           { label: "Atención",           color: "#eab308" },
  cumple_proyectado:  { label: "Cumple proyectado",  color: GOOD },
  sin_meta:           { label: "Sin meta SLA",       color: CLARO_MUTED },
};
const formatoDiasVencer = (dias) => {
  if (dias === null || dias === undefined) return "—";
  const n = Number(dias);
  if (Number.isNaN(n)) return "—";
  if (n < 0) return `Vencida hace ${Math.abs(n)} d`;
  if (n === 0) return "Vence hoy";
  return `${n} d restantes`;
};
// El backend devuelve fechas tipo DATE serializadas como ISO ("2026-09-30T05:00:00.000Z");
// se toma solo la parte de fecha (los primeros 10 caracteres) para evitar que el huso
// horario del navegador la corra un día — nunca se pasa por `new Date()`.
const formatoFecha = (valor) => {
  if (!valor) return "—";
  const soloFecha = String(valor).slice(0, 10); // YYYY-MM-DD
  const partes = soloFecha.split("-");
  if (partes.length !== 3) return soloFecha;
  const [y, m, d] = partes;
  return `${d}/${m}/${y}`;
};

// Mismo criterio que formatoFecha (nunca pasa por `new Date()` para no
// correr el día por huso horario), pero con guiones (DD-MM-YYYY) — formato
// pedido para las fechas en todo el módulo de Backlog R3.
// Importante: el backend tiene un middleware global que, para el campo
// `fecha_corte` puntualmente, YA lo entrega pre-formateado como DD/MM/YYYY
// (con barras) en varias rutas — mientras que otras rutas (o campos como
// fecha_limite/fecha_objetivo) siguen entregando el ISO típico de MySQL
// (YYYY-MM-DD...). Por eso esta función reconoce los dos formatos de
// entrada y siempre entrega el mismo resultado: DD-MM-YYYY con guiones.
const formatoFechaGuion = (valor) => {
  if (!valor) return "—";
  const s = String(valor).trim();
  // Ya viene como DD/MM/YYYY (el middleware del backend reformatea así
  // "fecha_corte" en varias respuestas) — se respeta el orden día/mes/año
  // tal cual viene, solo se cambia la barra por guion.
  let m = s.match(/^(\d{2})\/(\d{2})\/(\d{4})/);
  if (m) return `${m[1]}-${m[2]}-${m[3]}`;
  // Formato ISO de MySQL: YYYY-MM-DD (con o sin hora/zona horaria después)
  m = s.slice(0, 10).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  return s;
};

// Línea de tiempo de historial (reutilizada por el modal de la pestaña Comparación)
function LineaTiempo({ eventos, cargando }) {
  if (cargando) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Cargando historial…</div>;
  if (!eventos || eventos.length === 0) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Sin historial de cambios registrado para esta OT.</div>;
  const CAMPO_LABEL = {
    avance: "Avance (aliado)",
    segunda_revision: "Segunda revisión",
    categoria_seguimiento: "Categoría",
    sub_estado_seguimiento: "Sub-estado",
    observaciones_seguimiento: "Observaciones",
  };
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      {eventos.map((e, i) => (
        <div key={i} style={{ display: "flex", gap: "14px" }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
            <div style={{ width: "11px", height: "11px", borderRadius: "50%", background: i === 0 ? CLARO_RED : "#cbd5e1", marginTop: "3px", flexShrink: 0 }} />
            {i < eventos.length - 1 && <div style={{ width: "2px", flex: 1, background: BORDER }} />}
          </div>
          <div style={{ paddingBottom: "18px" }}>
            <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "3px" }}>
              {e.fecha ? new Date(e.fecha).toLocaleString("es-CO") : "—"} · {e.nombre_usuario || "—"} ({e.rol || "—"})
            </div>
            <div style={{ background: i === 0 ? "#fef2f2" : "#f8fafc", border: `1px solid ${i === 0 ? "#fca5a5" : BORDER}`, borderRadius: "10px", padding: "10px 12px", fontSize: "12.5px", color: CLARO_DARK, maxWidth: "640px", wordBreak: "break-word", overflowWrap: "anywhere" }}>
              <strong>{CAMPO_LABEL[e.campo_modificado] || e.campo_modificado}:</strong>{" "}
              {e.valor_anterior ? <span style={{ color: CLARO_MUTED }}>{e.valor_anterior}</span> : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>vacío</span>}
              {" → "}
              <strong>{e.valor_nuevo || <span style={{ fontStyle: "italic" }}>vacío</span>}</strong>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// Evolución día a día de una OT: una fila por cada corte en que esa OT
// apareció en el tablero (backlog_om_fijo), desde el primer corte en que
// "nace" (arriba) hasta el corte más reciente / hoy (abajo). A diferencia de
// LineaTiempo (que es el log de ediciones manuales de un usuario), esto es
// el estado real que trajo cada corte.
// `onCopiar` (opcional): si se pasa, se agrega una columna "Acción" con un
// botón "Copiar a hoy →" SOLO en la fila del corte inmediatamente anterior
// al más reciente — para traer su Categoría/Sub-estado/Observaciones al
// corte de hoy sin tener que volver a escribirlas (reemplaza la columna
// "Copiar" que tenía antes la tabla principal de Avances).
function TablaEvolucionOt({ filas, cargando, onCopiar, puedeCopiar, copiando }) {
  if (cargando) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Cargando evolución…</div>;
  if (!filas || filas.length === 0) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Sin cortes registrados para esta OT.</div>;
  const thSt = { background: "#C00000", color: "white", fontWeight: "700", padding: "9px 10px", textAlign: "left", whiteSpace: "nowrap", fontSize: "11px", letterSpacing: "0.01em", borderBottom: "2px solid #8c0000" };
  const tdSt = { padding: "8px 10px", fontSize: "12.5px", color: "#1a1a1a", whiteSpace: "nowrap", borderBottom: `1px solid ${BORDER_SOFT}` };
  const mostrarAccion = puedeCopiar && filas.length >= 2;
  return (
    <div style={{ overflowX: "auto", borderRadius: "10px", border: `1px solid ${BORDER}` }}>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={thSt}>Corte</th>
            <th style={thSt}>Antigüedad</th>
            <th style={thSt}>SLA</th>
            <th style={thSt}>Rango</th>
            <th style={thSt}>Estado OM</th>
            <th style={thSt}>Categoría</th>
            <th style={thSt}>Sub-estado</th>
            <th style={thSt}>Observaciones</th>
            {mostrarAccion && <th style={thSt}>Acción</th>}
          </tr>
        </thead>
        <tbody>
          {filas.map((f, i) => {
            const esHoy = i === filas.length - 1;
            const esCorteAnterior = mostrarAccion && i === filas.length - 2;
            return (
              <tr key={`${f.fecha_corte}-${i}`} style={{ background: esHoy ? "#fef2f2" : "white" }}>
                <td style={{ ...tdSt, fontWeight: esHoy ? "800" : "600" }}>{formatoFechaGuion(f.fecha_corte)}{esHoy ? " (hoy)" : ""}</td>
                <td style={{ ...tdSt, textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{f.antiguedad_dias ?? "—"} d</td>
                <td style={tdSt}>
                  {f.trabajo_cumple_sla
                    ? <Tag text={f.trabajo_cumple_sla} color={slaCumple(f.trabajo_cumple_sla) ? GOOD : CRITICAL} />
                    : <span style={{ color: CLARO_MUTED }}>—</span>}
                </td>
                <td style={tdSt}>{f.rangos || "—"}</td>
                <td style={tdSt}>{f.estado_om || "—"}</td>
                <td style={{ ...tdSt, whiteSpace: "normal" }}>{f.categoria_seguimiento || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin categorizar</span>}</td>
                <td style={{ ...tdSt, whiteSpace: "normal" }}>{f.sub_estado_seguimiento || <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                <td style={{ ...tdSt, whiteSpace: "normal", minWidth: "280px", maxWidth: "340px", wordBreak: "break-word", overflowWrap: "anywhere" }}>{f.observaciones_seguimiento || <span style={{ color: "#cbd5e1" }}>—</span>}</td>
                {mostrarAccion && (
                  <td style={tdSt}>
                    {esCorteAnterior && (
                      <button
                        onClick={() => onCopiar(f)}
                        disabled={copiando}
                        title="Copia la Categoría, Sub-estado y Observaciones de este corte al corte de hoy"
                        style={{
                          border: "none", background: copiando ? "#9ca3af" : CLARO_RED, color: "white",
                          borderRadius: "6px", padding: "5px 10px", cursor: copiando ? "default" : "pointer",
                          fontSize: "11px", fontWeight: "700", whiteSpace: "nowrap",
                        }}
                      >
                        {copiando ? "Copiando…" : "Copiar a hoy →"}
                      </button>
                    )}
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

const card = {
  background: "white", borderRadius: "14px", border: `1px solid ${BORDER}`,
  boxShadow: SHADOW_MD, padding: "22px",
};

// ─────────────────────────────────────────
// COMPONENTES DE APOYO
// ─────────────────────────────────────────
// Tarjetas KPI: a propósito tienen un tratamiento distinto al de las
// tarjetas contenedoras (`card`) — más planas, sin sombra pesada, con una
// franja de color arriba y un icono que refuerza el significado — para que
// no todas las cajas de la pantalla se vean igual.
function KpiCard({ label, value, sub, color = CLARO_DARK, icon, onClick, activo }) {
  const esClickeable = typeof onClick === "function";
  return (
    <div
      onClick={onClick}
      title={esClickeable ? (activo ? "Clic para quitar el filtro" : "Clic para filtrar la tabla con este estado") : undefined}
      style={{
        background: "white", flex: 1, minWidth: "148px",
        borderRadius: "10px", padding: "11px 13px 10px",
        border: activo ? `2px solid ${color}` : `1px solid ${BORDER}`,
        borderTop: `3px solid ${color}`,
        boxShadow: activo ? `0 0 0 3px ${color}22` : SHADOW_SM,
        cursor: esClickeable ? "pointer" : "default",
        transition: "border-color 0.12s ease, box-shadow 0.12s ease",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "5px" }}>
        <div style={{ fontSize: "9.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
        {icon && <Ic name={icon} size={15} color={color} />}
      </div>
      <div style={{ fontSize: "21px", fontWeight: "800", letterSpacing: "-0.01em", color, fontVariantNumeric: "tabular-nums" }}>{value}</div>
      {sub && <div style={{ fontSize: "10.5px", color: CLARO_MUTED, marginTop: "3px", lineHeight: 1.3 }}>{sub}</div>}
    </div>
  );
}

// Escala semafórica de 3 tramos para los % de cumplimiento gerenciales
// (resumen ejecutivo del Pronóstico): >= 90% verde, 70%-89,99% ámbar fuerte
// (visible, no pastel), < 70% rojo.
function colorIndicadorSla(pct) {
  const p = Number(pct) || 0;
  if (p >= 90) return GOOD;
  if (p >= 70) return AMBER;
  return CRITICAL;
}

// Resumen ejecutivo del Pronóstico: responde de un vistazo "del total de OT
// actuales, ¿cuántas se vencerían y en cuánto quedaría el indicador si no se
// atienden?" — sin tener que sumar mentalmente las tarjetas de Ya
// incumple/En riesgo/Atención. Versión condensada (solo el número clave + una
// línea de contexto por lado), aprobada en la vista previa.
function ResumenEjecutivoPronostico({ noCumple, pctNoCumple, cumpleProyectado, pctProyectado, pctHoy, fechaObjetivo }) {
  const colorProyectado = colorIndicadorSla(pctProyectado);
  const colorHoy = colorIndicadorSla(pctHoy);
  return (
    <div style={{
      display: "flex", background: "white", borderRadius: "14px",
      border: `1px solid ${BORDER}`, borderLeft: `5px solid ${CLARO_RED}`,
      boxShadow: SHADOW_MD, overflow: "hidden", marginBottom: "16px",
    }}>
      <div style={{ flex: 1, padding: "16px 22px", display: "flex", alignItems: "center", gap: "14px" }}>
        <div style={{ width: "40px", height: "40px", borderRadius: "11px", background: "#fdecec", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Ic name="ban" size={19} color={CRITICAL} />
        </div>
        <div>
          <div style={{ fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>No cumplirían a fin de mes</div>
          <div style={{ fontSize: "26px", fontWeight: "800", letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", lineHeight: 1.1 }}>
            {noCumple}<span style={{ fontSize: "13.5px", fontWeight: "700", color: CLARO_MUTED, marginLeft: "4px" }}>OT · {pctNoCumple}%</span>
          </div>
          <div style={{ fontSize: "11.5px", color: CLARO_MUTED, marginTop: "2px" }}>Si nadie las atiende antes del {formatoFechaGuion(fechaObjetivo)}.</div>
        </div>
      </div>
      <div style={{ flex: 1, padding: "16px 22px", display: "flex", alignItems: "center", gap: "14px", borderLeft: `1px solid ${BORDER_SOFT}` }}>
        <div style={{ width: "40px", height: "40px", borderRadius: "11px", background: "#fef3e2", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <Ic name="trendUp" size={19} color={AMBER} />
        </div>
        <div>
          <div style={{ fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "2px" }}>Indicador proyectado a fin de mes</div>
          <div style={{ fontSize: "26px", fontWeight: "800", letterSpacing: "-0.01em", fontVariantNumeric: "tabular-nums", lineHeight: 1.1, color: colorProyectado }}>
            {pctProyectado}%<span style={{ fontSize: "13.5px", fontWeight: "700", color: CLARO_MUTED, marginLeft: "4px" }}>{cumpleProyectado} OT</span>
          </div>
          <div style={{ fontSize: "11.5px", color: CLARO_MUTED, marginTop: "2px" }}>Hoy va en <strong style={{ fontWeight: "800", color: colorHoy }}>{pctHoy}%</strong>.</div>
        </div>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────
// Tablas dinámicas estilo Excel (Avances): mismo look que la tabla dinámica
// que ya usa el equipo — encabezado rojo, fila(s) de subtotal en salmón,
// Total general en rojo fuerte, y columna % con la misma escala de color
// (rojo→amarillo→verde) que el condicional de Excel. Cada celda/fila clave
// es también un filtro: un clic la activa (resaltada con un borde), otro
// clic sobre la misma la quita.
// Look "tabla dinámica" (encabezado rojo, subtotal salmón, Total general en
// rojo fuerte) MANTENIDO a propósito, porque es el formato que el equipo ya
// reconoce de Excel — lo que se limpió es el delineado: antes cada celda
// tenía su propio borde (grid completo en rojo oscuro/salmón, muy cargado).
// Ahora solo hay una línea sutil debajo de cada fila (separador, no grid) y
// el encabezado se despega del cuerpo con un único borde inferior más
// marcado — mismo look, lectura mucho más limpia.
const estiloPivotTh = {
  background: "#C00000", color: "white", fontWeight: "700", padding: "10px 13px",
  textAlign: "left", whiteSpace: "nowrap", fontSize: "12px",
  letterSpacing: "0.015em", borderBottom: "2px solid #8c0000",
};
const estiloPivotThNum = { ...estiloPivotTh, textAlign: "right" };
const estiloPivotTd = {
  padding: "9px 13px", background: "white", color: "#101114", fontSize: "13px",
  fontVariantNumeric: "tabular-nums", borderBottom: `1px solid ${BORDER_SOFT}`,
};
const estiloPivotTdNum = { ...estiloPivotTd, textAlign: "right" };
// Envuelve una tabla dinámica para que tenga esquinas redondeadas, una
// sombra sutil que la separa de la tarjeta de fondo y un único borde
// perimetral (en vez de que cada celda dibuje su propio borde) — mismo
// acabado "de verdad profesional" en las 4 tablas dinámicas de Avances.
function MarcoTablaPivote({ children }) {
  return (
    <div style={{ borderRadius: "12px", border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: SHADOW_SM }}>
      <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
        {children}
      </table>
    </div>
  );
}

function TablaPivoteSla({ resumenSla, filtroSla, setFiltroSla, filtroTipoOperacion, setFiltroTipoOperacion, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  // Cualquier celda de la fila — No cumple, Cumple, Total, % o % impacto —
  // funciona como filtro: las dos primeras fijan también el estado SLA
  // (no_cumple / cumple); Total y % representan la fila completa (limpian el
  // SLA); % impacto es en concreto el aporte al incumplimiento, así que
  // fija SLA = no cumple. Un segundo clic en la misma celda lo quita.
  const celdaClick = (label, valor) => () => {
    if (filtroTipoOperacion === label && filtroSla === valor) { setFiltroTipoOperacion(""); setFiltroSla(null); }
    else { setFiltroTipoOperacion(label); setFiltroSla(valor); }
  };
  const estiloCelda = (label, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroTipoOperacion === label && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalClick = (label) => () => {
    if (filtroTipoOperacion === label && !filtroSla) setFiltroTipoOperacion("");
    else { setFiltroTipoOperacion(label); setFiltroSla(null); }
  };
  const estiloTotal = (label) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: undefined,
  });
  const limpiarTodo = () => { setFiltroTipoOperacion(""); setFiltroSla(null); };
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "34%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>TIPO DE TRABAJO ▾</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        {resumenSla.filas.map((f) => {
          const filaSeleccionada = false; // el Tipo de trabajo ya viene filtrado por defecto: no se sombrea la fila
          return (
            <tr key={f.label}>
              <td
                style={{ ...estiloPivotTd, cursor: "pointer", background: filaSeleccionada ? "#fde8e8" : undefined, fontWeight: filaSeleccionada ? "700" : undefined }}
                onClick={() => { setFiltroTipoOperacion(filtroTipoOperacion === f.label && !filtroSla ? "" : f.label); setFiltroSla(null); }}
              >
                {f.label}
              </td>
              <td style={estiloCelda(f.label, "no_cumple")} onClick={celdaClick(f.label, "no_cumple")}>{f.no_cumple || ""}</td>
              <td style={estiloCelda(f.label, "cumple")} onClick={celdaClick(f.label, "cumple")}>{f.cumple || ""}</td>
              <td style={estiloTotal(f.label)} onClick={totalClick(f.label)}>{f.total}</td>
              <td style={{ ...estiloTotal(f.label), background: colorPct(pctNum(f.cumple, f.total)) }} onClick={totalClick(f.label)}>{pctTexto(f.cumple, f.total)}</td>
              <td style={{ ...estiloCelda(f.label, "no_cumple"), background: colorImpacto(pctImpactoNum(f.no_cumple, resumenSla.no_cumple)) }} onClick={celdaClick(f.label, "no_cumple")}>{pctImpactoTexto(f.no_cumple, resumenSla.no_cumple)}</td>
            </tr>
          );
        })}
        {resumenSla.filas.length === 0 && (
          <tr>
            <td colSpan={6} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>
              Sin datos para los filtros actuales.
            </td>
          </tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenSla.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenSla.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenSla.total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenSla.cumple, resumenSla.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenSla.no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// Igual estructura que TablaPivoteCategoria, pero agrupando por Tipo Trabajo
// OP como fila principal y Tipo Familia como sub-fila — con la diferencia de
// que aquí el desglose por Tipo Familia SÍ se puede contraer/desplegar: cada
// Tipo Trabajo OP arranca contraído y se despliega con clic en la flechita,
// sin que eso dispare el filtro (el filtro se activa con clic en el texto).
function TablaPivoteTipoTrabajo({ resumenTipoTrabajo, filtroTipoTrabajo, setFiltroTipoTrabajo, filtroTipoFamilia, setFiltroTipoFamilia, filtroSla, setFiltroSla, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  const [desplegados, setDesplegados] = useState(new Set());
  const alternarDesplegado = (label) => {
    setDesplegados((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  // Fila de Tipo Trabajo OP: No cumple/Cumple fijan también ese Tipo Trabajo
  // OP (limpiando el Tipo Familia); Total y % representan la fila completa
  // (limpian el SLA); % impacto es el aporte al incumplimiento → fija SLA =
  // no cumple. Un segundo clic en la misma celda lo quita.
  const celdaClick = (tipoLabel, valor) => () => {
    if (filtroTipoTrabajo === tipoLabel && !filtroTipoFamilia && filtroSla === valor) { setFiltroTipoTrabajo(null); setFiltroSla(null); }
    else { setFiltroTipoTrabajo(tipoLabel); setFiltroTipoFamilia(null); setFiltroSla(valor); }
  };
  const estiloCelda = (tipoLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroTipoTrabajo === tipoLabel && !filtroTipoFamilia && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalClick = (tipoLabel) => () => {
    if (filtroTipoTrabajo === tipoLabel && !filtroTipoFamilia && !filtroSla) { setFiltroTipoTrabajo(null); }
    else { setFiltroTipoTrabajo(tipoLabel); setFiltroTipoFamilia(null); setFiltroSla(null); }
  };
  const estiloTotal = (tipoLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroTipoTrabajo === tipoLabel && !filtroTipoFamilia && !filtroSla ? "#fde8e8" : undefined,
  });
  const celdaFamiliaClick = (tipoLabel, famLabel, valor) => () => {
    if (filtroTipoTrabajo === tipoLabel && filtroTipoFamilia === famLabel && filtroSla === valor) { setFiltroTipoTrabajo(null); setFiltroTipoFamilia(null); setFiltroSla(null); }
    else { setFiltroTipoTrabajo(tipoLabel); setFiltroTipoFamilia(famLabel); setFiltroSla(valor); }
  };
  const estiloCeldaFamilia = (tipoLabel, famLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroTipoTrabajo === tipoLabel && filtroTipoFamilia === famLabel && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalFamiliaClick = (tipoLabel, famLabel) => () => {
    if (filtroTipoTrabajo === tipoLabel && filtroTipoFamilia === famLabel && !filtroSla) { setFiltroTipoTrabajo(null); setFiltroTipoFamilia(null); }
    else { setFiltroTipoTrabajo(tipoLabel); setFiltroTipoFamilia(famLabel); setFiltroSla(null); }
  };
  const estiloTotalFamilia = (tipoLabel, famLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroTipoTrabajo === tipoLabel && filtroTipoFamilia === famLabel && !filtroSla ? "#fde8e8" : undefined,
  });
  const limpiarTodo = () => { setFiltroTipoTrabajo(null); setFiltroTipoFamilia(null); setFiltroSla(null); };
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "34%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>TIPO TRABAJO OP / TIPO FAMILIA ▾</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        {resumenTipoTrabajo.filas.map((f) => {
          const filaSeleccionada = filtroTipoTrabajo === f.label && !filtroTipoFamilia && !filtroSla;
          const abierto = desplegados.has(f.label);
          // Mientras está desplegado, la fila del Tipo Trabajo OP se sombrea
          // en salmón (igual que el subtotal de Categoría/Subcategoría) para
          // que no se confunda con las filas de Tipo Familia que aparecen
          // debajo — si además está seleccionada como filtro, gana el
          // resaltado rojo claro de selección sobre el salmón.
          const fondoFila = filaSeleccionada ? "#fde8e8" : abierto ? "#F8CBAD" : undefined;
          const colorTexto = abierto && !filaSeleccionada ? "#7a2f00" : undefined;
          const negrita = filaSeleccionada || abierto;
          return (
            <Fragment key={f.label}>
              <tr style={filaSeleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                <td style={{ ...estiloPivotTd, cursor: "pointer", background: fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined, display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    onClick={(e) => { e.stopPropagation(); alternarDesplegado(f.label); }}
                    title={abierto ? "Contraer Tipo Familia" : "Desplegar Tipo Familia"}
                    style={{ cursor: "pointer", color: colorTexto || CLARO_MUTED, fontSize: "10px", userSelect: "none", width: "12px", display: "inline-block" }}
                  >
                    {abierto ? "▾" : "▸"}
                  </span>
                  <span
                    onClick={() => { setFiltroTipoTrabajo(filtroTipoTrabajo === f.label && !filtroTipoFamilia && !filtroSla ? null : f.label); setFiltroTipoFamilia(null); setFiltroSla(null); }}
                    style={{ cursor: "pointer" }}
                  >
                    {f.label}
                  </span>
                </td>
                <td style={{ ...estiloCelda(f.label, "no_cumple"), background: estiloCelda(f.label, "no_cumple").background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "no_cumple")}>{f.no_cumple || ""}</td>
                <td style={{ ...estiloCelda(f.label, "cumple"), background: estiloCelda(f.label, "cumple").background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "cumple")}>{f.cumple || ""}</td>
                <td style={{ ...estiloTotal(f.label), background: estiloTotal(f.label).background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={totalClick(f.label)}>{f.total}</td>
                <td style={{ ...estiloTotal(f.label), background: estiloTotal(f.label).background || colorPct(pctNum(f.cumple, f.total)), color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={totalClick(f.label)}>{pctTexto(f.cumple, f.total)}</td>
                <td style={{ ...estiloCelda(f.label, "no_cumple"), background: colorImpacto(pctImpactoNum(f.no_cumple, resumenTipoTrabajo.no_cumple)), color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "no_cumple")}>{pctImpactoTexto(f.no_cumple, resumenTipoTrabajo.no_cumple)}</td>
              </tr>
              {abierto && f.familias.map((fam) => {
                const famSeleccionada = filtroTipoTrabajo === f.label && filtroTipoFamilia === fam.label && !filtroSla;
                return (
                  <tr key={fam.label} style={famSeleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                    <td
                      style={{ ...estiloPivotTd, paddingLeft: "34px", cursor: "pointer" }}
                      onClick={() => { if (famSeleccionada) { setFiltroTipoTrabajo(null); setFiltroTipoFamilia(null); } else { setFiltroTipoTrabajo(f.label); setFiltroTipoFamilia(fam.label); setFiltroSla(null); } }}
                    >
                      {fam.label}
                    </td>
                    <td style={estiloCeldaFamilia(f.label, fam.label, "no_cumple")} onClick={celdaFamiliaClick(f.label, fam.label, "no_cumple")}>{fam.no_cumple || ""}</td>
                    <td style={estiloCeldaFamilia(f.label, fam.label, "cumple")} onClick={celdaFamiliaClick(f.label, fam.label, "cumple")}>{fam.cumple || ""}</td>
                    <td style={estiloTotalFamilia(f.label, fam.label)} onClick={totalFamiliaClick(f.label, fam.label)}>{fam.total}</td>
                    <td style={{ ...estiloTotalFamilia(f.label, fam.label), background: estiloTotalFamilia(f.label, fam.label).background || colorPct(pctNum(fam.cumple, fam.total)) }} onClick={totalFamiliaClick(f.label, fam.label)}>{pctTexto(fam.cumple, fam.total)}</td>
                    <td style={{ ...estiloCeldaFamilia(f.label, fam.label, "no_cumple"), background: colorImpacto(pctImpactoNum(fam.no_cumple, resumenTipoTrabajo.no_cumple)) }} onClick={celdaFamiliaClick(f.label, fam.label, "no_cumple")}>{pctImpactoTexto(fam.no_cumple, resumenTipoTrabajo.no_cumple)}</td>
                  </tr>
                );
              })}
            </Fragment>
          );
        })}
        {resumenTipoTrabajo.filas.length === 0 && (
          <tr>
            <td colSpan={6} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>
              Sin datos para los filtros actuales.
            </td>
          </tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenTipoTrabajo.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenTipoTrabajo.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenTipoTrabajo.total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenTipoTrabajo.cumple, resumenTipoTrabajo.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenTipoTrabajo.no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// Igual estructura que TablaPivoteTipoTrabajo, pero agrupando por Jefe de
// Zona como fila principal y Municipio como sub-fila desplegable debajo.
function TablaPivoteJefe({ resumenJefe, filtroJefeResumen, setFiltroJefeResumen, filtroMunicipioResumen, setFiltroMunicipioResumen, filtroSla, setFiltroSla, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  const [desplegados, setDesplegados] = useState(new Set());
  const alternarDesplegado = (label) => {
    setDesplegados((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  const celdaClick = (jefeLabel, valor) => () => {
    if (filtroJefeResumen === jefeLabel && !filtroMunicipioResumen && filtroSla === valor) { setFiltroJefeResumen(null); setFiltroSla(null); }
    else { setFiltroJefeResumen(jefeLabel); setFiltroMunicipioResumen(null); setFiltroSla(valor); }
  };
  const estiloCelda = (jefeLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroJefeResumen === jefeLabel && !filtroMunicipioResumen && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalClick = (jefeLabel) => () => {
    if (filtroJefeResumen === jefeLabel && !filtroMunicipioResumen && !filtroSla) { setFiltroJefeResumen(null); }
    else { setFiltroJefeResumen(jefeLabel); setFiltroMunicipioResumen(null); setFiltroSla(null); }
  };
  const estiloTotal = (jefeLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroJefeResumen === jefeLabel && !filtroMunicipioResumen && !filtroSla ? "#fde8e8" : undefined,
  });
  const celdaMunicipioClick = (jefeLabel, munLabel, valor) => () => {
    if (filtroJefeResumen === jefeLabel && filtroMunicipioResumen === munLabel && filtroSla === valor) { setFiltroJefeResumen(null); setFiltroMunicipioResumen(null); setFiltroSla(null); }
    else { setFiltroJefeResumen(jefeLabel); setFiltroMunicipioResumen(munLabel); setFiltroSla(valor); }
  };
  const estiloCeldaMunicipio = (jefeLabel, munLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroJefeResumen === jefeLabel && filtroMunicipioResumen === munLabel && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalMunicipioClick = (jefeLabel, munLabel) => () => {
    if (filtroJefeResumen === jefeLabel && filtroMunicipioResumen === munLabel && !filtroSla) { setFiltroJefeResumen(null); setFiltroMunicipioResumen(null); }
    else { setFiltroJefeResumen(jefeLabel); setFiltroMunicipioResumen(munLabel); setFiltroSla(null); }
  };
  const estiloTotalMunicipio = (jefeLabel, munLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroJefeResumen === jefeLabel && filtroMunicipioResumen === munLabel && !filtroSla ? "#fde8e8" : undefined,
  });
  const limpiarTodo = () => { setFiltroJefeResumen(null); setFiltroMunicipioResumen(null); setFiltroSla(null); };
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "34%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>JEFE DE ZONA / MUNICIPIO ▾</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        {resumenJefe.filas.map((f) => {
          const filaSeleccionada = filtroJefeResumen === f.label && !filtroMunicipioResumen && !filtroSla;
          const abierto = desplegados.has(f.label);
          const fondoFila = filaSeleccionada ? "#fde8e8" : abierto ? "#F8CBAD" : undefined;
          const colorTexto = abierto && !filaSeleccionada ? "#7a2f00" : undefined;
          const negrita = filaSeleccionada || abierto;
          return (
            <Fragment key={f.label}>
              <tr style={filaSeleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                <td style={{ ...estiloPivotTd, cursor: "pointer", background: fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined, display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    onClick={(e) => { e.stopPropagation(); alternarDesplegado(f.label); }}
                    title={abierto ? "Contraer municipios" : "Desplegar municipios"}
                    style={{ cursor: "pointer", color: colorTexto || CLARO_MUTED, fontSize: "10px", userSelect: "none", width: "12px", display: "inline-block" }}
                  >
                    {abierto ? "▾" : "▸"}
                  </span>
                  <span
                    onClick={() => { setFiltroJefeResumen(filtroJefeResumen === f.label && !filtroMunicipioResumen && !filtroSla ? null : f.label); setFiltroMunicipioResumen(null); setFiltroSla(null); }}
                    style={{ cursor: "pointer" }}
                  >
                    {f.label}
                  </span>
                </td>
                <td style={{ ...estiloCelda(f.label, "no_cumple"), background: estiloCelda(f.label, "no_cumple").background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "no_cumple")}>{f.no_cumple || ""}</td>
                <td style={{ ...estiloCelda(f.label, "cumple"), background: estiloCelda(f.label, "cumple").background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "cumple")}>{f.cumple || ""}</td>
                <td style={{ ...estiloTotal(f.label), background: estiloTotal(f.label).background || fondoFila, color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={totalClick(f.label)}>{f.total}</td>
                <td style={{ ...estiloTotal(f.label), background: estiloTotal(f.label).background || colorPct(pctNum(f.cumple, f.total)), color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={totalClick(f.label)}>{pctTexto(f.cumple, f.total)}</td>
                <td style={{ ...estiloCelda(f.label, "no_cumple"), background: colorImpacto(pctImpactoNum(f.no_cumple, resumenJefe.no_cumple)), color: colorTexto, fontWeight: negrita ? "700" : undefined }} onClick={celdaClick(f.label, "no_cumple")}>{pctImpactoTexto(f.no_cumple, resumenJefe.no_cumple)}</td>
              </tr>
              {abierto && f.municipios.map((mun) => {
                const munSeleccionado = filtroJefeResumen === f.label && filtroMunicipioResumen === mun.label && !filtroSla;
                return (
                  <tr key={mun.label} style={munSeleccionado ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                    <td
                      style={{ ...estiloPivotTd, paddingLeft: "34px", cursor: "pointer" }}
                      onClick={() => { if (munSeleccionado) { setFiltroJefeResumen(null); setFiltroMunicipioResumen(null); } else { setFiltroJefeResumen(f.label); setFiltroMunicipioResumen(mun.label); setFiltroSla(null); } }}
                    >
                      {mun.label}
                    </td>
                    <td style={estiloCeldaMunicipio(f.label, mun.label, "no_cumple")} onClick={celdaMunicipioClick(f.label, mun.label, "no_cumple")}>{mun.no_cumple || ""}</td>
                    <td style={estiloCeldaMunicipio(f.label, mun.label, "cumple")} onClick={celdaMunicipioClick(f.label, mun.label, "cumple")}>{mun.cumple || ""}</td>
                    <td style={estiloTotalMunicipio(f.label, mun.label)} onClick={totalMunicipioClick(f.label, mun.label)}>{mun.total}</td>
                    <td style={{ ...estiloTotalMunicipio(f.label, mun.label), background: estiloTotalMunicipio(f.label, mun.label).background || colorPct(pctNum(mun.cumple, mun.total)) }} onClick={totalMunicipioClick(f.label, mun.label)}>{pctTexto(mun.cumple, mun.total)}</td>
                    <td style={{ ...estiloCeldaMunicipio(f.label, mun.label, "no_cumple"), background: colorImpacto(pctImpactoNum(mun.no_cumple, resumenJefe.no_cumple)) }} onClick={celdaMunicipioClick(f.label, mun.label, "no_cumple")}>{pctImpactoTexto(mun.no_cumple, resumenJefe.no_cumple)}</td>
                  </tr>
                );
              })}
            </Fragment>
          );
        })}
        {resumenJefe.filas.length === 0 && (
          <tr>
            <td colSpan={6} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>
              Sin datos para los filtros actuales.
            </td>
          </tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenJefe.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenJefe.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenJefe.total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenJefe.cumple, resumenJefe.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenJefe.no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

function TablaPivoteRango({ resumenRango, filtroRango, setFiltroRango, filtroSla, setFiltroSla, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  // Clic en el número de "No cumple" / "Cumple" de una fila: filtra a esa
  // celda exacta (ese rango Y ese estado SLA), no a toda la fila.
  const celdaClick = (label, valor) => () => {
    if (filtroRango === label && filtroSla === valor) { setFiltroRango(null); setFiltroSla(null); }
    else { setFiltroRango(label); setFiltroSla(valor); }
  };
  const estiloCelda = (label, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroRango === label && filtroSla === valor ? "#fde8e8" : undefined,
  });
  // Total y % representan la fila completa (limpian el SLA, sea cumple o no).
  const totalClick = (label) => () => {
    if (filtroRango === label && !filtroSla) setFiltroRango(null);
    else { setFiltroRango(label); setFiltroSla(null); }
  };
  const estiloTotal = (label) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroRango === label && !filtroSla ? "#fde8e8" : undefined,
  });
  const limpiarTodo = () => { setFiltroRango(null); setFiltroSla(null); };
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "40%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>TIPO DE TRABAJO ▾</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td style={{ ...estiloPivotTd, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">⊟ O&amp;M Fijo</td>
          <td style={{ ...estiloPivotTdNum, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.total}</td>
          <td style={{ ...estiloPivotTdNum, background: colorPct(pctNum(resumenRango.cumple, resumenRango.total)), color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenRango.cumple, resumenRango.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.no_cumple ? "100,00%" : "—"}</td>
        </tr>
        {resumenRango.filas.map((f) => {
          const seleccionada = filtroRango === f.label && !filtroSla;
          const estiloFila = seleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined;
          return (
            <tr key={f.label} style={estiloFila}>
              <td style={{ ...estiloPivotTd, paddingLeft: "22px", cursor: "pointer" }} onClick={() => { setFiltroRango((v) => (v === f.label && !filtroSla ? null : f.label)); setFiltroSla(null); }}>{f.label}</td>
              <td style={estiloCelda(f.label, "no_cumple")} onClick={celdaClick(f.label, "no_cumple")}>{f.no_cumple || ""}</td>
              <td style={estiloCelda(f.label, "cumple")} onClick={celdaClick(f.label, "cumple")}>{f.cumple || ""}</td>
              <td style={estiloTotal(f.label)} onClick={totalClick(f.label)}>{f.total}</td>
              <td style={{ ...estiloTotal(f.label), background: estiloTotal(f.label).background || colorPct(pctNum(f.cumple, f.total)) }} onClick={totalClick(f.label)}>{pctTexto(f.cumple, f.total)}</td>
              <td style={{ ...estiloCelda(f.label, "no_cumple"), background: colorImpacto(pctImpactoNum(f.no_cumple, resumenRango.no_cumple)) }} onClick={celdaClick(f.label, "no_cumple")}>{pctImpactoTexto(f.no_cumple, resumenRango.no_cumple)}</td>
            </tr>
          );
        })}
        {resumenRango.filas.length === 0 && (
          <tr><td colSpan={6} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>Sin datos para los filtros actuales.</td></tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenRango.cumple, resumenRango.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenRango.no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// Tabla dinámica — Backlog por municipio (pestaña Resumen Gerencial), formato
// Excel idéntico a las demás tablas dinámicas: No cumple / Cumple / Total
// general / % (escala rojo-amarillo-verde) / % impacto (tinte rojo según
// participación en el incumplimiento), ordenada de mayor a menor impacto —
// el backend ya la entrega así (ORDER BY no_cumple_sla DESC).
function TablaPivoteMunicipio({ datos, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  const filas = (datos || []).map((m) => ({
    municipio: m.municipio || "(sin dato)",
    departamento: m.departamento || "(sin dato)",
    no_cumple: Number(m.no_cumple_sla) || 0,
    cumple: Number(m.cumple_sla) || 0,
    total: Number(m.total) || 0,
  }));
  const no_cumple = filas.reduce((acc, f) => acc + f.no_cumple, 0);
  const cumple = filas.reduce((acc, f) => acc + f.cumple, 0);
  const total = no_cumple + cumple;
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "26%" }} /><col style={{ width: "22%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>MUNICIPIO ▾</th>
          <th style={estiloPivotTh}>DEPARTAMENTO</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        {filas.map((f, i) => (
          <tr key={i}>
            <td style={estiloPivotTd}>{f.municipio}</td>
            <td style={estiloPivotTd}>{f.departamento}</td>
            <td style={estiloPivotTdNum}>{f.no_cumple || ""}</td>
            <td style={estiloPivotTdNum}>{f.cumple || ""}</td>
            <td style={estiloPivotTdNum}>{f.total}</td>
            <td style={{ ...estiloPivotTdNum, background: colorPct(pctNum(f.cumple, f.total)) }}>{pctTexto(f.cumple, f.total)}</td>
            <td style={{ ...estiloPivotTdNum, background: colorImpacto(pctImpactoNum(f.no_cumple, no_cumple)) }}>{pctImpactoTexto(f.no_cumple, no_cumple)}</td>
          </tr>
        ))}
        {filas.length === 0 && (
          <tr><td colSpan={7} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>Sin datos para los filtros actuales.</td></tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800" }}>Total general</td>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800" }}></td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800" }}>{no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800" }}>{cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800" }}>{total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800" }}>{pctTexto(cumple, total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800" }}>{no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// Tabla dinámica 3 — Categoría / Subcategoría, para ver cuál categoría y
// cuál subcategoría de seguimiento impacta más en el indicador de
// cumplimiento SLA. Mismo formato que la tabla de rangos de antigüedad (No
// cumple / Cumple / Total general / %, con la misma escala de color): cada
// categoría sale como fila subtotal (salmón) con sus subcategorías debajo,
// indentadas; todo ordenado de mayor a menor cantidad. Clic en una categoría
// o subcategoría filtra igual que las otras dos tablas dinámicas.
function TablaPivoteCategoria({ resumenCategoria, filtroCategoria, setFiltroCategoria, filtroSubcategoria, setFiltroSubcategoria, filtroSla, setFiltroSla, colorPct, pctTexto, pctNum, colorImpacto, pctImpactoTexto, pctImpactoNum }) {
  // Igual que en "Resumen por Tipo Trabajo OP": cada Categoría arranca
  // contraída y se despliega con clic en la flechita (sin disparar el
  // filtro), mostrando entonces sus Subcategorías debajo.
  const [desplegados, setDesplegados] = useState(new Set());
  const alternarDesplegado = (label) => {
    setDesplegados((prev) => {
      const next = new Set(prev);
      if (next.has(label)) next.delete(label); else next.add(label);
      return next;
    });
  };
  // Clic en el número de "No cumple" / "Cumple" de una categoría o
  // subcategoría: filtra a esa celda exacta (esa categoría/subcategoría Y
  // ese estado SLA), no a toda la fila.
  const celdaCatClick = (catLabel, valor) => () => {
    if (filtroCategoria === catLabel && !filtroSubcategoria && filtroSla === valor) { setFiltroCategoria(null); setFiltroSla(null); }
    else { setFiltroCategoria(catLabel); setFiltroSubcategoria(null); setFiltroSla(valor); }
  };
  const estiloCeldaCat = (catLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer", fontWeight: "700", color: "#7a2f00",
    background: filtroCategoria === catLabel && !filtroSubcategoria && filtroSla === valor ? "#fde8e8" : "#F8CBAD",
  });
  // Total y % de una categoría representan la categoría completa (limpian
  // el SLA, sea cumple o no).
  const totalCatClick = (catLabel) => () => {
    if (filtroCategoria === catLabel && !filtroSubcategoria && !filtroSla) setFiltroCategoria(null);
    else { setFiltroCategoria(catLabel); setFiltroSubcategoria(null); setFiltroSla(null); }
  };
  const estiloTotalCat = (catLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer", fontWeight: "700", color: "#7a2f00",
    background: filtroCategoria === catLabel && !filtroSubcategoria && !filtroSla ? "#fde8e8" : "#F8CBAD",
  });
  const celdaSubClick = (catLabel, subLabel, valor) => () => {
    if (filtroCategoria === catLabel && filtroSubcategoria === subLabel && filtroSla === valor) { setFiltroCategoria(null); setFiltroSubcategoria(null); setFiltroSla(null); }
    else { setFiltroCategoria(catLabel); setFiltroSubcategoria(subLabel); setFiltroSla(valor); }
  };
  const estiloCeldaSub = (catLabel, subLabel, valor) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroCategoria === catLabel && filtroSubcategoria === subLabel && filtroSla === valor ? "#fde8e8" : undefined,
  });
  const totalSubClick = (catLabel, subLabel) => () => {
    if (filtroCategoria === catLabel && filtroSubcategoria === subLabel && !filtroSla) { setFiltroCategoria(null); setFiltroSubcategoria(null); }
    else { setFiltroCategoria(catLabel); setFiltroSubcategoria(subLabel); setFiltroSla(null); }
  };
  const estiloTotalSub = (catLabel, subLabel) => ({
    ...estiloPivotTdNum, cursor: "pointer",
    background: filtroCategoria === catLabel && filtroSubcategoria === subLabel && !filtroSla ? "#fde8e8" : undefined,
  });
  const limpiarTodo = () => { setFiltroCategoria(null); setFiltroSubcategoria(null); setFiltroSla(null); };
  return (
    <MarcoTablaPivote>
      <colgroup><col style={{ width: "40%" }} /><col /><col /><col /><col /><col /></colgroup>
      <thead>
        <tr>
          <th style={estiloPivotTh}>CATEGORÍA / SUBCATEGORÍA ▾</th>
          <th style={estiloPivotThNum}>No cumple</th>
          <th style={estiloPivotThNum}>Cumple</th>
          <th style={estiloPivotThNum}>Total general</th>
          <th style={estiloPivotThNum}>% ▾</th>
          <th style={estiloPivotThNum}>% impacto ▾</th>
        </tr>
      </thead>
      <tbody>
        {resumenCategoria.categorias.map((cat) => {
          const catSeleccionada = filtroCategoria === cat.label && !filtroSubcategoria && !filtroSla;
          const abierto = desplegados.has(cat.label);
          const alternarCategoria = () => {
            if (filtroCategoria === cat.label && !filtroSubcategoria && !filtroSla) setFiltroCategoria(null);
            else { setFiltroCategoria(cat.label); setFiltroSubcategoria(null); setFiltroSla(null); }
          };
          return (
            <Fragment key={cat.label}>
              <tr style={catSeleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                <td style={{ ...estiloPivotTd, background: "#F8CBAD", color: "#7a2f00", fontWeight: "700", cursor: "pointer", display: "flex", alignItems: "center", gap: "6px" }}>
                  <span
                    onClick={(e) => { e.stopPropagation(); alternarDesplegado(cat.label); }}
                    title={abierto ? "Contraer subcategorías" : "Desplegar subcategorías"}
                    style={{ cursor: "pointer", color: "#7a2f00", fontSize: "10px", userSelect: "none", width: "12px", display: "inline-block" }}
                  >
                    {abierto ? "▾" : "▸"}
                  </span>
                  <span onClick={alternarCategoria} style={{ cursor: "pointer" }}>{cat.label}</span>
                </td>
                <td style={estiloCeldaCat(cat.label, "no_cumple")} onClick={celdaCatClick(cat.label, "no_cumple")}>{cat.no_cumple || ""}</td>
                <td style={estiloCeldaCat(cat.label, "cumple")} onClick={celdaCatClick(cat.label, "cumple")}>{cat.cumple || ""}</td>
                <td style={estiloTotalCat(cat.label)} onClick={totalCatClick(cat.label)}>{cat.total}</td>
                <td style={{ ...estiloTotalCat(cat.label), background: filtroCategoria === cat.label && !filtroSubcategoria && !filtroSla ? "#fde8e8" : colorPct(pctNum(cat.cumple, cat.total)) }} onClick={totalCatClick(cat.label)}>{pctTexto(cat.cumple, cat.total)}</td>
                <td style={{ ...estiloCeldaCat(cat.label, "no_cumple"), background: colorImpacto(pctImpactoNum(cat.no_cumple, resumenCategoria.no_cumple)) }} onClick={celdaCatClick(cat.label, "no_cumple")}>{pctImpactoTexto(cat.no_cumple, resumenCategoria.no_cumple)}</td>
              </tr>
              {abierto && cat.subcategorias.map((sub) => {
                const subSeleccionada = filtroCategoria === cat.label && filtroSubcategoria === sub.label && !filtroSla;
                const alternarSub = () => {
                  if (subSeleccionada) { setFiltroCategoria(null); setFiltroSubcategoria(null); }
                  else { setFiltroCategoria(cat.label); setFiltroSubcategoria(sub.label); setFiltroSla(null); }
                };
                return (
                  <tr key={sub.label} style={subSeleccionada ? { outline: "2px solid #0f172a", outlineOffset: "-2px" } : undefined}>
                    <td style={{ ...estiloPivotTd, paddingLeft: "22px", cursor: "pointer" }} onClick={alternarSub}>{sub.label}</td>
                    <td style={estiloCeldaSub(cat.label, sub.label, "no_cumple")} onClick={celdaSubClick(cat.label, sub.label, "no_cumple")}>{sub.no_cumple || ""}</td>
                    <td style={estiloCeldaSub(cat.label, sub.label, "cumple")} onClick={celdaSubClick(cat.label, sub.label, "cumple")}>{sub.cumple || ""}</td>
                    <td style={estiloTotalSub(cat.label, sub.label)} onClick={totalSubClick(cat.label, sub.label)}>{sub.total}</td>
                    <td style={{ ...estiloTotalSub(cat.label, sub.label), background: estiloTotalSub(cat.label, sub.label).background || colorPct(pctNum(sub.cumple, sub.total)) }} onClick={totalSubClick(cat.label, sub.label)}>{pctTexto(sub.cumple, sub.total)}</td>
                    <td style={{ ...estiloCeldaSub(cat.label, sub.label, "no_cumple"), background: colorImpacto(pctImpactoNum(sub.no_cumple, resumenCategoria.no_cumple)) }} onClick={celdaSubClick(cat.label, sub.label, "no_cumple")}>{pctImpactoTexto(sub.no_cumple, resumenCategoria.no_cumple)}</td>
                  </tr>
                );
              })}
            </Fragment>
          );
        })}
        {resumenCategoria.categorias.length === 0 && (
          <tr><td colSpan={6} style={{ ...estiloPivotTd, textAlign: "center", color: CLARO_MUTED }}>Sin datos para los filtros actuales.</td></tr>
        )}
        <tr>
          <td style={{ ...estiloPivotTd, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo} title="Quitar filtro (ver todo)">Total general</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenCategoria.no_cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenCategoria.cumple}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenCategoria.total}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{pctTexto(resumenCategoria.cumple, resumenCategoria.total)}</td>
          <td style={{ ...estiloPivotTdNum, background: "#FF0000", color: "white", fontWeight: "800", cursor: "pointer" }} onClick={limpiarTodo}>{resumenCategoria.no_cumple ? "100,00%" : "—"}</td>
        </tr>
      </tbody>
    </MarcoTablaPivote>
  );
}

// Cuadro "Top causas de incumplimiento SLA" — análisis tipo Pareto a nivel
// subcategoría: toma todas las subcategorías con al menos 1 OT fuera de SLA,
// las ordena de mayor a menor aporte al incumplimiento total y las dibuja
// como barras horizontales, con el % acumulado marcando la regla 80/20.
function TablaParetoCausas({ resumenCategoria, filtroCategoria, setFiltroCategoria, filtroSubcategoria, setFiltroSubcategoria, filtroSla, setFiltroSla, pctImpactoTexto, pctImpactoNum }) {
  const causas = [];
  resumenCategoria.categorias.forEach((cat) => {
    cat.subcategorias.forEach((sub) => {
      if (sub.no_cumple > 0) causas.push({ categoria: cat.label, label: sub.label, no_cumple: sub.no_cumple });
    });
  });
  causas.sort((a, b) => b.no_cumple - a.no_cumple);
  const noCumpleTotal = resumenCategoria.no_cumple;
  const maxNoCumple = causas.length ? causas[0].no_cumple : 0;

  if (causas.length === 0) {
    return <div style={{ color: CLARO_MUTED, fontSize: "12.5px" }}>No hay OT incumpliendo SLA en los filtros actuales.</div>;
  }

  let acumulado = 0;
  const filas = causas.map((item) => {
    const pct = pctImpactoNum(item.no_cumple, noCumpleTotal);
    acumulado += pct;
    return { ...item, pct, acumulado };
  });
  const top3 = causas.slice(0, 3);
  const pctTop3 = pctImpactoNum(top3.reduce((acc, c) => acc + c.no_cumple, 0), noCumpleTotal);

  return (
    <div>
      {filas.map((item) => {
        // Cada barra representa específicamente las OT que NO cumplen SLA de
        // esa subcategoría (es un Pareto de incumplimiento), así que
        // seleccionarla filtra también por "No cumple" — es "el dato
        // específico" que la barra muestra, no toda la subcategoría.
        const seleccionada = filtroCategoria === item.categoria && filtroSubcategoria === item.label && filtroSla === "no_cumple";
        const alternar = () => {
          if (seleccionada) { setFiltroCategoria(null); setFiltroSubcategoria(null); setFiltroSla(null); }
          else { setFiltroCategoria(item.categoria); setFiltroSubcategoria(item.label); setFiltroSla("no_cumple"); }
        };
        return (
          <div
            key={item.categoria + "|" + item.label}
            onClick={alternar}
            title={`${item.categoria} › ${item.label}`}
            style={{
              display: "flex", alignItems: "center", gap: "10px", marginBottom: "7px",
              fontSize: "12px", cursor: "pointer",
            }}
          >
            <div style={{ flex: "0 0 190px", minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: seleccionada ? CLARO_RED : CLARO_DARK, fontWeight: seleccionada ? "700" : "400" }}>
              {item.label}
            </div>
            <div style={{ flex: 1, height: "18px", background: "#f1f2f4", borderRadius: "4px", overflow: "hidden", outline: seleccionada ? "2px solid #0f172a" : "none", outlineOffset: "-1px" }}>
              <div style={{
                height: "100%", borderRadius: "4px 0 0 0",
                width: `${maxNoCumple ? (item.no_cumple / maxNoCumple) * 100 : 0}%`,
                background: item.acumulado <= 80 ? "#8a1d1d" : CRITICAL,
              }} />
            </div>
            <div style={{ flex: "0 0 108px", textAlign: "right", fontVariantNumeric: "tabular-nums", color: CLARO_MUTED }}>
              <strong style={{ color: CLARO_DARK }}>{item.no_cumple}</strong> OT · {pctImpactoTexto(item.no_cumple, noCumpleTotal)}
            </div>
          </div>
        );
      })}
      <div style={{ fontSize: "11.5px", color: CLARO_MUTED, marginTop: "8px" }}>
        Las {Math.min(3, causas.length)} primeras causas explican {pctTop3.toFixed(2).replace(".", ",")}% del incumplimiento actual ({noCumpleTotal} OT fuera de SLA en total). Las barras en rojo oscuro son las que, acumuladas, llegan al 80% (regla de Pareto).
      </div>
    </div>
  );
}

function Tag({ text, color }) {
  return (
    <span style={{
      display: "inline-block", padding: "3px 10px", borderRadius: "20px",
      fontSize: "11px", fontWeight: "700", color: "white", background: color,
    }}>{text}</span>
  );
}

// Controles de paginación (100 filas por página) para las tablas grandes
function Paginador({ pagina, totalPaginas, total, pageSize, onChange }) {
  if (total === 0) return null;
  const desde = total === 0 ? 0 : (pagina - 1) * pageSize + 1;
  const hasta = Math.min(pagina * pageSize, total);
  const boton = (disabled) => ({
    border: `1px solid ${BORDER}`, background: disabled ? "#f8fafc" : "white",
    color: disabled ? "#cbd5e1" : CLARO_DARK, borderRadius: "7px", padding: "6px 12px",
    fontSize: "12px", fontWeight: "600", cursor: disabled ? "default" : "pointer",
  });
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 4px 4px 4px", fontSize: "12.5px", color: CLARO_MUTED, flexWrap: "wrap", gap: "10px" }}>
      <div>Mostrando <strong style={{ color: CLARO_DARK }}>{desde}–{hasta}</strong> de <strong style={{ color: CLARO_DARK }}>{total}</strong> registros</div>
      {totalPaginas > 1 && (
        <div style={{ display: "flex", gap: "6px", alignItems: "center" }}>
          <button disabled={pagina === 1} onClick={() => onChange(1)} style={boton(pagina === 1)}>«</button>
          <button disabled={pagina === 1} onClick={() => onChange(pagina - 1)} style={boton(pagina === 1)}>‹ Anterior</button>
          <span style={{ padding: "0 10px", fontWeight: "700", color: CLARO_DARK }}>Página {pagina} de {totalPaginas}</span>
          <button disabled={pagina === totalPaginas} onClick={() => onChange(pagina + 1)} style={boton(pagina === totalPaginas)}>Siguiente ›</button>
          <button disabled={pagina === totalPaginas} onClick={() => onChange(totalPaginas)} style={boton(pagina === totalPaginas)}>»</button>
        </div>
      )}
    </div>
  );
}

// Barra horizontal simple con leyenda y tooltip nativo (title). Cada barra
// muestra el conteo y, junto a él, su % de participación sobre el TOTAL real
// del conjunto (no solo sobre los `maxItems` visibles), para que el número
// tenga el mismo significado gerencial en cualquier gráfica de este tipo.
function BarraHorizontal({ datos, maxItems = 10, colorFn }) {
  if (!datos || datos.length === 0) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Sin datos para los filtros actuales.</div>;
  const totalGeneral = datos.reduce((acc, d) => acc + (Number(d.total) || 0), 0);
  const top = datos.slice(0, maxItems);
  const max = Math.max(...top.map((d) => d.total), 1);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
      {top.map((d, i) => {
        const pct = totalGeneral ? (d.total / totalGeneral) * 100 : 0;
        return (
          <div key={i} title={`${d.label}: ${d.total} (${pct.toFixed(1)}% del total)`} style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <div style={{ width: "160px", fontSize: "12px", color: CLARO_DARK, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flexShrink: 0 }}>
              {d.label || "(sin dato)"}
            </div>
            <div style={{ flex: 1, background: "#f1f5f9", borderRadius: "6px", height: "18px", position: "relative" }}>
              <div style={{
                width: `${Math.max((d.total / max) * 100, 3)}%`, height: "100%", borderRadius: "6px",
                background: colorFn ? colorFn(d, i) : CAT_COLORS[i % CAT_COLORS.length],
                transition: "width 0.3s ease",
              }} />
            </div>
            <div style={{ width: "88px", textAlign: "right", fontSize: "12px", fontWeight: "700", color: CLARO_DARK, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" }}>
              {d.total} <span style={{ fontWeight: "600", color: CLARO_MUTED }}>· {pct.toFixed(1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Dona simple Cumple / No cumple SLA (SVG puro, sin dependencias)
function DonutSLA({ cumplen, noCumplen }) {
  // Number(...) es importante: estos valores pueden venir como texto desde
  // la base de datos (ej. "311"), y "311" + "46" en JS concatena ("31146")
  // en vez de sumar — eso rompía el % mostrado en el centro de la dona.
  const cumplenNum = Number(cumplen) || 0;
  const noCumplenNum = Number(noCumplen) || 0;
  const total = cumplenNum + noCumplenNum;
  const pctCumple = total > 0 ? (cumplenNum / total) * 100 : 0;
  const r = 46, c = 2 * Math.PI * r;
  const dashCumple = (pctCumple / 100) * c;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "20px" }}>
      <svg width="120" height="120" viewBox="0 0 120 120">
        <circle cx="60" cy="60" r={r} fill="none" stroke="#f1f5f9" strokeWidth="16" />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke={CRITICAL} strokeWidth="16"
          strokeDasharray={`${c} ${c}`} transform="rotate(-90 60 60)"
        />
        <circle
          cx="60" cy="60" r={r} fill="none" stroke={GOOD} strokeWidth="16"
          strokeDasharray={`${dashCumple} ${c}`} transform="rotate(-90 60 60)"
        >
          <title>{`Cumple SLA: ${cumplen} (${pctCumple.toFixed(1)}%)`}</title>
        </circle>
        <text x="60" y="56" textAnchor="middle" fontSize="22" fontWeight="800" fill={CLARO_DARK}>{pctCumple.toFixed(0)}%</text>
        <text x="60" y="74" textAnchor="middle" fontSize="10" fill={CLARO_MUTED}>cumple SLA</text>
      </svg>
      <div style={{ display: "flex", flexDirection: "column", gap: "8px", fontSize: "13px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: GOOD, display: "inline-block" }} />
          Cumple SLA — <strong>{cumplen || 0}</strong>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: CRITICAL, display: "inline-block" }} />
          No cumple SLA — <strong>{noCumplen || 0}</strong>
        </div>
      </div>
    </div>
  );
}

// Línea simple de evolución mensual (SVG puro)
function LineaHistorico({ puntos, campos, mostrarValores = false, sufijo = "" }) {
  if (!puntos || puntos.length === 0) return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Aún no hay cortes suficientes para ver tendencia.</div>;
  const W = 760, H = mostrarValores ? 240 : 220, PAD = mostrarValores ? 44 : 36;
  const maxY = Math.max(...puntos.flatMap((p) => campos.map((c) => Number(p[c.key]) || 0)), 1);
  const stepX = puntos.length > 1 ? (W - PAD * 2) / (puntos.length - 1) : 0;
  const y = (v) => H - PAD - (v / maxY) * (H - PAD * 2);
  const x = (i) => PAD + i * stepX;

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: "100%" }}>
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => (
          <line key={i} x1={PAD} x2={W - PAD} y1={H - PAD - f * (H - PAD * 2)} y2={H - PAD - f * (H - PAD * 2)} stroke="#e1e0d9" strokeWidth="1" />
        ))}
        {campos.map((c, ci) => {
          const d = puntos.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(Number(p[c.key]) || 0)}`).join(" ");
          return <path key={ci} d={d} fill="none" stroke={c.color} strokeWidth="2.5" strokeLinecap="round" />;
        })}
        {campos.map((c, ci) => puntos.map((p, i) => (
          <circle key={`${ci}-${i}`} cx={x(i)} cy={y(Number(p[c.key]) || 0)} r="4" fill={c.color}>
            <title>{`${c.label} — ${formatoFechaGuion(p.fecha_corte)}: ${p[c.key]}`}</title>
          </circle>
        )))}
        {/* Valor visible encima de cada punto (no solo al pasar el mouse) —
            solo cuando mostrarValores=true, para no saturar gráficas con
            varias líneas superpuestas (como la del tab Histórico). */}
        {mostrarValores && campos.map((c, ci) => puntos.map((p, i) => (
          <text key={`v-${ci}-${i}`} x={x(i)} y={y(Number(p[c.key]) || 0) - 10} textAnchor="middle" fontSize="11" fontWeight="700" fill={CLARO_DARK}>
            {p[c.key]}{sufijo}
          </text>
        )))}
        {puntos.map((p, i) => (
          <text key={i} x={x(i)} y={H - 10} textAnchor="middle" fontSize="9" fill={CLARO_MUTED}>{formatoFechaGuion(p.fecha_corte)}</text>
        ))}
      </svg>
      <div style={{ display: "flex", gap: "18px", marginTop: "8px", flexWrap: "wrap" }}>
        {campos.map((c, i) => (
          <div key={i} style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "12px", color: CLARO_DARK }}>
            <span style={{ width: "10px", height: "10px", borderRadius: "3px", background: c.color, display: "inline-block" }} />
            {c.label}
          </div>
        ))}
      </div>
    </div>
  );
}

// Gráfica gerencial combinada: barras apiladas (Cumple/No cumple SLA) + línea
// de % de cumplimiento + badges de Nuevas/Salieron por corte. Reemplaza la
// línea simple + la tabla "Resumen por corte" que antes iban separadas —
// integra toda esa información en una sola vista.
//
// Escala compartida entre barras y línea (el eje izquierdo es de conteo, el
// derecho de %). El TOTAL de cada barra va SIEMPRE por fuera, justo encima de
// toda la pila (verde + rojo) — nunca dentro del segmento rojo, para que no
// parezca que ese número es solo "no cumple" cuando en realidad es el total
// completo. El % de la línea va SIEMPRE encima de su propio punto. Cuando la
// punta de la barra y el punto de la línea quedan muy cerca para un mismo
// corte, en vez de forzar una posición fija que cambiaría el significado
// visual, se detecta el choque y se separan dinámicamente (la etiqueta más
// abajo de las dos se empuja más arriba, solo cuando hace falta). Cada
// etiqueta lleva además un fondo blanco (pill) para leerse bien aunque quede
// cerca de la barra, la línea o el grid.
function ComboChartSlaDiaDia({ historico }) {
  if (!historico || historico.length === 0) {
    return <div style={{ color: CLARO_MUTED, fontSize: "13px" }}>Aún no hay suficientes cortes cargados para ver la evolución.</div>;
  }
  const W = 980, H = 320, PADL = 46, PADR = 46, PADB = 60, PADT = 46;
  const innerW = W - PADL - PADR, innerH = H - PADT - PADB;
  const n = historico.length;
  const datos = historico.map((h) => ({
    fecha: h.fecha_corte,
    total: Number(h.total_backlog) || 0,
    cumple: Number(h.cumplen_sla) || 0,
    noCumple: Number(h.no_cumplen_sla) || 0,
    pct: Number(h.pct_cumplimiento) || 0,
    nuevas: Number(h.nuevas) || 0,
    salieron: Number(h.salieron) || 0,
  }));
  const maxTotal = Math.max(...datos.map((d) => d.total), 1);
  const axisMax = Math.ceil(maxTotal / 50) * 50 || 50;
  const bandW = innerW / n;
  const barW = Math.min(bandW * 0.46, 56);
  const x = (i) => PADL + bandW * i + bandW / 2;
  const yCount = (v) => PADT + innerH - (v / axisMax) * innerH;
  const yPct = (p) => PADT + innerH - (p / 100) * innerH;
  const MIN_GAP = 16;

  // Posiciones de las dos etiquetas por columna, con choque resuelto
  const labelPos = datos.map((d) => {
    const yNoCumpleTop = yCount(d.cumple + d.noCumple);
    const yLinePoint = yPct(d.pct);
    let yTotal = yNoCumpleTop - 10;
    let yPctLbl = yLinePoint - 11;
    if (Math.abs(yTotal - yPctLbl) < MIN_GAP) {
      if (yTotal <= yPctLbl) yPctLbl = yTotal - MIN_GAP;
      else yTotal = yPctLbl - MIN_GAP;
    }
    return { yTotal, yPctLbl, yLinePoint, yNoCumpleTop };
  });

  // Etiqueta con fondo blanco (pill) para legibilidad sobre barras/línea/grid
  const PillLabel = ({ cx, cy, text, color, fontSize, weight }) => {
    const w = Math.max(String(text).length * (fontSize * 0.62) + 8, 20);
    const h = fontSize + 7;
    return (
      <>
        <rect x={cx - w / 2} y={cy - h + 3} width={w} height={h} rx={4} fill="white" fillOpacity={0.92} />
        <text x={cx} y={cy} textAnchor="middle" fontSize={fontSize} fontWeight={weight || 800} fill={color}>{text}</text>
      </>
    );
  };

  const dLine = datos.map((d, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${yPct(d.pct)}`).join(" ");

  return (
    <div>
      <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: "100%", fontFamily: "inherit" }}>
        {/* Gridlines + eje izquierdo (conteo) */}
        {[0, 0.25, 0.5, 0.75, 1].map((f, i) => {
          const yy = PADT + innerH - f * innerH;
          return (
            <Fragment key={i}>
              <line x1={PADL} x2={W - PADR} y1={yy} y2={yy} stroke="#eef0f4" strokeWidth="1" />
              <text x={PADL - 10} y={yy + 3} textAnchor="end" fontSize="10" fill="#97a1b3">{Math.round(axisMax * f)}</text>
            </Fragment>
          );
        })}
        {/* Eje derecho (%) */}
        {[0, 50, 100].map((p) => (
          <text key={p} x={W - PADR + 10} y={yPct(p) + 3} textAnchor="start" fontSize="10" fill="#97a1b3">{p}%</text>
        ))}

        {/* Barras apiladas Cumple (abajo) + No cumple (arriba) */}
        {datos.map((d, i) => {
          const cx = x(i);
          const yCumpleTop = yCount(d.cumple);
          const yBase = yCount(0);
          const yNoCumpleTop = labelPos[i].yNoCumpleTop;
          const pctCumpleCorte = d.total ? ((d.cumple / d.total) * 100).toFixed(1) : "0.0";
          const pctNoCumpleCorte = d.total ? ((d.noCumple / d.total) * 100).toFixed(1) : "0.0";
          return (
            <Fragment key={i}>
              <rect x={cx - barW / 2} y={yCumpleTop} width={barW} height={yBase - yCumpleTop} fill={GOOD} rx="3">
                <title>{`${formatoFechaGuion(d.fecha)} — Cumple SLA: ${d.cumple} (${pctCumpleCorte}% del corte)`}</title>
              </rect>
              <rect x={cx - barW / 2} y={yNoCumpleTop} width={barW} height={yCumpleTop - yNoCumpleTop} fill={CRITICAL} rx="3">
                <title>{`${formatoFechaGuion(d.fecha)} — No cumple SLA: ${d.noCumple} (${pctNoCumpleCorte}% del corte)`}</title>
              </rect>
              {/* Total: SIEMPRE por fuera, encima de toda la pila (verde + rojo) */}
              <PillLabel cx={cx} cy={labelPos[i].yTotal} text={d.total} color={CLARO_DARK} fontSize={11} />
              {/* Nuevas / Salieron como badges debajo del eje */}
              <text x={cx - 15} y={H - 32} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={BLUE}>▲{d.nuevas}</text>
              <text x={cx + 15} y={H - 32} textAnchor="middle" fontSize="9.5" fontWeight="700" fill={GOOD}>▼{d.salieron}</text>
              {/* Etiqueta de fecha */}
              <text x={cx} y={H - 14} textAnchor="middle" fontSize="10.5" fill={CLARO_MUTED}>{formatoFechaGuion(d.fecha)}</text>
            </Fragment>
          );
        })}

        {/* Línea de % cumplimiento — su etiqueta SIEMPRE por encima del punto */}
        <path d={dLine} fill="none" stroke={CLARO_DARK} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {datos.map((d, i) => {
          const cx = x(i), cy = labelPos[i].yLinePoint;
          return (
            <Fragment key={`l-${i}`}>
              <circle cx={cx} cy={cy} r="4" fill="white" stroke={CLARO_DARK} strokeWidth="2.5">
                <title>{`${formatoFechaGuion(d.fecha)} — % Cumplimiento: ${d.pct}%`}</title>
              </circle>
              <PillLabel cx={cx} cy={labelPos[i].yPctLbl} text={`${d.pct}%`} color={CLARO_DARK} fontSize={10.5} />
            </Fragment>
          );
        })}
      </svg>
      <div style={{ display: "flex", gap: "18px", marginTop: "10px", flexWrap: "wrap", fontSize: "12px", color: CLARO_DARK }}>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><span style={{ width: "10px", height: "10px", borderRadius: "3px", background: GOOD, display: "inline-block" }} />Cumple SLA</div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><span style={{ width: "10px", height: "10px", borderRadius: "3px", background: CRITICAL, display: "inline-block" }} />No cumple SLA</div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px" }}><span style={{ width: "10px", height: "2px", background: CLARO_DARK, display: "inline-block" }} />% Cumplimiento</div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: BLUE, fontWeight: "700" }}>▲ Nuevas</div>
        <div style={{ display: "flex", alignItems: "center", gap: "6px", color: GOOD, fontWeight: "700" }}>▼ Salieron</div>
      </div>
    </div>
  );
}

function Th({ children }) {
  return <th style={{ padding: "11px 10px", textAlign: "left", fontSize: "11px", fontWeight: "800", color: "#475569", textTransform: "uppercase", letterSpacing: "0.045em", background: "#f1f5f9", borderBottom: `2px solid ${BORDER}`, whiteSpace: "nowrap" }}>{children}</th>;
}
// Encabezado clicable para ordenar la tabla ascendente/descendente por ese campo
function ThOrdenable({ campo, label, orden, onOrdenar }) {
  const activo = orden.campo === campo;
  const flecha = activo ? (orden.direccion === "asc" ? "▲" : "▼") : "⇅";
  return (
    <th onClick={() => onOrdenar(campo)} title="Clic para ordenar" style={{
      padding: "11px 10px", textAlign: "left", fontSize: "11px", fontWeight: "800",
      color: activo ? CLARO_RED : "#475569", textTransform: "uppercase", letterSpacing: "0.045em", background: "#f1f5f9",
      borderBottom: `2px solid ${activo ? CLARO_RED : BORDER}`, whiteSpace: "nowrap", cursor: "pointer", userSelect: "none",
    }}>
      {label} <span style={{ fontSize: "9px", opacity: activo ? 1 : 0.45 }}>{flecha}</span>
    </th>
  );
}
// Texto de celda en negro casi puro (antes CLARO_DARK #0f172a, ya oscuro
// pero no tan nítido en pantalla) — letra más clara y profesional, a pedido.
function Td({ children, style }) {
  return <td style={{ padding: "10px 10px", fontSize: "13px", color: "#0a0a0c", borderBottom: `1px solid ${BORDER_SOFT}`, ...style }}>{children}</td>;
}

// ─────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────
function BacklogR3({ usuario }) {
  const API = process.env.REACT_APP_API_URL;
  // El módulo es visible para todos los perfiles. La edición (avance,
  // validación y carga masiva) sigue restringida por rol — Admin y Jefe de
  // Zona siempre, y ahora también los aliados/contratistas — pero SOLO sobre
  // el corte vigente (el más reciente). Los cortes anteriores (histórico)
  // quedan en solo lectura para todos, excepto el Administrador, que puede
  // corregir cualquier corte. Ver `corteEsElMasReciente` más abajo.
  const rolLower = (usuario?.rol || "").toLowerCase().trim();
  const esAdminEstricto = rolLower === "administrador";
  // Antes esto dependía de una lista fija de roles ("jefe de zona",
  // "contratista conectar", etc). Ahora el Administrador decide, usuario
  // por usuario, desde "Gestión de Usuarios" (permiso del módulo
  // "Backlog OM Fijo R3"). Si nunca se configuró nada específico para ese
  // usuario, se sigue respetando el permiso general de siempre (puede_editar).
  const rolPuedeEditarEnGeneral = puedeEditarModulo(usuario, "backlog_r3");
  // Permiso APARTE (no depende del de arriba): prender/apagar el interruptor
  // "marcar para revisión" de una OT. Por defecto nadie lo tiene salvo el
  // Admin — el Admin se lo asigna puntualmente a quien decida desde Gestión
  // de Usuarios (checkbox "Marcar OT para revisión (Backlog R3)").
  const puedeMarcarRevision = puedeMarcarRevisionBacklog(usuario);

  const [tab, setTab] = useState("avances");
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState("");

  // Carga la tipografía del refresco visual (Plus Jakarta Sans) una sola vez.
  // Se aplica solo dentro de este módulo (ver fontFamily en el div raíz más
  // abajo), así que no afecta el resto de la app (sidebar/topbar).
  useEffect(() => {
    if (document.getElementById("backlog-r3-font")) return;
    const link = document.createElement("link");
    link.id = "backlog-r3-font";
    link.rel = "stylesheet";
    link.href = "https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@400;500;600;700;800&display=swap";
    document.head.appendChild(link);
    // Los <button>/<input>/<select> no heredan font-family por defecto del
    // navegador — sin esto, los textos de botones/selects se verían con la
    // fuente genérica del sistema en vez de Plus Jakarta Sans.
    const style = document.createElement("style");
    style.id = "backlog-r3-font-reset";
    style.textContent =
      ".backlog-r3-root button, .backlog-r3-root input, .backlog-r3-root select, .backlog-r3-root textarea { font-family: inherit; }" +
      ".backlog-r3-root input:focus, .backlog-r3-root select:focus { outline: none; border-color: " + CLARO_RED + " !important; box-shadow: " + FOCUS_RING + "; }";
    document.head.appendChild(style);
  }, []);

  const [cortes, setCortes] = useState([]);
  const [fechaCorte, setFechaCorte] = useState("");
  const [filtrosDisp, setFiltrosDisp] = useState({});
  // "tipo_operacion" queda fijo en "O&M Fijo" (este módulo es solo para
  // O&M Fijo) — ya no se ofrece como filtro seleccionable. "aliado" y
  // "diagnostico" se quitaron de los filtros visibles, pero se dejan en el
  // estado (siempre vacíos) porque el backend y `queryFiltros` ya los usan.
  // aliado_tecnico, jefe y departamento ahora son arrays (selección múltiple).
  // tipo_operacion se deja como string único (ver nota arriba). aliado y
  // diagnostico no tienen UI propia pero se dejan como arrays vacíos por
  // consistencia con el resto del estado.
  const [filtros, setFiltros] = useState({ aliado: [], aliado_tecnico: [], jefe: [], departamento: [], municipio: [], diagnostico: [], tipo_operacion: "O&M Fijo" });

  // Filtros PROPIOS de la pestaña "OT en Revisión" — a propósito son
  // independientes de los filtros generales de arriba (Corte, Aliado
  // técnico, Jefe, etc. de las demás pestañas): esa pestaña muestra un
  // historial que cruza cortes, así que no tendría sentido que el corte
  // general se lo filtrara. Mismos tipos de filtro, pero su propio estado.
  const [filtrosRevision, setFiltrosRevision] = useState({ aliado_tecnico: "", jefe: "", diagnostico: "", estado_seguimiento: "" });
  const hayFiltrosRevisionActivos = !!filtrosRevision.aliado_tecnico || !!filtrosRevision.jefe || !!filtrosRevision.diagnostico || !!filtrosRevision.estado_seguimiento;

  // Valores FIJOS del campo "Estado" de seguimiento (pestaña "OT en
  // Revisión") — no son libres como antes, siempre son uno de estos 4.
  const ESTADOS_REVISION = ["Sin escalar", "Escalado", "Resuelto", "Otro"];

  // Búsqueda rápida por OT y por Nodo (= campo "sitio" / ID sitio) — es un
  // filtro de texto libre que se aplica en el navegador sobre lo que ya se
  // cargó del corte actual (no hace falta ir al backend para esto).
  const [busquedaOt, setBusquedaOt] = useState("");
  const [busquedaNodo, setBusquedaNodo] = useState("");
  const normalizarBusqueda = (v) => String(v || "").trim().toLowerCase();

  // Cuadros resumen de la pestaña Avances, en formato de tabla dinámica
  // (igual a la tabla dinámica de Excel que usa el equipo): Resumen SLA y
  // Cumplimiento SLA por rango de antigüedad. Cada celda/fila es, a la vez,
  // un filtro más que se combina con los de arriba (Aliado técnico, Buscar
  // OT, etc.) y afecta tanto las tarjetas KPI como la tabla de abajo. Un
  // segundo clic sobre el mismo valor lo quita.
  const [filtroSla, setFiltroSla] = useState(null); // null | "cumple" | "no_cumple"
  // De dónde vino el filtro SLA actual: "sla" | "rango" | "categoria" |
  // "tipo_trabajo" | null (tarjetas KPI de arriba, sin tabla "dueña"). Sirve
  // para que, al hacer clic en un valor DENTRO de una tabla (que fija SLA
  // junto con su propio dato), esa MISMA tabla no se recorte a sí misma —
  // pero las demás tablas sí reflejen ese filtro SLA con normalidad.
  const [origenSla, setOrigenSla] = useState(null);
  const setFiltroSlaDesdeSla = useCallback((v) => { setFiltroSla(v); setOrigenSla(v == null ? null : "sla"); }, []);
  // Las tarjetas KPI "Cumple SLA"/"No cumple SLA" de arriba no son "una
  // tabla" — así que al usarlas, TODAS las tablas (Resumen SLA incluida)
  // deben reflejar el filtro con normalidad.
  const setFiltroSlaGenerico = useCallback((v) => { setFiltroSla(v); setOrigenSla(null); }, []);
  const setFiltroSlaDesdeRango = useCallback((v) => { setFiltroSla(v); setOrigenSla(v == null ? null : "rango"); }, []);
  const setFiltroSlaDesdeCategoria = useCallback((v) => { setFiltroSla(v); setOrigenSla(v == null ? null : "categoria"); }, []);
  const setFiltroSlaDesdeTipoTrabajo = useCallback((v) => { setFiltroSla(v); setOrigenSla(v == null ? null : "tipo_trabajo"); }, []);
  const setFiltroSlaDesdeJefe = useCallback((v) => { setFiltroSla(v); setOrigenSla(v == null ? null : "jefe"); }, []);
  const [filtroRango, setFiltroRango] = useState(null); // null | string (incluye "(sin dato)")
  const [filtroTipoTrabajo, setFiltroTipoTrabajo] = useState(null); // null | string (tipo_trabajo_op, incluye "(Sin tipificar)")
  const [filtroTipoFamilia, setFiltroTipoFamilia] = useState(null); // null | string (tipo_familia, dentro de filtroTipoTrabajo)
  const [filtroCategoria, setFiltroCategoria] = useState(null); // null | string (categoria_seguimiento)
  const [filtroSubcategoria, setFiltroSubcategoria] = useState(null); // null | string (sub_estado_seguimiento, dentro de filtroCategoria)
  // Tabla dinámica "Resumen por Jefe de Zona": Jefe como fila principal y
  // Municipio como sub-fila desplegable debajo — mismo patrón que Tipo
  // Trabajo OP / Tipo Familia. Nombres distintos a los filtros de arriba
  // (filtros.jefe / filtros.municipio, que son el buscador general de la
  // pestaña) para que no se pisen entre sí.
  const [filtroJefeResumen, setFiltroJefeResumen] = useState(null); // null | string (jefe, incluye "(Sin dato)")
  const [filtroMunicipioResumen, setFiltroMunicipioResumen] = useState(null); // null | string (municipio, dentro de filtroJefeResumen)
  // Igual patrón, pero para las tarjetas KPI de arriba de Avances (Nuevo /
  // Actualizado / Sin cambio, Validadas) — un clic en la tarjeta filtra,
  // otro clic la quita.
  const [filtroTipoCambio, setFiltroTipoCambio] = useState(null); // null | "nuevo" | "actualizado" | "sin_cambio"
  const [filtroValidadas, setFiltroValidadas] = useState(null); // null | true (solo OT con categoria_seguimiento)
  // Igual que los filtros de arriba, pero para las tarjetas KPI de la pestaña
  // Pronóstico: un clic en una tarjeta de estado (Ya incumple / En riesgo /
  // Atención / Sin meta SLA) filtra la tabla de abajo, otro clic la quita.
  const [filtroEstadoProyeccion, setFiltroEstadoProyeccion] = useState(null); // null | "ya_incumple" | "en_riesgo" | "atencion" | "cumple_proyectado" | "sin_meta"

  // Para saber si hay algo que limpiar: cualquier filtro de dropdown con
  // valor distinto del default, o alguna de las dos búsquedas con texto.
  const hayFiltrosActivos =
    !!busquedaOt || !!busquedaNodo ||
    filtros.aliado.length > 0 || filtros.aliado_tecnico.length > 0 || filtros.jefe.length > 0 ||
    filtros.departamento.length > 0 || filtros.municipio.length > 0 || filtros.diagnostico.length > 0 ||
    (filtros.tipo_operacion || "") !== "O&M Fijo" ||
    !!filtroSla || !!filtroRango || !!filtroTipoTrabajo || !!filtroTipoFamilia || !!filtroCategoria || !!filtroSubcategoria || !!filtroEstadoProyeccion ||
    !!filtroTipoCambio || !!filtroValidadas || !!filtroJefeResumen || !!filtroMunicipioResumen;

  // Escala de color tipo Excel (rojo → amarillo → verde) para la columna %
  // de las tablas dinámicas.
  const colorPct = (pct) => {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    const mix = (c1, c2, t) => c1.map((v, i) => Math.round(v + (c2[i] - v) * t));
    const rojo = [248, 105, 107], amarillo = [255, 235, 132], verde = [99, 190, 123];
    const rgb = p <= 50 ? mix(rojo, amarillo, p / 50) : mix(amarillo, verde, (p - 50) / 50);
    return `rgb(${rgb.join(",")})`;
  };
  const pctTexto = (cumple, total) => (total ? `${((cumple / total) * 100).toFixed(2).replace(".", ",")}%` : "—");
  const pctNum = (cumple, total) => (total ? (cumple / total) * 100 : 0);

  // "% impacto": qué parte de TODO el incumplimiento SLA (dentro del alcance
  // filtrado actual) aporta esta fila — no es el % de cumplimiento de la
  // fila, es su participación dentro del total de OT que NO cumplen.
  // Cuando no hay ningún caso que incumpla SLA (noCumpleTotal = 0) no hay
  // nada que repartir: en vez del feo "#DIV/0!" se muestra un guion, que
  // en este contexto es buena noticia (nada incumple).
  const pctImpactoTexto = (noCumpleFila, noCumpleTotal) =>
    (noCumpleTotal ? `${((noCumpleFila / noCumpleTotal) * 100).toFixed(2).replace(".", ",")}%` : "—");
  const pctImpactoNum = (noCumpleFila, noCumpleTotal) => (noCumpleTotal ? (noCumpleFila / noCumpleTotal) * 100 : 0);
  // Tinte suave (blanco → rojo) proporcional al % de impacto — a mayor
  // participación en el incumplimiento, más intenso.
  const colorImpacto = (pct) => {
    const p = Math.max(0, Math.min(100, Number(pct) || 0));
    const alpha = 0.08 + (p / 100) * 0.45;
    return `rgba(208,59,59,${alpha.toFixed(2)})`;
  };

  const [resumen, setResumen] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [comparacion, setComparacion] = useState({ fecha_hoy: null, fecha_ayer: null, filas: [] });
  // Historial de OT "marcadas para revisión" (interruptor de Avances) — es
  // un registro permanente: una fila por cada vez que alguien marcó una OT
  // (foto completa del registro en ese momento), nunca desaparece sola.
  const [revisionHistorial, setRevisionHistorial] = useState([]);
  const [marcandoOt, setMarcandoOt] = useState(null);
  const [proyeccion, setProyeccion] = useState({ fecha_corte: null, fecha_objetivo: null, filas: [] });

  // Tipificación de rutas (Tipo Trabajo OP / Tipo Familia) — rutas del corte
  // vigente que aún no tienen tipificación guardada, para poder completarlas
  // desde la pestaña Avances sin tener que adivinar o buscarlas a mano.
  const [rutasSinTipificar, setRutasSinTipificar] = useState([]);
  const [mostrarRutasSinTipificar, setMostrarRutasSinTipificar] = useState(false);
  const [tipificacionAbierta, setTipificacionAbierta] = useState(null); // { ruta_clasificacion, cantidad_ot } o null
  const [tipificacionDatos, setTipificacionDatos] = useState({ tipo_trabajo_op: "", tipo_familia: "" });
  const [guardandoTipificacion, setGuardandoTipificacion] = useState(false);
  const [errorTipificacion, setErrorTipificacion] = useState(null);
  // La proyección siempre es a fin de mes del corte vigente (el backend la calcula
  // por defecto con LAST_DAY() cuando no se envía fecha_objetivo); no es configurable.

  // El primer corte de la lista (orden DESC) es siempre el más reciente/vigente.
  const corteEsElMasReciente = cortes.length > 0 && fechaCorte === cortes[0];
  const puedeEditar = esAdminEstricto || (rolPuedeEditarEnGeneral && corteEsElMasReciente);

  // ── Orden de la tabla de Avances (clic en encabezado OT / Antigüedad) ──
  const [orden, setOrden] = useState({ campo: null, direccion: "asc" });
  const alternarOrden = (campo) => {
    setOrden((o) => (o.campo === campo ? { campo, direccion: o.direccion === "asc" ? "desc" : "asc" } : { campo, direccion: "asc" }));
  };
  // Búsqueda de OT / Nodo (campo "sitio") — se aplica antes de ordenar.
  const avancesFiltrados = useMemo(() => {
    const ot = normalizarBusqueda(busquedaOt);
    const nodo = normalizarBusqueda(busquedaNodo);
    if (!ot && !nodo) return comparacion.filas;
    return comparacion.filas.filter((f) => {
      const coincideOt = !ot || normalizarBusqueda(f.ot_lls).includes(ot);
      const coincideNodo = !nodo || normalizarBusqueda(f.sitio).includes(nodo);
      return coincideOt && coincideNodo;
    });
  }, [comparacion.filas, busquedaOt, busquedaNodo]);

  // Aplica los filtros de las tablas dinámicas (SLA / Rango de antigüedad)
  // sobre `lista`. `excluir` deja afuera uno de los dos para poder calcular,
  // para cada tabla, los totales según los OTROS filtros ya aplicados
  // (incluida la búsqueda de OT/Nodo) sin que la propia tabla se filtre a sí
  // misma — así sus números no "desaparecen" al seleccionar un valor de esa
  // misma tabla.
  const aplicaFiltrosResumen = useCallback((lista, excluir) => {
    return lista.filter((f) => {
      // Resumen SLA ("sla") siempre muestra el desglose completo Cumple/No
      // cumple, sin importar el origen (comportamiento de siempre). Para las
      // demás tablas, el filtro SLA se salta solo cuando la tabla que se
      // está calculando (`excluir`) es justo la que originó ese filtro con
      // su propio clic (`origenSla`) — así esa tabla no se recorta a sí
      // misma, pero sigue reflejando el SLA cuando lo fijó otra tabla o una
      // tarjeta KPI.
      // `excluir` null = tabla de detalle de abajo: ahí el filtro SLA SIEMPRE
      // se aplica (antes, si el filtro venía de una tarjeta KPI —origenSla
      // null— null === null y el detalle se saltaba el filtro SLA).
      if (excluir !== "sla" && (excluir == null || origenSla !== excluir)) {
        if (filtroSla === "cumple" && !slaCumple(f.trabajo_cumple_sla)) return false;
        if (filtroSla === "no_cumple" && !slaNoCumple(f.trabajo_cumple_sla)) return false;
      }
      if (excluir !== "rango" && filtroRango && (f.rangos || "(sin dato)") !== filtroRango) return false;
      if (excluir !== "tipo_trabajo") {
        if (filtroTipoTrabajo && (f.tipo_trabajo_op || "(Sin tipificar)") !== filtroTipoTrabajo) return false;
        if (filtroTipoFamilia && (f.tipo_familia || "(Sin dato)") !== filtroTipoFamilia) return false;
      }
      if (excluir !== "categoria") {
        if (filtroCategoria && (f.categoria_seguimiento || "(Sin categorizar)") !== filtroCategoria) return false;
        if (filtroSubcategoria && (f.sub_estado_seguimiento || "(Sin dato)") !== filtroSubcategoria) return false;
      }
      if (excluir !== "jefe") {
        if (filtroJefeResumen && (f.jefe || "(Sin dato)") !== filtroJefeResumen) return false;
        if (filtroMunicipioResumen && (f.municipio || "(Sin dato)") !== filtroMunicipioResumen) return false;
      }
      if (excluir !== "tipo_cambio" && filtroTipoCambio && f.tipo_cambio !== filtroTipoCambio) return false;
      if (excluir !== "validadas" && filtroValidadas && !f.categoria_seguimiento) return false;
      return true;
    });
  }, [filtroSla, origenSla, filtroRango, filtroTipoTrabajo, filtroTipoFamilia, filtroCategoria, filtroSubcategoria, filtroJefeResumen, filtroMunicipioResumen, filtroTipoCambio, filtroValidadas]);

  // Base para cada tabla/tarjeta: todos los filtros activos salvo el propio
  // (para que sus números no "desaparezcan" al seleccionar un valor de esa
  // misma tabla/tarjeta).
  const baseParaSla = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "sla"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaRango = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "rango"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaTipoTrabajo = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "tipo_trabajo"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaCategoria = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "categoria"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaJefe = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "jefe"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaTipoCambio = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "tipo_cambio"), [avancesFiltrados, aplicaFiltrosResumen]);
  const baseParaValidadas = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, "validadas"), [avancesFiltrados, aplicaFiltrosResumen]);

  // Tabla dinámica 1 — Resumen SLA, desglosado por el Tipo de trabajo REAL de
  // cada OT (hoy.tipo_operacion), de mayor a menor cantidad. Mientras el
  // filtro "Tipo operación" de arriba esté en "O&M Fijo" solo va a salir esa
  // fila (porque el backend ya viene filtrado a eso); si lo sueltas a
  // "Todos" u otro valor, aquí aparecen todos los tipos de trabajo que haya
  // en ese corte, cada uno con su propia fila.
  const resumenSla = useMemo(() => {
    const porTipo = {};
    baseParaSla.forEach((f) => {
      const key = f.tipo_operacion || "(sin dato)";
      if (!porTipo[key]) porTipo[key] = { no_cumple: 0, cumple: 0, otros: 0 };
      if (slaNoCumple(f.trabajo_cumple_sla)) porTipo[key].no_cumple += 1;
      else if (slaCumple(f.trabajo_cumple_sla)) porTipo[key].cumple += 1;
      else porTipo[key].otros += 1; // SLA vacío u otro valor: cuenta en el Total para que cuadre con el detalle
    });
    const filas = Object.entries(porTipo)
      .map(([label, v]) => ({ label, no_cumple: v.no_cumple, cumple: v.cumple, total: v.no_cumple + v.cumple + v.otros }))
      .sort((a, b) => b.total - a.total);
    const no_cumple = filas.reduce((acc, f) => acc + f.no_cumple, 0);
    const cumple = filas.reduce((acc, f) => acc + f.cumple, 0);
    return { filas, no_cumple, cumple, total: filas.reduce((acc, f) => acc + f.total, 0) };
  }, [baseParaSla]);

  // Tabla dinámica 1b — Resumen SLA desglosado por Tipo Trabajo OP (la
  // tipificación propia que se le asigna a cada ruta de clasificación:
  // Preventivo / Correctivo / Empresas y Negocios / etc.), con el Tipo
  // Familia de esa misma tipificación como sub-fila desplegable debajo de
  // cada Tipo Trabajo OP. Las OT cuya ruta todavía no tiene tipificación
  // caen en "(Sin tipificar)".
  const resumenTipoTrabajo = useMemo(() => {
    const porTipo = {};
    baseParaTipoTrabajo.forEach((f) => {
      const key = f.tipo_trabajo_op || "(Sin tipificar)";
      const familiaKey = f.tipo_familia || "(Sin dato)";
      if (!porTipo[key]) porTipo[key] = { no_cumple: 0, cumple: 0, otros: 0, familias: {} };
      if (!porTipo[key].familias[familiaKey]) porTipo[key].familias[familiaKey] = { no_cumple: 0, cumple: 0, otros: 0 };
      const noCumpleFila = slaNoCumple(f.trabajo_cumple_sla);
      const cumpleFila = slaCumple(f.trabajo_cumple_sla);
      if (noCumpleFila) { porTipo[key].no_cumple += 1; porTipo[key].familias[familiaKey].no_cumple += 1; }
      else if (cumpleFila) { porTipo[key].cumple += 1; porTipo[key].familias[familiaKey].cumple += 1; }
      else { porTipo[key].otros += 1; porTipo[key].familias[familiaKey].otros += 1; }
    });
    const filas = Object.entries(porTipo)
      .map(([label, v]) => ({
        label, no_cumple: v.no_cumple, cumple: v.cumple, total: v.no_cumple + v.cumple + v.otros,
        familias: Object.entries(v.familias)
          .map(([fLabel, fv]) => ({ label: fLabel, no_cumple: fv.no_cumple, cumple: fv.cumple, total: fv.no_cumple + fv.cumple + fv.otros }))
          .sort((a, b) => b.total - a.total),
      }))
      .sort((a, b) => b.total - a.total);
    const no_cumple = filas.reduce((acc, f) => acc + f.no_cumple, 0);
    const cumple = filas.reduce((acc, f) => acc + f.cumple, 0);
    return { filas, no_cumple, cumple, total: filas.reduce((acc, f) => acc + f.total, 0) };
  }, [baseParaTipoTrabajo]);

  // Tabla dinámica 2 — Cumplimiento SLA por rango de antigüedad (subtotal
  // "O&M Fijo" + una fila por rango + Total general).
  const resumenRango = useMemo(() => {
    const porRango = {};
    baseParaRango.forEach((f) => {
      const key = f.rangos || "(sin dato)";
      if (!porRango[key]) porRango[key] = { no_cumple: 0, cumple: 0, otros: 0 };
      if (slaNoCumple(f.trabajo_cumple_sla)) porRango[key].no_cumple += 1;
      else if (slaCumple(f.trabajo_cumple_sla)) porRango[key].cumple += 1;
      else porRango[key].otros += 1; // SLA vacío u otro valor: cuenta en el Total para que cuadre con el detalle
    });
    // Las etiquetas ya vienen numeradas con letra (A., B., C.…), así que el
    // orden alfabético ya corresponde al orden de antigüedad creciente.
    const filas = Object.entries(porRango)
      .map(([label, v]) => ({ label, no_cumple: v.no_cumple, cumple: v.cumple, total: v.no_cumple + v.cumple + v.otros }))
      .sort((a, b) => a.label.localeCompare(b.label));
    const no_cumple = filas.reduce((acc, f) => acc + f.no_cumple, 0);
    const cumple = filas.reduce((acc, f) => acc + f.cumple, 0);
    return { filas, no_cumple, cumple, total: filas.reduce((acc, f) => acc + f.total, 0) };
  }, [baseParaRango]);

  // Tabla dinámica 3 — Categoría / Subcategoría de seguimiento: cuántas OT hay
  // en cada una y qué porcentaje del total filtrado representa, para ver cuál
  // categoría y cuál subcategoría impacta más. Cada categoría es un subtotal
  // con sus subcategorías debajo, todo ordenado de mayor a menor cantidad.
  const resumenCategoria = useMemo(() => {
    const porCategoria = {};
    baseParaCategoria.forEach((f) => {
      const catKey = f.categoria_seguimiento || "(Sin categorizar)";
      const subKey = f.sub_estado_seguimiento || "(Sin dato)";
      if (!porCategoria[catKey]) porCategoria[catKey] = { no_cumple: 0, cumple: 0, otros: 0, subcategorias: {} };
      if (!porCategoria[catKey].subcategorias[subKey]) porCategoria[catKey].subcategorias[subKey] = { no_cumple: 0, cumple: 0, otros: 0 };
      const noCumpleFila = slaNoCumple(f.trabajo_cumple_sla);
      const cumpleFila = slaCumple(f.trabajo_cumple_sla);
      if (noCumpleFila) { porCategoria[catKey].no_cumple += 1; porCategoria[catKey].subcategorias[subKey].no_cumple += 1; }
      else if (cumpleFila) { porCategoria[catKey].cumple += 1; porCategoria[catKey].subcategorias[subKey].cumple += 1; }
      else { porCategoria[catKey].otros += 1; porCategoria[catKey].subcategorias[subKey].otros += 1; }
    });
    const categorias = Object.entries(porCategoria)
      .map(([label, v]) => ({
        label,
        no_cumple: v.no_cumple,
        cumple: v.cumple,
        total: v.no_cumple + v.cumple + v.otros,
        subcategorias: Object.entries(v.subcategorias)
          .map(([subLabel, sv]) => ({ label: subLabel, no_cumple: sv.no_cumple, cumple: sv.cumple, total: sv.no_cumple + sv.cumple + sv.otros }))
          .sort((a, b) => b.total - a.total),
      }))
      .sort((a, b) => b.total - a.total);
    const no_cumple = categorias.reduce((acc, c) => acc + c.no_cumple, 0);
    const cumple = categorias.reduce((acc, c) => acc + c.cumple, 0);
    return { categorias, no_cumple, cumple, total: categorias.reduce((acc, f) => acc + f.total, 0) };
  }, [baseParaCategoria]);

  // Tabla dinámica 4 — Resumen por Jefe de Zona: cada Jefe como fila
  // principal (subtotal) y, debajo, un desglose por Municipio — mismo
  // patrón que "Resumen por Tipo Trabajo OP" (desplegable, contraído por
  // defecto).
  const resumenJefe = useMemo(() => {
    const porJefe = {};
    baseParaJefe.forEach((f) => {
      const key = f.jefe || "(Sin dato)";
      const munKey = f.municipio || "(Sin dato)";
      if (!porJefe[key]) porJefe[key] = { no_cumple: 0, cumple: 0, otros: 0, municipios: {} };
      if (!porJefe[key].municipios[munKey]) porJefe[key].municipios[munKey] = { no_cumple: 0, cumple: 0, otros: 0 };
      const noCumpleFila = slaNoCumple(f.trabajo_cumple_sla);
      const cumpleFila = slaCumple(f.trabajo_cumple_sla);
      if (noCumpleFila) { porJefe[key].no_cumple += 1; porJefe[key].municipios[munKey].no_cumple += 1; }
      else if (cumpleFila) { porJefe[key].cumple += 1; porJefe[key].municipios[munKey].cumple += 1; }
      else { porJefe[key].otros += 1; porJefe[key].municipios[munKey].otros += 1; }
    });
    const filas = Object.entries(porJefe)
      .map(([label, v]) => ({
        label, no_cumple: v.no_cumple, cumple: v.cumple, total: v.no_cumple + v.cumple + v.otros,
        municipios: Object.entries(v.municipios)
          .map(([mLabel, mv]) => ({ label: mLabel, no_cumple: mv.no_cumple, cumple: mv.cumple, total: mv.no_cumple + mv.cumple + mv.otros }))
          .sort((a, b) => b.total - a.total),
      }))
      .sort((a, b) => b.total - a.total);
    const no_cumple = filas.reduce((acc, f) => acc + f.no_cumple, 0);
    const cumple = filas.reduce((acc, f) => acc + f.cumple, 0);
    return { filas, no_cumple, cumple, total: filas.reduce((acc, f) => acc + f.total, 0) };
  }, [baseParaJefe]);

  const [editando, setEditando] = useState(null); // { ot_lls, campo, valor }

  // ── OT "fijadas": cuando se guarda un cambio (Categoría/Sub-estado/
  // Observaciones, o "Copiar de ayer") en una fila que, con el valor nuevo,
  // ya no coincide con el filtro activo de las tablas dinámicas (SLA / Rango
  // / Categoría / Tipo de cambio / Validadas), esa fila se queda fijada —
  // visible en la tabla de Avances de todas formas — tanto mientras se sigue
  // editando como DESPUÉS de guardada. Deja de estar fijada (y la tabla
  // vuelve a filtrar normal) solo cuando se suelta o se cambia alguno de esos
  // filtros, o se cambia de corte — nunca por el solo hecho de guardar o
  // cerrar la edición. Antes, la fila desaparecía apenas se guardaba el
  // cambio, sin dar tiempo a terminar de diligenciar los demás campos.
  const [otsFijadas, setOtsFijadas] = useState(() => new Set());
  useEffect(() => {
    setOtsFijadas(new Set());
  }, [filtroSla, filtroRango, filtroTipoTrabajo, filtroTipoFamilia, filtroCategoria, filtroSubcategoria, filtroJefeResumen, filtroMunicipioResumen, filtroTipoCambio, filtroValidadas, fechaCorte]);

  // Resultado final de Avances: búsqueda + las 3 tablas dinámicas, todas combinadas.
  const avancesPivotados = useMemo(() => aplicaFiltrosResumen(avancesFiltrados, null), [avancesFiltrados, aplicaFiltrosResumen]);

  // Le suma de vuelta cualquier OT fijada que el filtro activo haya dejado
  // afuera (buscándola en `avancesFiltrados`, que ya trae aplicada la
  // búsqueda de OT/Nodo pero NO los filtros de las tablas dinámicas).
  // Importante: se reinserta en su MISMA posición original (el orden de
  // avancesFiltrados), no al final — si no, apenas se guarda una categoría
  // que deja de cumplir el filtro activo, el renglón "salta" al fondo de la
  // tabla en plena edición y se pierde de dónde se estaba diligenciando.
  const avancesParaTabla = useMemo(() => {
    if (otsFijadas.size === 0) return avancesPivotados;
    const presentes = new Set(avancesPivotados.map((f) => f.ot_lls));
    const hayFaltantes = avancesFiltrados.some((f) => otsFijadas.has(f.ot_lls) && !presentes.has(f.ot_lls));
    if (!hayFaltantes) return avancesPivotados;
    return avancesFiltrados.filter((f) => presentes.has(f.ot_lls) || otsFijadas.has(f.ot_lls));
  }, [avancesPivotados, avancesFiltrados, otsFijadas]);

  const avancesOrdenados = useMemo(() => {
    if (!orden.campo) return avancesParaTabla;
    const filas = [...avancesParaTabla];
    const dir = orden.direccion === "asc" ? 1 : -1;
    filas.sort((a, b) => {
      if (orden.campo === "antiguedad_dias") {
        return ((Number(a.antiguedad_dias) || 0) - (Number(b.antiguedad_dias) || 0)) * dir;
      }
      // orden.campo === "ot_lls" — compara alfanuméricamente, respetando los números dentro del texto
      return String(a.ot_lls || "").localeCompare(String(b.ot_lls || ""), "es", { numeric: true, sensitivity: "base" }) * dir;
    });
    return filas;
  }, [avancesParaTabla, orden]);

  // Misma búsqueda aplicada a la pestaña Pronóstico.
  const proyeccionFiltrada = useMemo(() => {
    const ot = normalizarBusqueda(busquedaOt);
    const nodo = normalizarBusqueda(busquedaNodo);
    if (!ot && !nodo && !filtroEstadoProyeccion) return proyeccion.filas;
    return proyeccion.filas.filter((f) => {
      const coincideOt = !ot || normalizarBusqueda(f.ot_lls).includes(ot);
      const coincideNodo = !nodo || normalizarBusqueda(f.sitio).includes(nodo);
      const coincideEstado = !filtroEstadoProyeccion || f.estado_proyeccion === filtroEstadoProyeccion;
      return coincideOt && coincideNodo && coincideEstado;
    });
  }, [proyeccion.filas, busquedaOt, busquedaNodo, filtroEstadoProyeccion]);

  // ── Paginación (100 filas por página) para las tablas grandes ──
  const PAGE_SIZE = 100;
  const [paginaAvances, setPaginaAvances] = useState(1);
  const [paginaProyeccion, setPaginaProyeccion] = useState(1);
  useEffect(() => { setPaginaAvances(1); setPaginaProyeccion(1); }, [fechaCorte, filtros, orden, busquedaOt, busquedaNodo, filtroSla, filtroRango, filtroTipoTrabajo, filtroTipoFamilia, filtroCategoria, filtroSubcategoria, filtroJefeResumen, filtroMunicipioResumen, filtroEstadoProyeccion, filtroTipoCambio, filtroValidadas]);

  const avancesPagina = useMemo(() => avancesOrdenados.slice((paginaAvances - 1) * PAGE_SIZE, paginaAvances * PAGE_SIZE), [avancesOrdenados, paginaAvances]);
  const totalPagAvances = Math.max(1, Math.ceil(avancesOrdenados.length / PAGE_SIZE));
  const proyeccionPagina = useMemo(() => proyeccionFiltrada.slice((paginaProyeccion - 1) * PAGE_SIZE, paginaProyeccion * PAGE_SIZE), [proyeccionFiltrada, paginaProyeccion]);
  const totalPagProyeccion = Math.max(1, Math.ceil(proyeccionFiltrada.length / PAGE_SIZE));

  const [guardando, setGuardando] = useState(false);

  // ── Modal de historial completo de una OT (desde la pestaña Comparación) ──
  const [historialAbierto, setHistorialAbierto] = useState(null); // ot_lls o null
  const [historialDatos, setHistorialDatos] = useState([]);
  const [historialCargando, setHistorialCargando] = useState(false);
  // Evolución día a día: una fila por corte desde que la OT nace en el
  // tablero hasta el corte más reciente (ver /backlog-evolucion-ot).
  const [evolucionDatos, setEvolucionDatos] = useState([]);
  const [evolucionCargando, setEvolucionCargando] = useState(false);

  const abrirHistorial = (ot_lls) => {
    setHistorialAbierto(ot_lls);
    setHistorialCargando(true);
    setEvolucionCargando(true);
    fetch(`${API}/backlog-historico/${encodeURIComponent(ot_lls)}`)
      .then((r) => r.json())
      .then((data) => setHistorialDatos(Array.isArray(data) ? data : []))
      .catch(() => setHistorialDatos([]))
      .finally(() => setHistorialCargando(false));
    fetch(`${API}/backlog-evolucion-ot/${encodeURIComponent(ot_lls)}`)
      .then((r) => r.json())
      .then((data) => setEvolucionDatos(Array.isArray(data) ? data : []))
      .catch(() => setEvolucionDatos([]))
      .finally(() => setEvolucionCargando(false));
  };

  // "Copiar a hoy" desde el modal de Evolución día a día: trae la Categoría,
  // Sub-estado y Observaciones del corte anterior (fila que se pasa como
  // `filaAnterior`) y las guarda en el corte de hoy de la OT abierta en el
  // modal — reemplaza la columna "Copiar" que antes tenía la tabla principal
  // de Avances, ahora disponible desde el historial.
  const [copiandoDesdeHistorial, setCopiandoDesdeHistorial] = useState(false);
  const copiarDesdeHistorial = (filaAnterior) => {
    if (!historialAbierto || copiandoDesdeHistorial) return;
    const valores = {
      categoria: filaAnterior.categoria_seguimiento || "",
      sub_estado: filaAnterior.sub_estado_seguimiento || "",
      observaciones: filaAnterior.observaciones_seguimiento || "",
    };
    setCopiandoDesdeHistorial(true);
    guardarValidacion(historialAbierto, valores);
    // Refleja el cambio de inmediato en la fila "hoy" del modal (que sigue
    // abierto), sin esperar a recargar la evolución desde el servidor.
    setEvolucionDatos((prev) => {
      if (prev.length === 0) return prev;
      const ultimo = prev.length - 1;
      return prev.map((f, i) => (i === ultimo ? {
        ...f,
        categoria_seguimiento: valores.categoria,
        sub_estado_seguimiento: valores.sub_estado,
        observaciones_seguimiento: valores.observaciones,
      } : f));
    });
    setTimeout(() => setCopiandoDesdeHistorial(false), 400);
  };

  // ── Catálogo de categorías / sub-categorías de validación (viene de la BD) ──
  const [catalogo, setCatalogo] = useState(CATALOGO_RESPALDO);
  const cargarCatalogo = useCallback(() => {
    fetch(`${API}/backlog-categorias`).then((r) => r.json()).then((data) => {
      if (Array.isArray(data) && data.length > 0) setCatalogo(data);
    }).catch(() => {});
  }, [API]);

  // Ordenados alfabéticamente; el sub-estado depende de la categoría elegida
  const categoriasDisponibles = useMemo(
    () => Array.from(new Set(catalogo.map((c) => c.categoria))).sort((a, b) => a.localeCompare(b, "es")),
    [catalogo]
  );
  const subEstadosPorCategoria = useMemo(() => {
    const mapa = {};
    catalogo.forEach((c) => { if (!mapa[c.categoria]) mapa[c.categoria] = []; mapa[c.categoria].push(c.sub_estado); });
    Object.keys(mapa).forEach((k) => mapa[k].sort((a, b) => a.localeCompare(b, "es")));
    return mapa;
  }, [catalogo]);

  // ── Gestionar tipificaciones (cambio masivo de Tipo Trabajo OP / Familia) — SOLO Admin ──
  const [modalTipifAbierto, setModalTipifAbierto] = useState(false);
  const [tipifLista, setTipifLista] = useState([]);
  const [tipifEdit, setTipifEdit] = useState(null); // { tipo, familia, nuevoTipo, nuevaFamilia }
  const [tipifMensaje, setTipifMensaje] = useState(null);
  const [tipifGuardando, setTipifGuardando] = useState(false);
  const cargarTipifLista = () => {
    fetch(`${API}/backlog-tipificaciones`).then((r) => r.json())
      .then((d) => { if (d.ok) setTipifLista(d.filas || []); })
      .catch(() => setTipifMensaje({ ok: false, texto: "No se pudo cargar la lista." }));
  };
  const abrirGestionTipif = () => { setModalTipifAbierto(true); setTipifEdit(null); setTipifMensaje(null); cargarTipifLista(); };
  const tipifGrupos = useMemo(() => {
    const m = {};
    tipifLista.forEach((r) => {
      const k = `${r.tipo_trabajo_op || ""}||${r.tipo_familia || ""}`;
      if (!m[k]) m[k] = { tipo: r.tipo_trabajo_op || "", familia: r.tipo_familia || "", cantidad: 0 };
      m[k].cantidad += 1;
    });
    return Object.values(m).sort((a, b) => a.tipo.localeCompare(b.tipo) || a.familia.localeCompare(b.familia));
  }, [tipifLista]);
  const guardarCambioTipif = () => {
    if (!tipifEdit.nuevoTipo.trim()) { setTipifMensaje({ ok: false, texto: "Escribe el nuevo Tipo Trabajo OP." }); return; }
    setTipifGuardando(true); setTipifMensaje(null);
    fetch(`${API}/backlog-tipificaciones-cambio-masivo`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        rol: usuario?.rol, desde_tipo: tipifEdit.tipo, desde_familia: tipifEdit.familia,
        hacia_tipo: tipifEdit.nuevoTipo, hacia_familia: tipifEdit.nuevaFamilia,
      }),
    }).then((r) => r.json())
      .then((d) => {
        if (!d.ok) { setTipifMensaje({ ok: false, texto: d.mensaje || "No se pudo guardar." }); return; }
        setTipifMensaje({ ok: true, texto: `Listo: ${d.cambiadas} ruta(s) actualizadas.` });
        setTipifEdit(null); cargarTipifLista(); cargarComparacion();
      })
      .catch(() => setTipifMensaje({ ok: false, texto: "No se pudo conectar con el servidor." }))
      .finally(() => setTipifGuardando(false));
  };

  // ── Alta de categoría / sub-categoría nueva — SOLO Administrador ──
  const [modalCategoriaAbierto, setModalCategoriaAbierto] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState("");
  const [nuevaSubcategoria, setNuevaSubcategoria] = useState("");
  const [guardandoCategoria, setGuardandoCategoria] = useState(false);
  const [mensajeCategoria, setMensajeCategoria] = useState(null); // { ok, texto }

  const guardarNuevaCategoria = () => {
    const categoria = nuevaCategoria.trim();
    const sub_estado = nuevaSubcategoria.trim();
    if (!categoria || !sub_estado) { setMensajeCategoria({ ok: false, texto: "Completa la categoría y la sub-categoría." }); return; }
    setGuardandoCategoria(true); setMensajeCategoria(null);
    fetch(`${API}/backlog-categorias`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ categoria, sub_estado, rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.ok) {
          setMensajeCategoria({ ok: true, texto: data.ya_existia ? "Esa combinación ya existía en el catálogo." : "Agregada correctamente." });
          setNuevaCategoria(""); setNuevaSubcategoria("");
          cargarCatalogo();
        } else {
          setMensajeCategoria({ ok: false, texto: data.mensaje || "No se pudo guardar." });
        }
      })
      .catch(() => setMensajeCategoria({ ok: false, texto: "No se pudo conectar con el servidor." }))
      .finally(() => setGuardandoCategoria(false));
  };

  // ── Carga de catálogos (cortes disponibles + filtros) ──
  useEffect(() => {
    fetch(`${API}/backlog-cortes-disponibles`).then((r) => r.json()).then((data) => {
      setCortes(data || []);
      if (data && data.length > 0) setFechaCorte(data[0]);
    }).catch(() => {});
    cargarCatalogo();
  }, [API, cargarCatalogo]);

  // Las opciones de los filtros (Aliado técnico, Jefe, Departamento, Tipo
  // operación, etc.) se recalculan EN CASCADA: por corte Y por lo que ya
  // esté seleccionado en los demás filtros — así si filtras Tipo operación
  // = "O&M Fijo" y dentro de ese tipo no hay ningún Jefe "Calixto Chaves",
  // ese nombre ya no aparece en el select de Jefe (antes se traían de TODA
  // la historia de la tabla sin importar los demás filtros, así que se
  // podían elegir combinaciones que no tenían ni una sola OT y todo
  // quedaba en 0).
  const MAPA_LISTA_POR_CAMPO = {
    aliado: "aliados", aliado_tecnico: "aliados_tecnicos", jefe: "jefes",
    departamento: "departamentos", municipio: "municipios", diagnostico: "diagnosticos", tipo_operacion: "tipos_operacion",
  };
  // Agrega un filtro a un URLSearchParams: si es array (selección múltiple),
  // manda una entrada repetida por cada valor elegido (?campo=A&campo=B);
  // Express/qs la recibe como arreglo. Si es string (tipo_operacion), se
  // manda como antes.
  const agregarParamFiltro = (p, campo, valor) => {
    if (Array.isArray(valor)) {
      valor.forEach((v) => { if (v) p.append(campo, v); });
    } else if (valor) {
      p.set(campo, valor);
    }
  };
  useEffect(() => {
    if (!fechaCorte) return;
    const p = new URLSearchParams();
    p.set("fecha_corte", fechaCorte);
    Object.entries(filtros).forEach(([k, v]) => agregarParamFiltro(p, k, v));
    fetch(`${API}/backlog-filtros?${p.toString()}`).then((r) => r.json()).then((data) => {
      setFiltrosDisp(data);
      // Si al recalcular en cascada algún valor que ya tenías seleccionado
      // deja de aparecer en su lista (porque ya no tiene ninguna OT con la
      // combinación actual de los demás filtros), se quita ese valor en vez
      // de dejarlo seleccionado "a ciegas" — así nunca te quedas viendo "sin
      // registros" sin saber por qué.
      setFiltros((f) => {
        let cambio = false;
        const nuevo = { ...f };
        for (const [campo, lista] of Object.entries(MAPA_LISTA_POR_CAMPO)) {
          if (campo === "tipo_operacion") continue;
          const valorActual = f[campo];
          if (Array.isArray(valorActual) && valorActual.length > 0 && Array.isArray(data[lista])) {
            const filtrado = valorActual.filter((v) => data[lista].includes(v));
            if (filtrado.length !== valorActual.length) {
              nuevo[campo] = filtrado;
              cambio = true;
            }
          }
        }
        return cambio ? nuevo : f;
      });
    }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [API, fechaCorte, filtros]);

  const queryFiltros = useCallback(() => {
    const p = new URLSearchParams();
    p.set("rol", usuario?.rol || "");
    p.set("cedula", usuario?.cedula || "");
    if (fechaCorte) p.set("fecha_corte", fechaCorte);
    Object.entries(filtros).forEach(([k, v]) => agregarParamFiltro(p, k, v));
    return p.toString();
  }, [usuario, fechaCorte, filtros]);

  const cargarResumen = useCallback(() => {
    setCargando(true); setError("");
    fetch(`${API}/backlog-resumen-gerencial?${queryFiltros()}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setResumen(data); else setError(data.mensaje || "Error cargando resumen."); })
      .catch(() => setError("No se pudo cargar el resumen gerencial."))
      .finally(() => setCargando(false));
  }, [API, queryFiltros]);

  const cargarHistorico = useCallback(() => {
    setCargando(true); setError("");
    // Mismos filtros que el resto de la pestaña (aliado técnico, jefe,
    // departamento, tipo operación) — antes solo se mandaba aliado_tecnico,
    // así que con cualquier otro filtro aplicado esta gráfica/tabla no
    // coincidía con los KPIs de arriba (ni con la pestaña Avances).
    const p = new URLSearchParams();
    p.set("rol", usuario?.rol || "");
    Object.entries(filtros).forEach(([k, v]) => agregarParamFiltro(p, k, v));
    fetch(`${API}/backlog-historico?${p.toString()}`)
      .then((r) => r.json())
      .then((data) => setHistorico(Array.isArray(data) ? data : []))
      .catch(() => setError("No se pudo cargar el histórico."))
      .finally(() => setCargando(false));
  }, [API, usuario, filtros]);

  const cargarComparacion = useCallback(() => {
    setCargando(true); setError("");
    fetch(`${API}/backlog-comparacion-corte?${queryFiltros()}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setComparacion(data); else setError(data.mensaje || "Error cargando la comparación de corte."); })
      .catch(() => setError("No se pudo cargar la comparación de corte."))
      .finally(() => setCargando(false));
  }, [API, queryFiltros]);

  // Historial de OT marcadas para revisión — vive en su propia pestaña
  // ("OT en Revisión"), con sus propios filtros (filtrosRevision), sin
  // depender del corte ni de los filtros generales de las demás pestañas:
  // es un registro permanente que cruza cortes.
  const cargarRevisionHistorial = useCallback(() => {
    const p = new URLSearchParams();
    p.set("rol", usuario?.rol || "");
    p.set("cedula", usuario?.cedula || "");
    Object.entries(filtrosRevision).forEach(([k, v]) => { if (v) p.set(k, v); });
    fetch(`${API}/backlog-revision-historial?${p.toString()}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setRevisionHistorial(data.filas || []); })
      .catch(() => {});
  }, [API, usuario, filtrosRevision]);

  const cargarProyeccion = useCallback(() => {
    setCargando(true); setError("");
    // Siempre proyecta a fin de mes del corte vigente (no se envía fecha_objetivo,
    // el backend usa LAST_DAY() por defecto).
    fetch(`${API}/backlog-proyeccion-cierre?${queryFiltros()}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setProyeccion(data); else setError(data.mensaje || "Error cargando la proyección de cierre."); })
      .catch(() => setError("No se pudo cargar la proyección de cierre."))
      .finally(() => setCargando(false));
  }, [API, queryFiltros]);

  const cargarRutasSinTipificar = useCallback(() => {
    // Manda el corte que se está viendo en Avances ahora mismo (fechaCorte),
    // para que la alerta de "rutas sin tipificar" siempre hable del mismo
    // corte que está en pantalla y no de otro corte distinto más reciente.
    const qs = fechaCorte ? `?fecha_corte=${encodeURIComponent(fechaCorte)}` : "";
    fetch(`${API}/backlog-rutas-sin-tipificar${qs}`)
      .then((r) => r.json())
      .then((data) => { if (data.ok) setRutasSinTipificar(data.filas || []); })
      .catch(() => {});
  }, [API, fechaCorte]);

  useEffect(() => {
    if (!fechaCorte && tab !== "historico" && tab !== "revision") return;
    if (tab === "avances") { cargarComparacion(); if (esAdminEstricto) cargarRutasSinTipificar(); }
    else if (tab === "revision") cargarRevisionHistorial();
    else if (tab === "pronostico") cargarProyeccion();
    else if (tab === "resumen") { cargarResumen(); cargarHistorico(); cargarProyeccion(); }
    else if (tab === "historico") cargarHistorico();
  }, [tab, fechaCorte, filtros, filtrosRevision, esAdminEstricto, cargarComparacion, cargarRevisionHistorial, cargarProyeccion, cargarResumen, cargarHistorico, cargarRutasSinTipificar]); // eslint-disable-line react-hooks/exhaustive-deps

  const abrirTipificar = (ruta) => {
    setTipificacionAbierta(ruta);
    setTipificacionDatos({ tipo_trabajo_op: "", tipo_familia: "" });
    setErrorTipificacion(null);
  };
  const guardarTipificacion = () => {
    if (!tipificacionDatos.tipo_trabajo_op || !tipificacionDatos.tipo_trabajo_op.trim()) {
      setErrorTipificacion("Elige el Tipo Trabajo OP."); return;
    }
    setGuardandoTipificacion(true);
    setErrorTipificacion(null);
    fetch(`${API}/backlog-tipificaciones`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        ruta_clasificacion: tipificacionAbierta.ruta_clasificacion,
        tipo_trabajo_op: tipificacionDatos.tipo_trabajo_op,
        tipo_familia: tipificacionDatos.tipo_familia,
        nombre_usuario: usuario?.nombre,
      }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) { setErrorTipificacion(data.mensaje || "No se pudo guardar."); return; }
        setTipificacionAbierta(null);
        cargarRutasSinTipificar();
        cargarComparacion();
      })
      .catch(() => setErrorTipificacion("No se pudo conectar con el servidor."))
      .finally(() => setGuardandoTipificacion(false));
  };

  // Referencias a los inputs de Categoría/Sub-estado/Observaciones de la fila
  // que esté en edición — solo se edita una fila a la vez, así que basta un
  // único ref por campo. Sirven para el botón "limpiar" de cada campo: como
  // los inputs son no controlados (defaultValue, para no perder el cursor en
  // cada tecleo), hay que tocar el DOM directamente para vaciarlos.
  const catInputRef = useRef(null);
  const subInputRef = useRef(null);
  const obsInputRef = useRef(null);

  // Guarda automáticamente — sin botón "✔" de confirmación. Se dispara al
  // salir de cada campo (onBlur) mientras se edita Categoría/Sub-estado/
  // Observaciones, así que la fila NO se cierra sola (el usuario puede seguir
  // pasando de un campo a otro); se cierra solo al hacer clic en "✕" o al
  // abrir la edición de otra OT.
  //
  // Guarda Categoría/Sub-estado/Observaciones con valores explícitos (no lee
  // el estado `editando` por closure, para evitar guardar un valor viejo
  // justo después de escribir/pegar y salir del campo con Tab o clic).
  const guardarValidacion = (ot_lls, valores) => {
    setGuardando(true);
    // Fija esta OT (ver comentario junto a `otsFijadas` más arriba) para que,
    // si el valor nuevo ya no coincide con el filtro activo, la fila no
    // desaparezca — ni mientras se sigue editando ni después de guardada.
    setOtsFijadas((prev) => (prev.has(ot_lls) ? prev : new Set(prev).add(ot_lls)));
    // Guarda los valores ANTERIORES de esta fila (antes del cambio optimista
    // de abajo) por si el guardado en el servidor falla y hay que revertir.
    const filaAnterior = comparacion.filas.find((f) => f.ot_lls === ot_lls);
    const valoresAnteriores = filaAnterior ? {
      categoria_seguimiento: filaAnterior.categoria_seguimiento,
      sub_estado_seguimiento: filaAnterior.sub_estado_seguimiento,
      observaciones_seguimiento: filaAnterior.observaciones_seguimiento,
    } : null;
    // Actualiza SOLO esa fila en el estado local, en vez de volver a pedirle
    // al servidor la tabla completa (cargarComparacion). El orden de la tabla
    // depende de `tipo_cambio` ('nuevo'/'actualizado'/'sin_cambio'), y guardar
    // una Categoría/Sub-estado hoy puede hacer que esa OT pase a contar como
    // "actualizado" hoy — si recargáramos todo, la fila se recalculaba y
    // saltaba de posición en plena edición, dando la sensación de que
    // entraban y salían registros de la página mientras se documentaba.
    // Tocando solo esta fila, el resto de la tabla (y su orden) no se mueve.
    setComparacion((prev) => ({
      ...prev,
      filas: prev.filas.map((f) =>
        f.ot_lls === ot_lls
          ? {
              ...f,
              categoria_seguimiento: valores.categoria || "",
              sub_estado_seguimiento: valores.sub_estado || "",
              observaciones_seguimiento: valores.observaciones || "",
            }
          : f
      ),
    }));
    const body = {
      categoria_seguimiento: valores.categoria || "",
      sub_estado_seguimiento: valores.sub_estado || "",
      observaciones_seguimiento: valores.observaciones || "",
      rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre,
      // Corte que se está viendo ahora mismo: así el servidor guarda esta
      // categorización ligada a ESE corte (y no a otro), para que cada corte
      // tenga su propia "celda" independiente — si no hay uno seleccionado
      // (fechaCorte vacío = "el vigente"), el servidor usa el corte vigente.
      fecha_corte: fechaCorte || null,
    };
    // Si el guardado FALLA en el servidor (p. ej. un navegador con una
    // versión vieja de esta página que todavía deja escribir texto libre, y
    // el servidor lo rechaza por no estar en el catálogo), el cambio
    // optimista de arriba se revierte — si no, la pantalla de quien editó
    // seguía mostrando el valor "guardado" aunque en realidad nunca llegó a
    // la base de datos, y por eso otras sesiones (u otro usuario) no lo
    // veían. También se avisa con una alerta imposible de pasar por alto,
    // porque el aviso rojo de arriba de la página es fácil de no notar
    // cuando se está trabajando varias filas más abajo en la tabla.
    fetch(`${API}/backlog/${encodeURIComponent(ot_lls)}/aseguramiento`, {
      method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) {
          if (valoresAnteriores) {
            setComparacion((prev) => ({
              ...prev,
              filas: prev.filas.map((f) => (f.ot_lls === ot_lls ? { ...f, ...valoresAnteriores } : f)),
            }));
          }
          const msg = data.mensaje || "No se pudo guardar.";
          setError(msg);
          window.alert(`⚠️ No se guardó el cambio en la OT ${ot_lls}:\n\n${msg}\n\nEl valor que escribiste NO quedó en la base de datos — vuelve a intentarlo. Si el problema sigue, haz un refresco completo de la página (Ctrl+Shift+R) para asegurarte de tener la versión más reciente.`);
        }
      })
      .catch(() => {
        if (valoresAnteriores) {
          setComparacion((prev) => ({
            ...prev,
            filas: prev.filas.map((f) => (f.ot_lls === ot_lls ? { ...f, ...valoresAnteriores } : f)),
          }));
        }
        setError("No se pudo guardar el cambio.");
        window.alert(`⚠️ No se pudo guardar el cambio en la OT ${ot_lls} (sin conexión con el servidor). El valor NO quedó guardado — vuelve a intentarlo.`);
      })
      .finally(() => setGuardando(false));
  };

  // Deja un campo en blanco (Categoría/Sub-estado/Observaciones) con un solo
  // clic — el <input list> por sí solo no ofrece una opción "vacía" en su
  // desplegable, así que este botón vacía el campo a mano y guarda de una vez,
  // igual que si hubieras borrado el texto y salido del campo.
  const limpiarCampo = (f, campo) => {
    const actualizado = { ...editando, [campo]: "" };
    if (campo === "categoria") {
      actualizado.sub_estado = ""; // sin categoría no aplica sub-estado
      if (catInputRef.current) catInputRef.current.value = "";
      if (subInputRef.current) subInputRef.current.value = "";
    } else if (campo === "sub_estado" && subInputRef.current) {
      subInputRef.current.value = "";
    } else if (campo === "observaciones" && obsInputRef.current) {
      obsInputRef.current.value = "";
    }
    setEditando(actualizado);
    guardarValidacion(f.ot_lls, actualizado);
  };

  // ── Interruptor "marcar para revisión" (pestaña Avances) ──
  // Al PRENDERLO: primero se pide confirmación ("¿Seguro que quieres
  // marcarla?"). Si confirma, el servidor copia/pega una foto completa de
  // ese registro (Categoría/Sub-estado actuales incluidos) en la lista de
  // seguimiento — esa copia queda para siempre, aunque luego se corrija la
  // categoría oficial. Si esa OT ya tenía una copia anterior en esa lista,
  // el servidor lo avisa (con la categoría/sub-estado de esa copia previa)
  // y se pregunta de nuevo si de todas formas se quiere agregar otra.
  // Al APAGARLO (a mano, o solo cuando se guarda el cambio real de
  // Categoría/Sub-estado de esa OT) no se toca nada de lo ya copiado — solo
  // reinicia el interruptor para que el ciclo pueda volver a empezar.
  const enviarMarcarRevision = async (ot_lls, marcado, confirmarDuplicado) => {
    setMarcandoOt(ot_lls); setError("");
    try {
      const res = await fetch(`${API}/backlog/${encodeURIComponent(ot_lls)}/marcar-revision`, {
        method: "PUT", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          marcado, confirmar_duplicado: confirmarDuplicado,
          rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre,
          fecha_corte: fechaCorte || null,
        }),
      });
      const data = await res.json();
      if (!data.ok) { setError(data.mensaje || "No se pudo actualizar la marca de revisión."); return; }
      if (data.duplicado) {
        const fecha = data.fecha_marcado_previa ? formatoFechaGuion(data.fecha_marcado_previa) : "—";
        const confirmarDeTodasFormas = window.confirm(
          `La OT ${ot_lls} ya aparece antes en la lista de seguimiento.\n\n` +
          `Categoría con la que quedó: ${data.categoria_previa || "Sin categorizar"}\n` +
          `Sub-estado: ${data.sub_estado_previa || "—"}\n` +
          `Marcada el: ${fecha}\n\n` +
          `¿Quieres agregarla de nuevo de todas formas?`
        );
        if (confirmarDeTodasFormas) await enviarMarcarRevision(ot_lls, true, true);
        return;
      }
      // Igual que en guardarValidacion: se actualiza solo esta fila de
      // Avances en el estado local (el interruptor y quién la marcó), sin
      // recargar toda la tabla, para no reordenarla en plena edición. La
      // lista de "OT en Revisión" sí es otra pestaña/otra tabla, así que esa
      // sí hay que recargarla para que aparezca la fila recién copiada.
      setComparacion((prev) => ({
        ...prev,
        filas: prev.filas.map((f) =>
          f.ot_lls === ot_lls
            ? { ...f, marcado_revision: marcado ? 1 : 0, marcado_revision_por: marcado ? (usuario?.nombre || null) : null }
            : f
        ),
      }));
      cargarRevisionHistorial();
    } catch {
      setError("No se pudo actualizar la marca de revisión.");
    } finally {
      setMarcandoOt(null);
    }
  };

  const marcarRevision = (f, marcado) => {
    if (!puedeMarcarRevision) return;
    if (!marcado) { enviarMarcarRevision(f.ot_lls, false, false); return; }
    if (!window.confirm(`¿Seguro que quieres marcar la OT ${f.ot_lls} para revisión?\n\nSe copiará este registro (con su Categoría y Sub-estado actuales) a la lista de seguimiento.`)) return;
    enviarMarcarRevision(f.ot_lls, true, false);
  };

  // ── Seguimiento propio de cada fila del historial (pestaña "OT en
  // Revisión"): Fecha envío transversal / Estado / Notas u observaciones.
  // A diferencia del resto de la fila (que es una foto fija de cuando se
  // marcó), estos 3 campos sí se pueden seguir editando después — se
  // guardan solos al salir del campo (igual que Categoría/Sub-estado en
  // Avances), sin necesidad de un botón "Guardar".
  const guardarSeguimientoRevision = (id, campo, valor) => {
    if (!puedeMarcarRevision) return;
    setRevisionHistorial((prev) => prev.map((x) => (x.id === id ? { ...x, [campo]: valor } : x)));
    fetch(`${API}/backlog-revision-historial/${id}`, {
      method: "PUT", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [campo]: valor, rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre }),
    })
      .then((r) => r.json())
      .then((data) => { if (!data.ok) setError(data.mensaje || "No se pudo guardar el seguimiento."); })
      .catch(() => setError("No se pudo guardar el seguimiento."));
  };

  const eliminarRevisionHistorial = (id) => {
    if (!esAdminEstricto) return;
    if (!window.confirm("¿Seguro que quieres eliminar este registro de la lista de revisión? Esta acción no se puede deshacer.")) return;
    fetch(`${API}/backlog-revision-historial/${id}`, {
      method: "DELETE", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rol: usuario?.rol, cedula: usuario?.cedula, nombre_usuario: usuario?.nombre }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data.ok) { setError(data.mensaje || "No se pudo eliminar el registro."); return; }
        setRevisionHistorial((prev) => prev.filter((x) => x.id !== id));
      })
      .catch(() => setError("No se pudo eliminar el registro."));
  };

  // Descarga en CSV (se abre directo en Excel) la info que se ve ahora mismo
  // en la tabla de "OT en Revisión" — respeta los filtros propios de esta
  // pestaña, porque descarga exactamente lo que hay en revisionHistorial.
  const descargarRevisionCSV = () => {
    const encabezados = [
      "OT", "Sitio", "Municipio", "Aliado técnico", "Corte", "Antigüedad (días)",
      "Categoría", "Sub-estado", "Observaciones", "Marcada por", "Fecha de marcado",
      "Fecha envío transversal", "Estado", "Notas u observaciones",
    ];
    const escapar = (v) => {
      const s = v === null || v === undefined ? "" : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const filas = revisionHistorial.map((r) => [
      r.ot_lls,
      r.sitio,
      r.municipio,
      r.aliado_tecnico,
      r.fecha_corte ? formatoFechaGuion(r.fecha_corte) : "",
      r.antiguedad_dias ?? "",
      r.categoria_seguimiento || "",
      r.sub_estado_seguimiento || "",
      r.observaciones_seguimiento || "",
      r.marcado_por || "",
      r.fecha_marcado ? formatoFechaGuion(r.fecha_marcado) : "",
      r.fecha_envio_transversal ? String(r.fecha_envio_transversal).slice(0, 10) : "",
      r.estado_seguimiento || "",
      r.notas_seguimiento || "",
    ]);
    const contenido = [encabezados, ...filas].map((fila) => fila.map(escapar).join(";")).join("\r\n");
    const blob = new Blob(["﻿" + contenido], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `ot_en_revision_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // ── KPIs derivados para el header (según pestaña activa) ──
  const kpis = resumen?.kpis || {};
  const pctCumple = useMemo(() => {
    const t = Number(kpis.total_backlog) || 0;
    return t > 0 ? ((Number(kpis.cumplen_sla) || 0) / t * 100).toFixed(1) : "0.0";
  }, [kpis]);

  const porAntiguedadAgrupado = useMemo(() => {
    if (!resumen?.por_antiguedad) return [];
    const mapa = {};
    resumen.por_antiguedad.forEach((r) => {
      const key = r.rangos || "(sin rango)";
      if (!mapa[key]) mapa[key] = { label: key, total: 0 };
      mapa[key].total += r.total;
    });
    return Object.values(mapa).sort((a, b) => a.label.localeCompare(b.label));
  }, [resumen]);

  return (
    <div className="backlog-r3-root" style={{ fontFamily: FONT_FAMILY, WebkitFontSmoothing: "antialiased" }}>
      {/* ── Encabezado ── */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: "20px", flexWrap: "wrap", gap: "12px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{
            width: "38px", height: "38px", borderRadius: "11px", flexShrink: 0,
            background: `linear-gradient(155deg, ${CLARO_RED}, ${CLARO_RED_DEEP})`,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `0 4px 10px -4px ${CLARO_RED}8c`,
          }}>
            <Ic name="grid" size={20} color="white" style={{ strokeWidth: 2 }} />
          </div>
          <div>
            <h2 style={{ margin: 0, fontSize: "21px", color: CLARO_DARK, fontWeight: "800", letterSpacing: "-0.01em" }}>Backlog OM Fijo — Seguimiento R3</h2>
            <p style={{ margin: "3px 0 0 0", fontSize: "13px", color: CLARO_MUTED }}>Avances, histórico y resumen gerencial según causas y observaciones de los contratistas.</p>
          </div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", gap: "10px" }}>
          {fechaCorte && (
            <a
              href={`${API}/backlog-exportar?${queryFiltros()}`}
              style={{
                padding: "9px 14px", borderRadius: "8px", border: `1px solid ${BORDER}`, background: "white",
                color: CLARO_DARK, fontSize: "12.5px", fontWeight: "700", cursor: "pointer", textDecoration: "none",
                display: "inline-flex", alignItems: "center", gap: "6px",
              }}
              title="Descarga en Excel el corte actual: las 103 columnas originales + Rango de antigüedad, Categoría, Sub-categoría y Observaciones"
            >
              <Ic name="download" size={14} />Descargar backlog
            </a>
          )}
          {esAdminEstricto && (
            <button onClick={() => { setModalCategoriaAbierto(true); setMensajeCategoria(null); }} style={{
              padding: "9px 14px", borderRadius: "8px", border: `1px solid ${BORDER}`, background: "white",
              color: CLARO_RED, fontSize: "12.5px", fontWeight: "700", cursor: "pointer",
            }}>+ Nueva categoría / sub-categoría</button>
          )}
          {esAdminEstricto && (
            <button onClick={abrirGestionTipif} style={{
              padding: "9px 14px", borderRadius: "8px", border: `1px solid ${BORDER}`, background: "white",
              color: CLARO_RED, fontSize: "12.5px", fontWeight: "700", cursor: "pointer",
            }}>Gestionar tipificaciones</button>
          )}
          {cortes.length > 0 && (
            <div>
              <label style={{ display: "block", fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Corte</label>
              <select value={fechaCorte} onChange={(e) => setFechaCorte(e.target.value)}
                style={{ padding: "8px 12px", borderRadius: "8px", border: `1px solid ${BORDER}`, fontSize: "13px", color: CLARO_DARK }}>
                {cortes.map((c) => <option key={c} value={c}>{formatoFechaGuion(c)}</option>)}
              </select>
            </div>
          )}
        </div>
      </div>

      {/* ── Filtros ── */}
      <div style={{ ...card, marginBottom: "16px", display: "flex", gap: "12px", flexWrap: "wrap" }}>
        <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
          <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "5px" }}>Buscar OT</label>
          <div style={{ position: "relative" }}>
            <Ic name="search" size={15} color={CLARO_MUTED2} style={{ position: "absolute", left: "9px", top: "50%", transform: "translateY(-50%)" }} />
            <input
              type="text"
              value={busquedaOt}
              onChange={(e) => setBusquedaOt(e.target.value)}
              placeholder="Ej. WO0000005878415"
              style={{ width: "100%", height: "34px", boxSizing: "border-box", padding: "8px 10px 8px 30px", borderRadius: "9px", border: `1px solid ${BORDER}`, fontSize: "12.5px", fontFamily: "inherit", color: CLARO_DARK }}
            />
          </div>
        </div>
        <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
          <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "5px" }}>Buscar Nodo (ID sitio)</label>
          <div style={{ position: "relative" }}>
            <Ic name="search" size={15} color={CLARO_MUTED2} style={{ position: "absolute", left: "9px", top: "50%", transform: "translateY(-50%)" }} />
            <input
              type="text"
              value={busquedaNodo}
              onChange={(e) => setBusquedaNodo(e.target.value)}
              placeholder="Ej. nombre o ID del sitio"
              style={{ width: "100%", height: "34px", boxSizing: "border-box", padding: "8px 10px 8px 30px", borderRadius: "9px", border: `1px solid ${BORDER}`, fontSize: "12.5px", fontFamily: "inherit", color: CLARO_DARK }}
            />
          </div>
        </div>
        {/* Aliado técnico, Jefe de zona, Departamento y Municipio: selección
            múltiple (igual patrón que Ofensores N2 / Conciliaciones) — se
            puede elegir más de un valor con Ctrl/Cmd+clic o arrastrando. */}
        {[
          ["aliado_tecnico", "Aliado técnico", "aliados_tecnicos"],
          ["jefe", "Jefe de zona", "jefes"],
          ["departamento", "Departamento", "departamentos"],
          ["municipio", "Municipio", "municipios"],
        ].map(([campo, label, listaKey]) => {
          const opciones = filtrosDisp[listaKey] || [];
          const valorActual = filtros[campo];
          return (
            <div key={campo} style={{ flex: "1 1 150px", minWidth: "150px" }}>
              <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>{label}</label>
              <select
                multiple
                size={Math.min(Math.max(opciones.length, 1), 5)}
                value={valorActual}
                onChange={(e) => {
                  const seleccion = Array.from(e.target.selectedOptions).map((o) => o.value);
                  setFiltros((f) => ({ ...f, [campo]: seleccion }));
                }}
                style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: "8px", border: `1px solid ${valorActual.length > 0 ? CLARO_RED : BORDER}`, fontSize: "12.5px", color: CLARO_DARK, cursor: "pointer" }}
              >
                {opciones.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
              {valorActual.length > 0 && (
                <button
                  onClick={() => setFiltros((f) => ({ ...f, [campo]: [] }))}
                  style={{ fontSize: "11px", color: CLARO_RED, background: "none", border: "none", cursor: "pointer", textAlign: "left", padding: "4px 0 0 0" }}
                >
                  ✕ Limpiar
                </button>
              )}
            </div>
          );
        })}
        {/* Tipo operación: se deja selección única (este módulo es solo
            O&M Fijo en la práctica y el valor se comparte con el clic en la
            tabla dinámica de SLA de más abajo). */}
        <div style={{ flex: "1 1 150px", minWidth: "150px" }}>
          <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Tipo operación</label>
          <select
            value={filtros.tipo_operacion}
            onChange={(e) => setFiltros((f) => ({ ...f, tipo_operacion: e.target.value }))}
            style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: "8px", border: `1px solid ${BORDER}`, fontSize: "12.5px", color: CLARO_DARK }}
          >
            <option value="">Todos</option>
            {(filtrosDisp.tipos_operacion || []).map((v) => <option key={v} value={v}>{v}</option>)}
          </select>
        </div>
        <div style={{ flex: "0 0 auto", display: "flex", alignItems: "flex-end" }}>
          <button
            onClick={() => {
              setBusquedaOt("");
              setBusquedaNodo("");
              setFiltros({ aliado: [], aliado_tecnico: [], jefe: [], departamento: [], municipio: [], diagnostico: [], tipo_operacion: "O&M Fijo" });
              setFiltroSla(null);
              setOrigenSla(null);
              setFiltroRango(null);
              setFiltroTipoTrabajo(null);
              setFiltroTipoFamilia(null);
              setFiltroCategoria(null);
              setFiltroSubcategoria(null);
              setFiltroJefeResumen(null);
              setFiltroMunicipioResumen(null);
              setFiltroEstadoProyeccion(null);
              setFiltroTipoCambio(null);
              setFiltroValidadas(null);
            }}
            disabled={!hayFiltrosActivos}
            title="Quita la búsqueda de OT/Nodo y vuelve los demás filtros a su valor por defecto"
            style={{
              height: "34px", padding: "0 14px", borderRadius: "9px", border: `1px solid ${hayFiltrosActivos ? "#f3b9b9" : BORDER}`,
              background: hayFiltrosActivos ? "#fdeceb" : "#f8fafc",
              color: hayFiltrosActivos ? CLARO_RED : "#cbd5e1",
              fontSize: "12.5px", fontWeight: "700", fontFamily: "inherit",
              cursor: hayFiltrosActivos ? "pointer" : "default",
              whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: "6px",
            }}
          >
            <Ic name="x" size={14} />
            Limpiar filtros
          </button>
        </div>
      </div>

      {error && <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: "8px", padding: "10px 14px", color: "#991b1b", fontSize: "13px", marginBottom: "14px" }}>{error}</div>}

      {/* ── Pestañas ── */}
      <div style={{ display: "flex", gap: "4px", marginBottom: "18px", borderBottom: `2px solid ${BORDER}` }}>
        {[["avances", "Avances"], ["revision", "OT en Revisión"], ["pronostico", "Pronóstico"], ["historico", "Histórico"], ["resumen", "Resumen Gerencial"]].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} style={{
            padding: "10px 18px", border: "none", background: "none", cursor: "pointer",
            fontSize: "13.5px", fontWeight: tab === id ? "700" : "500",
            color: tab === id ? CLARO_RED : CLARO_MUTED,
            borderBottom: tab === id ? `3px solid ${CLARO_RED}` : "3px solid transparent",
            marginBottom: "-2px",
          }}>{label}</button>
        ))}
      </div>

      {cargando && <div style={{ color: CLARO_MUTED, fontSize: "13px", marginBottom: "12px" }}>Cargando…</div>}

      {/* ══════════════ TAB: AVANCES (con comparación ayer → hoy integrada) ══════════════ */}
      {tab === "avances" && (
        <div>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "14px", flexWrap: "wrap", gap: "10px" }}>
            <div style={{ fontSize: "13px", color: CLARO_MUTED }}>
              Comparando <strong style={{ color: CLARO_DARK }}>{comparacion.fecha_ayer ? formatoFechaGuion(comparacion.fecha_ayer) : "— sin corte anterior —"}</strong>
              {" → "}
              <strong style={{ color: CLARO_RED }}>{comparacion.fecha_hoy ? formatoFechaGuion(comparacion.fecha_hoy) : "—"}</strong>
            </div>
            {rolPuedeEditarEnGeneral && !esAdminEstricto && !corteEsElMasReciente && cortes.length > 0 && (
              <div style={{ fontSize: "12px", color: "#b45309", background: "#fffbeb", border: "1px solid #fde68a", borderRadius: "8px", padding: "6px 12px" }}>
                Estás viendo un corte anterior: solo lectura. Solo el corte vigente ({cortes[0]}) se puede editar.
              </div>
            )}
          </div>

          {esAdminEstricto && rutasSinTipificar.length > 0 && (
            <div style={{ marginBottom: "18px" }}>
              <button onClick={() => setMostrarRutasSinTipificar((v) => !v)} style={{
                border: "1px solid #fde68a", background: "#fffbeb", color: "#b45309", borderRadius: "8px",
                padding: "8px 14px", cursor: "pointer", fontSize: "12.5px", fontWeight: "700",
              }}>
                ⚠️ {rutasSinTipificar.length} ruta(s) de clasificación sin Tipo Trabajo OP / Tipo Familia — {mostrarRutasSinTipificar ? "ocultar" : "ver y tipificar"}
              </button>
              {mostrarRutasSinTipificar && (
                <div style={{ ...card, marginTop: "10px", padding: 0, overflow: "hidden" }}>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse" }}>
                      <thead>
                        <tr><Th>Ruta de clasificación</Th><Th>OT en el corte vigente</Th><Th>Acción</Th></tr>
                      </thead>
                      <tbody>
                        {rutasSinTipificar.map((r) => (
                          <tr key={r.ruta_clasificacion}>
                            <Td style={{ fontSize: "11.5px" }}>{r.ruta_clasificacion}</Td>
                            <Td style={{ textAlign: "right" }}>{r.cantidad_ot}</Td>
                            <Td>
                              <button onClick={() => abrirTipificar(r)} style={{
                                border: "none", background: CLARO_RED, color: "white", borderRadius: "6px",
                                padding: "5px 12px", cursor: "pointer", fontSize: "11.5px", fontWeight: "700",
                              }}>Tipificar</button>
                            </Td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }}>
            <KpiCard label="Total OT en el corte" value={avancesPivotados.length} icon="bars" />
            {["nuevo", "actualizado", "sin_cambio"].map((t) => {
              const activo = filtroTipoCambio === t;
              return (
                <KpiCard
                  key={t}
                  label={TIPO_CAMBIO_INFO[t].label}
                  value={baseParaTipoCambio.filter((f) => f.tipo_cambio === t).length}
                  color={TIPO_CAMBIO_INFO[t].color}
                  icon={t === "nuevo" ? "plus" : t === "actualizado" ? "edit" : "dash"}
                  activo={activo}
                  onClick={() => setFiltroTipoCambio((v) => (v === t ? null : t))}
                />
              );
            })}
            <KpiCard label="Cumple SLA" value={baseParaSla.filter((f) => slaCumple(f.trabajo_cumple_sla)).length} color={GOOD} icon="check"
              activo={filtroSla === "cumple"} onClick={() => setFiltroSlaGenerico(filtroSla === "cumple" ? null : "cumple")} />
            <KpiCard label="No cumple SLA" value={baseParaSla.filter((f) => slaNoCumple(f.trabajo_cumple_sla)).length} color={CRITICAL} icon="alert"
              activo={filtroSla === "no_cumple"} onClick={() => setFiltroSlaGenerico(filtroSla === "no_cumple" ? null : "no_cumple")} />
            <KpiCard label="Validadas (con categoría)" value={baseParaValidadas.filter((f) => f.categoria_seguimiento).length} sub={`de ${avancesPivotados.length} casos`} icon="badgeCheck"
              activo={!!filtroValidadas} onClick={() => setFiltroValidadas((v) => (v ? null : true))} />
            <KpiCard label="Salieron del backlog" value={(comparacion.salieron || []).length}
              color={GOOD} icon="arrowRight" sub={comparacion.fecha_ayer ? `Estaban en el corte ${formatoFechaGuion(comparacion.fecha_ayer)} y ya no están en ${formatoFechaGuion(comparacion.fecha_hoy)}` : "Sin corte anterior para comparar"} />
          </div>
          <div style={{ fontSize: "11px", color: CLARO_MUTED, marginTop: "-7px", marginBottom: "14px" }}>
            Clic en una tarjeta para filtrar por ese dato; otro clic la quita. "Salieron del backlog" es una comparación aparte con el corte anterior y no se puede usar como filtro.
          </div>

          {/* ── Tablas dinámicas (estilo Excel): funcionan como filtros adicionales — afectan tarjetas y tabla ── */}
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "18px", alignItems: "flex-start" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "14px", flex: "1 1 320px", minWidth: "300px" }}>
              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="bars" color={CLARO_RED} />Resumen SLA</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "12px" }}>Clic en Cumple / No cumple para filtrar.</div>
                <TablaPivoteSla
                  resumenSla={resumenSla}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeSla}
                  filtroTipoOperacion={filtros.tipo_operacion}
                  setFiltroTipoOperacion={(valor) => setFiltros((f) => ({ ...f, tipo_operacion: valor }))}
                  colorPct={colorPct}
                  pctTexto={pctTexto}
                  pctNum={pctNum}
                  colorImpacto={colorImpacto}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>

              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="layers" color={CLARO_RED} />Resumen por Tipo Trabajo OP</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "12px" }}>Clic en ▸ despliega el Tipo Familia de ese Tipo Trabajo OP. Clic en el nombre filtra toda la fila; clic en Cumple / No cumple filtra solo ese dato.</div>
                <TablaPivoteTipoTrabajo
                  resumenTipoTrabajo={resumenTipoTrabajo}
                  filtroTipoTrabajo={filtroTipoTrabajo}
                  setFiltroTipoTrabajo={setFiltroTipoTrabajo}
                  filtroTipoFamilia={filtroTipoFamilia}
                  setFiltroTipoFamilia={setFiltroTipoFamilia}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeTipoTrabajo}
                  colorPct={colorPct}
                  pctTexto={pctTexto}
                  pctNum={pctNum}
                  colorImpacto={colorImpacto}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>

              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="folder" color={CLARO_RED} />Categoría / Subcategoría de seguimiento</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "12px" }}>El % es cumplimiento SLA; "% impacto" es qué parte del incumplimiento total aporta esa fila. Clic en el nombre filtra toda la fila; clic en el número de No cumple/Cumple filtra solo ese dato.</div>
                <TablaPivoteCategoria
                  resumenCategoria={resumenCategoria}
                  filtroCategoria={filtroCategoria}
                  setFiltroCategoria={setFiltroCategoria}
                  filtroSubcategoria={filtroSubcategoria}
                  setFiltroSubcategoria={setFiltroSubcategoria}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeCategoria}
                  colorPct={colorPct}
                  pctTexto={pctTexto}
                  pctNum={pctNum}
                  colorImpacto={colorImpacto}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", flex: "1.6 1 420px", minWidth: "360px" }}>
              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="layers" color={CLARO_RED} />Cumplimiento SLA por rango de antigüedad</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "12px" }}>Clic en el nombre del rango filtra toda la fila; clic en el número de No cumple/Cumple filtra solo ese dato.</div>
                <TablaPivoteRango
                  resumenRango={resumenRango}
                  filtroRango={filtroRango}
                  setFiltroRango={setFiltroRango}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeRango}
                  colorPct={colorPct}
                  pctTexto={pctTexto}
                  pctNum={pctNum}
                  colorImpacto={colorImpacto}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>

              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="trendUp" color={CLARO_RED} />Top causas de incumplimiento SLA</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "14px" }}>Subcategorías ordenadas de mayor a menor aporte al incumplimiento actual (análisis Pareto). Clic en una barra para filtrar esa subcategoría en estado "No cumple".</div>
                <TablaParetoCausas
                  resumenCategoria={resumenCategoria}
                  filtroCategoria={filtroCategoria}
                  setFiltroCategoria={setFiltroCategoria}
                  filtroSubcategoria={filtroSubcategoria}
                  setFiltroSubcategoria={setFiltroSubcategoria}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeCategoria}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>

              <div style={{ ...card, padding: "18px" }}>
                <h3 style={{ margin: "0 0 3px 0", fontSize: "13.5px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="layers" color={CLARO_RED} />Resumen por Jefe de Zona</h3>
                <div style={{ fontSize: "11px", color: CLARO_MUTED, marginBottom: "12px" }}>Clic en ▸ despliega los Municipios de ese Jefe. Clic en el nombre filtra toda la fila; clic en Cumple / No cumple filtra solo ese dato.</div>
                <TablaPivoteJefe
                  resumenJefe={resumenJefe}
                  filtroJefeResumen={filtroJefeResumen}
                  setFiltroJefeResumen={setFiltroJefeResumen}
                  filtroMunicipioResumen={filtroMunicipioResumen}
                  setFiltroMunicipioResumen={setFiltroMunicipioResumen}
                  filtroSla={filtroSla}
                  setFiltroSla={setFiltroSlaDesdeJefe}
                  colorPct={colorPct}
                  pctTexto={pctTexto}
                  pctNum={pctNum}
                  colorImpacto={colorImpacto}
                  pctImpactoTexto={pctImpactoTexto}
                  pctImpactoNum={pctImpactoNum}
                />
              </div>
            </div>
          </div>

          <div style={{ ...card, overflowX: "auto", boxShadow: SHADOW_SM }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <ThOrdenable campo="ot_lls" label="OT" orden={orden} onOrdenar={alternarOrden} /><Th>Sitio</Th><Th>Municipio</Th><Th>Aliado técnico</Th>
                  <Th>Tipo Trabajo OP</Th><Th>Tipo Familia</Th>
                  <ThOrdenable campo="antiguedad_dias" label="Antigüedad" orden={orden} onOrdenar={alternarOrden} /><Th>SLA</Th>
                  <Th>Categoría <span style={{ color: CLARO_RED, fontWeight: 400 }}>({comparacion.fecha_hoy ? formatoFechaGuion(comparacion.fecha_hoy) : "actual"})</span></Th>
                  <Th>Sub-estado</Th><Th>Observaciones</Th>
                  <Th>Historial</Th>
                  <Th>Revisión</Th>
                </tr>
              </thead>
              <tbody>
                {avancesPagina.map((f, idxFila) => {
                  const info = TIPO_CAMBIO_INFO[f.tipo_cambio] || TIPO_CAMBIO_INFO.sin_cambio;
                  const editandoFila = editando?.ot_lls === f.ot_lls && editando?.campo === "validacion";
                  const catColor = CATEGORIA_COLOR[f.categoria_seguimiento] || CLARO_MUTED;
                  const abrirEdicion = () => puedeEditar && setEditando({
                    ot_lls: f.ot_lls, campo: "validacion",
                    categoria: f.categoria_seguimiento || "", sub_estado: f.sub_estado_seguimiento || "",
                    observaciones: f.observaciones_seguimiento || "",
                  });
                  // Al entrar a editar Observaciones, la caja de texto arranca con el
                  // mismo alto que tenía el renglón en ese momento (en vez de volverse
                  // una cajita de una sola línea), para no tener que re-agrandarla a
                  // mano cada vez que la nota ya es larga.
                  const abrirEdicionObs = (e) => {
                    if (!puedeEditar) return;
                    const alto = e.currentTarget.offsetHeight;
                    setEditando({
                      ot_lls: f.ot_lls, campo: "validacion",
                      categoria: f.categoria_seguimiento || "", sub_estado: f.sub_estado_seguimiento || "",
                      observaciones: f.observaciones_seguimiento || "",
                      altoObs: alto,
                    });
                  };
                  return (
                    <tr key={f.ot_lls} style={{ borderLeft: `3px solid ${info.color}`, background: idxFila % 2 === 1 ? "#fafbfc" : "white" }}>
                      <Td style={{ fontWeight: "700" }}>{f.ot_lls}</Td>
                      <Td>{f.sitio}</Td>
                      <Td>{f.municipio}</Td>
                      <Td>{f.aliado_tecnico}</Td>
                      <Td>{f.tipo_trabajo_op || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin tipificar</span>}</Td>
                      <Td>{f.tipo_familia || <span style={{ color: "#cbd5e1" }}>—</span>}</Td>
                      <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{f.antiguedad_dias ?? "—"} d</Td>
                      <Td>
                        {f.trabajo_cumple_sla
                          ? <Tag text={f.trabajo_cumple_sla} color={slaCumple(f.trabajo_cumple_sla) ? GOOD : CRITICAL} />
                          : <span style={{ color: CLARO_MUTED }}>—</span>}
                      </Td>
                      <Td style={{ minWidth: "160px" }}>
                        {editandoFila ? (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <select
                              ref={catInputRef}
                              autoFocus
                              value={editando.categoria}
                              onChange={(e) => {
                                const nuevaCategoria = e.target.value;
                                const actualizado = { ...editando, categoria: nuevaCategoria, sub_estado: editando.categoria !== nuevaCategoria ? "" : editando.sub_estado };
                                setEditando(actualizado);
                                guardarValidacion(f.ot_lls, actualizado);
                              }}
                              style={{ flex: 1, minWidth: 0, padding: "5px 6px", border: `1px solid ${CLARO_RED}`, borderRadius: "6px", fontSize: "12px", background: "white", cursor: "pointer" }}
                            >
                              <option value="">Sin categorizar</option>
                              {categoriasDisponibles.map((c) => <option key={c} value={c}>{c}</option>)}
                            </select>
                            {editando.categoria && (
                              <button onClick={() => limpiarCampo(f, "categoria")} title="Dejar Categoría (y Sub-estado) en blanco"
                                style={{ border: "none", background: "#f1f5f9", color: CLARO_MUTED, borderRadius: "6px", padding: "0 7px", cursor: "pointer", fontSize: "12px" }}>✕</button>
                            )}
                          </div>
                        ) : (
                          <div onClick={abrirEdicion} style={{ cursor: puedeEditar ? "pointer" : "default" }} title={puedeEditar ? "Clic para editar" : ""}>
                            {f.categoria_seguimiento ? <Tag text={f.categoria_seguimiento} color={catColor} /> : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin categorizar</span>}
                          </div>
                        )}
                      </Td>
                      <Td style={{ minWidth: "180px" }}>
                        {editandoFila ? (
                          <div style={{ display: "flex", gap: "4px" }}>
                            <select
                              ref={subInputRef}
                              value={editando.sub_estado}
                              disabled={!editando.categoria}
                              onChange={(e) => {
                                const actualizado = { ...editando, sub_estado: e.target.value };
                                setEditando(actualizado);
                                guardarValidacion(f.ot_lls, actualizado);
                              }}
                              style={{ flex: 1, minWidth: 0, padding: "5px 6px", border: `1px solid ${CLARO_RED}`, borderRadius: "6px", fontSize: "12px", background: editando.categoria ? "white" : "#f1f5f9", cursor: editando.categoria ? "pointer" : "not-allowed" }}
                            >
                              <option value="">{editando.categoria ? "Sin sub-estado" : "Elige primero una categoría"}</option>
                              {(subEstadosPorCategoria[editando.categoria] || []).map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                            {editando.sub_estado && (
                              <button onClick={() => limpiarCampo(f, "sub_estado")} title="Dejar Sub-estado en blanco"
                                style={{ border: "none", background: "#f1f5f9", color: CLARO_MUTED, borderRadius: "6px", padding: "0 7px", cursor: "pointer", fontSize: "12px" }}>✕</button>
                            )}
                          </div>
                        ) : (
                          <div onClick={abrirEdicion} style={{ cursor: puedeEditar ? "pointer" : "default", color: f.sub_estado_seguimiento ? CLARO_DARK : "#cbd5e1", fontStyle: f.sub_estado_seguimiento ? "normal" : "italic" }} title={puedeEditar ? "Clic para editar" : ""}>
                            {f.sub_estado_seguimiento || "—"}
                          </div>
                        )}
                      </Td>
                      <Td style={{ minWidth: "220px", maxWidth: "260px" }}>
                        {editandoFila ? (
                          <div style={{ display: "flex", gap: "6px", alignItems: "flex-start" }}>
                            <textarea
                              ref={obsInputRef}
                              defaultValue={editando.observaciones}
                              placeholder="Observaciones… (puedes copiar y pegar)"
                              onFocus={(e) => {
                                // Arranca con el alto que tenía el renglón al hacer clic
                                // (y crece más si el contenido ya no cabe en ese alto).
                                e.target.style.height = Math.max(editando.altoObs || 0, e.target.scrollHeight, 34) + "px";
                              }}
                              onInput={(e) => {
                                e.target.style.height = "auto";
                                e.target.style.height = Math.max(e.target.scrollHeight, editando.altoObs || 34) + "px";
                              }}
                              onBlur={(e) => {
                                const actualizado = { ...editando, observaciones: e.target.value };
                                setEditando(actualizado);
                                guardarValidacion(f.ot_lls, actualizado);
                              }}
                              style={{ width: "100%", maxWidth: "220px", padding: "5px 8px", border: `1px solid ${CLARO_RED}`, borderRadius: "6px", fontSize: "12px", fontFamily: "inherit", lineHeight: "1.4", resize: "vertical", minHeight: (editando.altoObs || 34) + "px", overflow: "hidden", wordBreak: "break-word", overflowWrap: "anywhere" }} />
                            {editando.observaciones && (
                              <button onClick={() => limpiarCampo(f, "observaciones")} title="Dejar Observaciones en blanco"
                                style={{ border: "none", background: "#f1f5f9", color: CLARO_MUTED, borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontSize: "11px" }}>🗑</button>
                            )}
                            <button onClick={() => setEditando(null)} title="Cerrar edición (ya se guarda automáticamente)" style={{ border: "none", background: "#f1f5f9", borderRadius: "6px", padding: "4px 8px", cursor: "pointer", fontSize: "11px" }}>✕</button>
                          </div>
                        ) : (
                          <div onClick={abrirEdicionObs} style={{ cursor: puedeEditar ? "pointer" : "default", color: f.observaciones_seguimiento ? CLARO_DARK : "#cbd5e1", fontStyle: f.observaciones_seguimiento ? "normal" : "italic", whiteSpace: "pre-wrap", wordBreak: "break-word", overflowWrap: "anywhere", maxWidth: "220px" }} title={puedeEditar ? "Clic para editar" : ""}>
                            {f.observaciones_seguimiento || "Sin observaciones"}
                          </div>
                        )}
                      </Td>
                      <Td>
                        <button onClick={() => abrirHistorial(f.ot_lls)} style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", fontSize: "12px", cursor: "pointer", padding: 0 }}>
                          Ver historial →
                        </button>
                      </Td>
                      <Td style={{ textAlign: "center" }}>
                        <label
                          title={
                            !puedeMarcarRevision
                              ? "No tienes permiso para marcar OT para revisión"
                              : f.marcado_revision
                                ? `Marcada por ${f.marcado_revision_por || "—"} — clic para desmarcar`
                                : "Marcar esta OT para revisión (va a la lista de seguimiento, sin cambiar aún su Categoría/Sub-estado)"
                          }
                          style={{ display: "inline-flex", alignItems: "center", gap: "6px", cursor: puedeMarcarRevision ? "pointer" : "not-allowed", opacity: marcandoOt === f.ot_lls ? 0.6 : 1 }}
                        >
                          <input
                            type="checkbox"
                            checked={!!f.marcado_revision}
                            disabled={!puedeMarcarRevision || marcandoOt === f.ot_lls}
                            onChange={(e) => marcarRevision(f, e.target.checked)}
                            style={{ width: "16px", height: "16px", cursor: puedeMarcarRevision ? "pointer" : "not-allowed" }}
                          />
                          {!!f.marcado_revision && <Tag text="En revisión" color={CLARO_RED} />}
                        </label>
                      </Td>
                    </tr>
                  );
                })}
                {avancesOrdenados.length === 0 && !cargando && (
                  <tr><td colSpan={11} style={{ padding: "24px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>
                    {busquedaOt || busquedaNodo ? "No hay registros que coincidan con la búsqueda de OT / Nodo." : "No hay registros para este corte y estos filtros."}
                  </td></tr>
                )}
              </tbody>
            </table>
            <Paginador pagina={paginaAvances} totalPaginas={totalPagAvances} total={avancesOrdenados.length} pageSize={PAGE_SIZE} onChange={setPaginaAvances} />
          </div>

          {/* OT que estaban en el corte anterior y ya no aparecen en el corte
              actual (se cerraron/cancelaron y el reporte O&M Fijo ya no las
              trae) — distinto de "Cerrado" arriba, que es para OT que SIGUEN
              apareciendo pero con estado cerrado. */}
          {comparacion.fecha_ayer && (
            <details style={{ ...card, marginTop: "16px" }}>
              <summary style={{ cursor: "pointer", fontSize: "13px", fontWeight: "700", color: CLARO_DARK }}>
                OT que salieron del backlog ({(comparacion.salieron || []).length}) — estaban en el corte {formatoFechaGuion(comparacion.fecha_ayer)}, ya no están en {formatoFechaGuion(comparacion.fecha_hoy)}
              </summary>
              <div style={{ overflowX: "auto", marginTop: "12px" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                  <thead>
                    <tr>
                      <Th>OT</Th><Th>Sitio</Th><Th>Municipio</Th><Th>Aliado técnico</Th><Th>Antigüedad (último corte)</Th>
                      <Th>Estado O&M (último corte)</Th><Th>Categoría (último corte)</Th><Th>Sub-estado (último corte)</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {(comparacion.salieron || []).map((s) => (
                      <tr key={s.ot_lls}>
                        <Td style={{ fontWeight: "700" }}>{s.ot_lls}</Td>
                        <Td>{s.sitio}</Td>
                        <Td>{s.municipio}</Td>
                        <Td>{s.aliado_tecnico}</Td>
                        <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{s.antiguedad_dias ?? "—"} d</Td>
                        <Td>{s.estado_om || "—"}</Td>
                        <Td>{s.categoria_seguimiento || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin categorizar</span>}</Td>
                        <Td>{s.sub_estado_seguimiento || "—"}</Td>
                      </tr>
                    ))}
                    {(comparacion.salieron || []).length === 0 && (
                      <tr><td colSpan={8} style={{ padding: "16px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>Ninguna OT salió del backlog entre estos dos cortes.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </details>
          )}

        </div>
      )}

      {/* ══════════════ TAB: OT EN REVISIÓN — historial permanente de lo marcado
          con el interruptor de Avances. Una fila por cada vez que se marcó una
          OT (foto del registro en ese momento) — no se actualiza ni desaparece
          sola cuando después se corrige la Categoría/Sub-estado oficial.
          Tiene sus PROPIOS filtros (independientes del Corte y de los filtros
          generales de las demás pestañas), porque es un registro que cruza
          cortes. ══════════════ */}
      {tab === "revision" && (
        <div>
          <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
            <button
              onClick={descargarRevisionCSV}
              disabled={revisionHistorial.length === 0}
              title="Descarga en Excel (CSV) la info de esta tabla, con los filtros actuales"
              style={{
                padding: "9px 14px", borderRadius: "8px", border: `1px solid ${BORDER}`, background: "white",
                color: CLARO_DARK, fontSize: "12.5px", fontWeight: "700",
                cursor: revisionHistorial.length === 0 ? "default" : "pointer",
                opacity: revisionHistorial.length === 0 ? 0.5 : 1,
                display: "inline-flex", alignItems: "center", gap: "6px",
              }}
            >
              <Ic name="download" size={14} />Descargar
            </button>
          </div>

          {/* ── Tarjetas informativas: totales y porcentajes sobre la tabla de abajo (respetan los filtros de esta pestaña) ── */}
          {(() => {
            const totalRevision = revisionHistorial.length;
            const pct = (n) => (totalRevision > 0 ? Math.round((n / totalRevision) * 100) : 0);
            const contarEstado = (estado) => revisionHistorial.filter((r) => (r.estado_seguimiento || "") === estado).length;
            const sinEstado = revisionHistorial.filter((r) => !r.estado_seguimiento).length;
            const conEnvioTransversal = revisionHistorial.filter((r) => r.fecha_envio_transversal).length;
            return (
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "16px" }}>
                <KpiCard label="Total OT en revisión" value={totalRevision} icon="bars" />
                {ESTADOS_REVISION.map((estado) => {
                  const n = contarEstado(estado);
                  return (
                    <KpiCard
                      key={estado}
                      label={estado}
                      value={n}
                      sub={`${pct(n)}% del total`}
                      color={ESTADO_REVISION_COLOR[estado] || CLARO_MUTED}
                      icon={ESTADO_REVISION_ICONO[estado] || "dash"}
                      activo={filtrosRevision.estado_seguimiento === estado}
                      onClick={() => setFiltrosRevision((f) => ({ ...f, estado_seguimiento: f.estado_seguimiento === estado ? "" : estado }))}
                    />
                  );
                })}
                <KpiCard
                  label="Sin estado definido"
                  value={sinEstado}
                  sub={`${pct(sinEstado)}% del total`}
                  color={CLARO_MUTED}
                  icon="alert"
                />
                <KpiCard
                  label="Con fecha de envío transversal"
                  value={conEnvioTransversal}
                  sub={`${pct(conEnvioTransversal)}% del total`}
                  color={GOOD}
                  icon="check"
                />
              </div>
            );
          })()}

          <div style={{ ...card, marginBottom: "16px", display: "flex", gap: "14px", flexWrap: "wrap", alignItems: "flex-end" }}>
            {[
              ["aliado_tecnico", "Aliado técnico"], ["jefe", "Jefe de zona"], ["diagnostico", "Diagnóstico"],
            ].map(([campo, label]) => (
              <div key={campo} style={{ flex: "1 1 180px", minWidth: "180px" }}>
                <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>{label}</label>
                <select
                  value={filtrosRevision[campo]}
                  onChange={(e) => setFiltrosRevision((f) => ({ ...f, [campo]: e.target.value }))}
                  style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: "8px", border: `1px solid ${BORDER}`, fontSize: "12.5px", color: CLARO_DARK }}
                >
                  <option value="">Todos</option>
                  {(filtrosDisp[campo === "aliado_tecnico" ? "aliados_tecnicos" : campo === "diagnostico" ? "diagnosticos" : "jefes"] || [])
                    .map((v) => <option key={v} value={v}>{v}</option>)}
                </select>
              </div>
            ))}
            <div style={{ flex: "1 1 180px", minWidth: "180px" }}>
              <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Estado</label>
              <select
                value={filtrosRevision.estado_seguimiento}
                onChange={(e) => setFiltrosRevision((f) => ({ ...f, estado_seguimiento: e.target.value }))}
                style={{ width: "100%", boxSizing: "border-box", padding: "7px 10px", borderRadius: "8px", border: `1px solid ${BORDER}`, fontSize: "12.5px", color: CLARO_DARK }}
              >
                <option value="">Todos</option>
                {ESTADOS_REVISION.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            </div>
            <div style={{ flex: "0 0 auto" }}>
              <button
                onClick={() => setFiltrosRevision({ aliado_tecnico: "", jefe: "", diagnostico: "", estado_seguimiento: "" })}
                disabled={!hayFiltrosRevisionActivos}
                title="Vuelve estos filtros (solo de esta pestaña) a su valor por defecto"
                style={{
                  height: "34px", padding: "0 14px", borderRadius: "9px", border: `1px solid ${hayFiltrosRevisionActivos ? "#f3b9b9" : BORDER}`,
                  background: hayFiltrosRevisionActivos ? "#fdeceb" : "#f8fafc",
                  color: hayFiltrosRevisionActivos ? CLARO_RED : "#cbd5e1",
                  fontSize: "12.5px", fontWeight: "700", fontFamily: "inherit",
                  cursor: hayFiltrosRevisionActivos ? "pointer" : "default", whiteSpace: "nowrap",
                }}
              >
                Limpiar filtros
              </button>
            </div>
          </div>

          <div style={{ ...card, overflowX: "auto", boxShadow: SHADOW_SM }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>OT</Th><Th>Sitio</Th><Th>Municipio</Th><Th>Aliado técnico</Th><Th>Corte</Th><Th>Antigüedad</Th>
                  <Th>Categoría</Th><Th>Sub-estado</Th><Th>Observaciones</Th>
                  <Th>¿Sigue en el backlog?</Th>
                  <Th>Marcada por</Th><Th>Fecha de marcado</Th>
                  <Th>Fecha envío transversal</Th><Th>Estado</Th><Th>Notas u observaciones</Th>
                  {esAdminEstricto && <Th>Eliminar</Th>}
                </tr>
              </thead>
              <tbody>
                {revisionHistorial.map((r) => (
                  <tr key={r.id}>
                    <Td style={{ fontWeight: "700" }}>{r.ot_lls}</Td>
                    <Td>{r.sitio}</Td>
                    <Td>{r.municipio}</Td>
                    <Td>{r.aliado_tecnico}</Td>
                    <Td>{r.fecha_corte ? formatoFechaGuion(r.fecha_corte) : "—"}</Td>
                    <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{r.antiguedad_dias ?? "—"} d</Td>
                    <Td>{r.categoria_seguimiento || <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin categorizar</span>}</Td>
                    <Td>{r.sub_estado_seguimiento || "—"}</Td>
                    <Td style={{ minWidth: "220px", whiteSpace: "pre-wrap" }}>
                      {r.observaciones_seguimiento || "Sin observaciones"}
                    </Td>
                    <Td style={{ minWidth: "190px" }}>
                      {(() => {
                        // Sigue activa: su último corte en backlog_om_fijo ES el corte
                        // vigente del sistema. Si no hay NADA de historial en
                        // backlog_om_fijo para esta OT (caso raro, dato muy viejo),
                        // no se puede saber — se muestra "Sin datos".
                        if (!r.ultimo_corte_backlog) {
                          return <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin datos de backlog</span>;
                        }
                        const sigueActiva = r.corte_vigente_backlog && r.ultimo_corte_backlog === r.corte_vigente_backlog;
                        if (sigueActiva) {
                          return <Tag text="Sigue activa" color={CLARO_MUTED} />;
                        }
                        return (
                          <div>
                            <Tag text="Ya no aparece" color={GOOD} />
                            <div style={{ fontSize: "11.5px", color: CLARO_DARK, marginTop: "4px" }}>
                              Desde el corte {r.corte_desde_que_no_sale ? formatoFechaGuion(r.corte_desde_que_no_sale) : "—"}
                            </div>
                            {r.categoria_ultima_aparicion && (
                              <div style={{ fontSize: "11px", color: CLARO_MUTED, marginTop: "2px" }}>
                                Última categoría: {r.categoria_ultima_aparicion}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </Td>
                    <Td>{r.marcado_por || "—"}</Td>
                    <Td>{r.fecha_marcado ? formatoFechaGuion(r.fecha_marcado) : "—"}</Td>
                    <Td style={{ minWidth: "140px" }}>
                      <input
                        type="date"
                        defaultValue={r.fecha_envio_transversal ? String(r.fecha_envio_transversal).slice(0, 10) : ""}
                        disabled={!puedeMarcarRevision}
                        onBlur={(e) => guardarSeguimientoRevision(r.id, "fecha_envio_transversal", e.target.value || null)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px", borderRadius: "6px", border: `1px solid ${BORDER}`, fontSize: "12px", fontFamily: "inherit" }}
                      />
                    </Td>
                    <Td style={{ minWidth: "150px" }}>
                      <select
                        value={r.estado_seguimiento || ""}
                        disabled={!puedeMarcarRevision}
                        onChange={(e) => guardarSeguimientoRevision(r.id, "estado_seguimiento", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px", borderRadius: "6px", border: `1px solid ${BORDER}`, fontSize: "12px", fontFamily: "inherit" }}
                      >
                        <option value="">Sin definir</option>
                        {ESTADOS_REVISION.map((v) => <option key={v} value={v}>{v}</option>)}
                      </select>
                    </Td>
                    <Td style={{ minWidth: "200px" }}>
                      <input
                        type="text"
                        defaultValue={r.notas_seguimiento || ""}
                        placeholder="Notas u observaciones…"
                        disabled={!puedeMarcarRevision}
                        onBlur={(e) => guardarSeguimientoRevision(r.id, "notas_seguimiento", e.target.value)}
                        style={{ width: "100%", boxSizing: "border-box", padding: "5px 6px", borderRadius: "6px", border: `1px solid ${BORDER}`, fontSize: "12px", fontFamily: "inherit" }}
                      />
                    </Td>
                    {esAdminEstricto && (
                      <Td>
                        <button
                          onClick={() => eliminarRevisionHistorial(r.id)}
                          style={{ background: "#fef2f2", color: "#b91c1c", border: "1px solid #fca5a5", borderRadius: "6px", padding: "5px 10px", fontSize: "12px", fontWeight: "600", cursor: "pointer", whiteSpace: "nowrap" }}
                        >
                          🗑️ Eliminar
                        </button>
                      </Td>
                    )}
                  </tr>
                ))}
                {revisionHistorial.length === 0 && !cargando && (
                  <tr><td colSpan={esAdminEstricto ? 14 : 13} style={{ padding: "24px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>
                    Todavía no se ha marcado ninguna OT para revisión.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ TAB: PRONÓSTICO — proyección de cierre de mes ══════════════ */}
      {tab === "pronostico" && (
        <div>
          <div style={{ ...card, marginBottom: "16px" }}>
            <div style={{ fontSize: "13px", color: CLARO_MUTED }}>
              Si nadie atiende las OT antes del <strong style={{ color: CLARO_DARK }}>{formatoFechaGuion(proyeccion.fecha_objetivo)}</strong> (fin del mes del corte {formatoFechaGuion(fechaCorte)}), así cerraría el indicador de SLA —
              antigüedad proyectada = antigüedad actual + días que faltan hasta fin de mes, comparada contra la meta (<code>sla_dia</code>) de cada OT.
              Las que no cumplirían ese proyectado se subdividen por urgencia: {" "}
              <strong style={{ color: "#eda100" }}>En riesgo</strong> (3 días o menos para vencer su meta SLA hoy) y{" "}
              <strong style={{ color: "#eab308" }}>Atención</strong> (4 días o más para vencer hoy, pero igual no cumplirían a fin de mes).
            </div>
          </div>

          {(() => {
            const total = proyeccion.filas.length;
            const conMeta = proyeccion.filas.filter((f) => f.estado_proyeccion !== "sin_meta").length;
            const cumpleHoy = proyeccion.filas.filter((f) => f.estado_proyeccion === "en_riesgo" || f.estado_proyeccion === "atencion" || f.estado_proyeccion === "cumple_proyectado").length;
            const cumpleProyectado = proyeccion.filas.filter((f) => f.estado_proyeccion === "cumple_proyectado").length;
            const noCumpleProyectado = conMeta - cumpleProyectado;
            const pctHoy = conMeta > 0 ? ((cumpleHoy / conMeta) * 100).toFixed(1) : "0.0";
            const pctProyectado = conMeta > 0 ? ((cumpleProyectado / conMeta) * 100).toFixed(1) : "0.0";
            const pctNoCumpleProyectado = conMeta > 0 ? ((noCumpleProyectado / conMeta) * 100).toFixed(1) : "0.0";
            return (
              <>
              <ResumenEjecutivoPronostico
                noCumple={noCumpleProyectado}
                pctNoCumple={pctNoCumpleProyectado}
                cumpleProyectado={cumpleProyectado}
                pctProyectado={pctProyectado}
                pctHoy={pctHoy}
                fechaObjetivo={proyeccion.fecha_objetivo}
              />
              <div style={{ display: "flex", gap: "14px", flexWrap: "wrap", marginBottom: "18px" }}>
                <KpiCard label="% Cumple HOY" value={`${pctHoy}%`} color={Number(pctHoy) >= 80 ? GOOD : CRITICAL} icon="target" sub={`${cumpleHoy} de ${conMeta} con meta SLA`} />
                <KpiCard label="% Cumpliría a fin de mes" value={`${pctProyectado}%`} color={Number(pctProyectado) >= 80 ? GOOD : CRITICAL} icon="trendUp" sub={`${cumpleProyectado} de ${conMeta} con meta SLA`} />
                <KpiCard label="OT en seguimiento" value={total} icon="bars" sub="Sin cerradas ni canceladas" />
                <KpiCard
                  label="Ya incumple (hoy)"
                  value={proyeccion.filas.filter((f) => f.estado_proyeccion === "ya_incumple").length}
                  color={CRITICAL}
                  icon="ban"
                  activo={filtroEstadoProyeccion === "ya_incumple"}
                  onClick={() => setFiltroEstadoProyeccion((v) => (v === "ya_incumple" ? null : "ya_incumple"))}
                />
                <KpiCard
                  label="En riesgo"
                  value={proyeccion.filas.filter((f) => f.estado_proyeccion === "en_riesgo").length}
                  color="#eda100"
                  icon="clock"
                  sub="No cumpliría a fin de mes; 3 días o menos para vencer hoy"
                  activo={filtroEstadoProyeccion === "en_riesgo"}
                  onClick={() => setFiltroEstadoProyeccion((v) => (v === "en_riesgo" ? null : "en_riesgo"))}
                />
                <KpiCard
                  label="Atención"
                  value={proyeccion.filas.filter((f) => f.estado_proyeccion === "atencion").length}
                  color="#eab308"
                  icon="flag"
                  sub="No cumpliría a fin de mes; 4 días o más para vencer hoy"
                  activo={filtroEstadoProyeccion === "atencion"}
                  onClick={() => setFiltroEstadoProyeccion((v) => (v === "atencion" ? null : "atencion"))}
                />
                <KpiCard
                  label="Sin meta SLA"
                  value={proyeccion.filas.filter((f) => f.estado_proyeccion === "sin_meta").length}
                  icon="dash"
                  activo={filtroEstadoProyeccion === "sin_meta"}
                  onClick={() => setFiltroEstadoProyeccion((v) => (v === "sin_meta" ? null : "sin_meta"))}
                />
              </div>
              </>
            );
          })()}

          <div style={{ ...card, overflowX: "auto", boxShadow: SHADOW_SM }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <Th>OT</Th><Th>Sitio</Th><Th>Aliado técnico</Th><Th>Jefe</Th><Th>Categoría</Th><Th>Sub-categoría</Th><Th>Observaciones</Th>
                  <Th>Antigüedad actual</Th><Th>Meta SLA</Th><Th>Días para vencer</Th><Th>Fecha límite</Th><Th>Estado proyectado</Th>
                </tr>
              </thead>
              <tbody>
                {proyeccionPagina.map((f) => {
                  const info = PROYECCION_INFO[f.estado_proyeccion] || PROYECCION_INFO.sin_meta;
                  return (
                    <tr key={f.ot_lls} style={{ borderLeft: `3px solid ${info.color}` }}>
                      <Td style={{ fontWeight: "700" }}>{f.ot_lls}</Td>
                      <Td>{f.sitio}</Td>
                      <Td>{f.aliado_tecnico}</Td>
                      <Td>{f.jefe}</Td>
                      <Td>{f.categoria_seguimiento ? <Tag text={f.categoria_seguimiento} color={CATEGORIA_COLOR[f.categoria_seguimiento] || CLARO_MUTED} /> : <span style={{ color: "#cbd5e1", fontStyle: "italic" }}>Sin categorizar</span>}</Td>
                      <Td>{f.sub_estado_seguimiento || <span style={{ color: "#cbd5e1" }}>—</span>}</Td>
                      <Td style={{ maxWidth: "220px", whiteSpace: "normal" }}>{f.observaciones_seguimiento || <span style={{ color: "#cbd5e1" }}>—</span>}</Td>
                      <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{f.antiguedad_dias ?? "—"} d</Td>
                      <Td style={{ textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{f.sla_dia ?? "—"} d</Td>
                      <Td style={{ color: f.dias_para_vencer < 0 ? CRITICAL : f.dias_para_vencer === null ? CLARO_MUTED : CLARO_DARK, fontWeight: f.dias_para_vencer !== null && f.dias_para_vencer < 0 ? "700" : "400" }}>
                        {formatoDiasVencer(f.dias_para_vencer)}
                      </Td>
                      <Td>{formatoFechaGuion(f.fecha_limite)}</Td>
                      <Td><Tag text={info.label} color={info.color} /></Td>
                    </tr>
                  );
                })}
                {proyeccionFiltrada.length === 0 && !cargando && (
                  <tr><td colSpan={12} style={{ padding: "24px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>
                    {busquedaOt || busquedaNodo ? "No hay registros que coincidan con la búsqueda de OT / Nodo." : "No hay OT en seguimiento para este corte y estos filtros."}
                  </td></tr>
                )}
              </tbody>
            </table>
            <Paginador pagina={paginaProyeccion} totalPaginas={totalPagProyeccion} total={proyeccionFiltrada.length} pageSize={PAGE_SIZE} onChange={setPaginaProyeccion} />
          </div>
        </div>
      )}

      {/* ══════════════ MODAL: TIPIFICAR RUTA DE CLASIFICACIÓN ══════════════ */}
      {tipificacionAbierta && (
        <div
          onClick={() => setTipificacionAbierta(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "480px", maxWidth: "100%", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: CLARO_DARK }}>Tipificar ruta de clasificación</div>
              <button onClick={() => setTipificacionAbierta(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>
            <p style={{ margin: "0 0 16px 0", fontSize: "11.5px", color: CLARO_MUTED, lineHeight: 1.5, wordBreak: "break-word" }}>
              <strong style={{ color: CLARO_DARK }}>{tipificacionAbierta.ruta_clasificacion}</strong>
              {" — "}{tipificacionAbierta.cantidad_ot} OT en el corte vigente usan esta ruta. Esta tipificación quedará guardada para todas, en cualquier corte.
            </p>

            <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Tipo Trabajo OP</label>
            <input list="tipos-trabajo-op-existentes" value={tipificacionDatos.tipo_trabajo_op}
              onChange={(e) => setTipificacionDatos((p) => ({ ...p, tipo_trabajo_op: e.target.value }))}
              placeholder="Ej. Preventivo, Correctivo, Empresas y Negocios"
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }} />
            <datalist id="tipos-trabajo-op-existentes">
              {[...new Set(["Preventivo", "Correctivo", "Empresas y Negocios"])].map((t) => <option key={t} value={t} />)}
            </datalist>

            <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Tipo Familia (opcional)</label>
            <input value={tipificacionDatos.tipo_familia}
              onChange={(e) => setTipificacionDatos((p) => ({ ...p, tipo_familia: e.target.value }))}
              placeholder="Ej. Fotoreportes, Afectacion, MPI…"
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }} />

            {errorTipificacion && <div style={{ color: "#991b1b", fontSize: "12.5px", fontWeight: "600", marginBottom: "14px" }}>⚠️ {errorTipificacion}</div>}

            <button onClick={guardarTipificacion} disabled={guardandoTipificacion} style={{
              width: "100%", padding: "10px", border: "none", borderRadius: "8px",
              background: guardandoTipificacion ? "#9ca3af" : `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`,
              color: "white", fontSize: "13px", fontWeight: "700", cursor: guardandoTipificacion ? "default" : "pointer",
            }}>{guardandoTipificacion ? "Guardando…" : "Guardar tipificación"}</button>
          </div>
        </div>
      )}

      {/* ══════════════ MODAL: GESTIONAR TIPIFICACIONES (solo Admin) ══════════════ */}
      {modalTipifAbierto && esAdminEstricto && (
        <div onClick={() => setModalTipifAbierto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}>
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "560px", maxWidth: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: CLARO_DARK }}>Gestionar tipificaciones</div>
              <button onClick={() => setModalTipifAbierto(false)} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>
            <p style={{ margin: "0 0 14px 0", fontSize: "12.5px", color: CLARO_MUTED, lineHeight: 1.5 }}>
              Cada fila es una combinación de Tipo Trabajo OP y Tipo Familia. Al cambiarla, se actualizan todas las rutas que la usan, en todos los cortes.
            </p>
            {tipifMensaje && <div style={{ marginBottom: "12px", fontSize: "12.5px", fontWeight: "600", color: tipifMensaje.ok ? "#166534" : "#991b1b" }}>{tipifMensaje.ok ? "✅ " : "⚠️ "}{tipifMensaje.texto}</div>}
            <div style={{ border: `1px solid ${BORDER}`, borderRadius: "10px", overflow: "hidden" }}>
              {tipifGrupos.map((g) => {
                const editando = tipifEdit && tipifEdit.tipo === g.tipo && tipifEdit.familia === g.familia;
                return (
                  <div key={`${g.tipo}||${g.familia}`} style={{ padding: "10px 14px", borderBottom: `1px solid ${BORDER}`, background: editando ? "#fef2f2" : "white" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div style={{ flex: 1, fontSize: "13px", fontWeight: "700", color: CLARO_DARK }}>
                        {g.tipo}{g.familia ? ` - ${g.familia}` : ""}
                        <span style={{ fontWeight: "500", color: CLARO_MUTED }}> · {g.cantidad} ruta(s)</span>
                      </div>
                      {!editando && (
                        <button onClick={() => { setTipifMensaje(null); setTipifEdit({ tipo: g.tipo, familia: g.familia, nuevoTipo: g.tipo, nuevaFamilia: g.familia }); }}
                          style={{ border: "none", background: "none", color: CLARO_RED, fontWeight: "700", cursor: "pointer", fontSize: "12.5px" }}>Cambiar</button>
                      )}
                    </div>
                    {editando && (
                      <div style={{ marginTop: "10px", display: "flex", gap: "8px", flexWrap: "wrap", alignItems: "center" }}>
                        <input list="tipos-trabajo-op-existentes" value={tipifEdit.nuevoTipo} placeholder="Tipo Trabajo OP"
                          onChange={(e) => setTipifEdit((p) => ({ ...p, nuevoTipo: e.target.value }))}
                          style={{ flex: 1, minWidth: "140px", padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px" }} />
                        <input value={tipifEdit.nuevaFamilia} placeholder="Tipo Familia"
                          onChange={(e) => setTipifEdit((p) => ({ ...p, nuevaFamilia: e.target.value }))}
                          style={{ flex: 1, minWidth: "140px", padding: "8px 10px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px" }} />
                        <button onClick={guardarCambioTipif} disabled={tipifGuardando} style={{ border: "none", borderRadius: "8px", padding: "8px 14px", background: tipifGuardando ? "#9ca3af" : CLARO_RED, color: "white", fontWeight: "700", fontSize: "12.5px", cursor: "pointer" }}>
                          {tipifGuardando ? "Guardando…" : `Aplicar a ${g.cantidad} ruta(s)`}
                        </button>
                        <button onClick={() => setTipifEdit(null)} style={{ border: `1px solid ${BORDER}`, borderRadius: "8px", padding: "8px 12px", background: "white", color: CLARO_DARK, fontWeight: "600", fontSize: "12.5px", cursor: "pointer" }}>Cancelar</button>
                      </div>
                    )}
                  </div>
                );
              })}
              {tipifGrupos.length === 0 && <div style={{ padding: "20px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>No hay tipificaciones guardadas.</div>}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ MODAL: NUEVA CATEGORÍA / SUB-CATEGORÍA (solo Admin) ══════════════ */}
      {modalCategoriaAbierto && esAdminEstricto && (
        <div
          onClick={() => setModalCategoriaAbierto(false)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "520px", maxWidth: "100%", maxHeight: "85vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "6px" }}>
              <div style={{ fontSize: "16px", fontWeight: "800", color: CLARO_DARK }}>Nueva categoría / sub-categoría</div>
              <button onClick={() => setModalCategoriaAbierto(false)} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>
            <p style={{ margin: "0 0 18px 0", fontSize: "12.5px", color: CLARO_MUTED, lineHeight: 1.5 }}>
              Si la categoría ya existe, escríbela igual (revisa la lista de abajo) y solo se le agrega la sub-categoría nueva.
              Si es una categoría totalmente nueva, escribe ambos campos y quedará disponible de inmediato en los desplegables.
            </p>

            <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Categoría</label>
            <input list="categorias-existentes" value={nuevaCategoria} onChange={(e) => setNuevaCategoria(e.target.value)}
              placeholder="Ej. Pendiente"
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }} />
            <datalist id="categorias-existentes">
              {categoriasDisponibles.map((c) => <option key={c} value={c} />)}
            </datalist>

            <label style={{ display: "block", fontSize: "10.5px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "4px" }}>Sub-categoría</label>
            <input value={nuevaSubcategoria} onChange={(e) => setNuevaSubcategoria(e.target.value)}
              placeholder="Ej. Pendiente por respuesta de correo"
              style={{ width: "100%", boxSizing: "border-box", padding: "9px 12px", border: `1px solid ${BORDER}`, borderRadius: "8px", fontSize: "13px", marginBottom: "14px" }} />

            {mensajeCategoria && (
              <div style={{
                marginBottom: "14px", padding: "10px 14px", borderRadius: "8px", fontSize: "12.5px",
                background: mensajeCategoria.ok ? "#f0fdf4" : "#fef2f2",
                border: `1px solid ${mensajeCategoria.ok ? "#86efac" : "#fca5a5"}`,
                color: mensajeCategoria.ok ? "#15803d" : "#991b1b",
              }}>{mensajeCategoria.texto}</div>
            )}

            <button onClick={guardarNuevaCategoria} disabled={guardandoCategoria} style={{
              width: "100%", padding: "10px", border: "none", borderRadius: "8px",
              background: guardandoCategoria ? "#9ca3af" : `linear-gradient(135deg, ${CLARO_RED}, #B0000E)`,
              color: "white", fontSize: "13px", fontWeight: "700", cursor: "pointer", marginBottom: "18px",
            }}>{guardandoCategoria ? "Guardando…" : "Guardar"}</button>

            <div style={{ fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "8px" }}>Categorías y sub-categorías actuales</div>
            <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
              {categoriasDisponibles.map((cat) => (
                <div key={cat} style={{ fontSize: "12px" }}>
                  <span style={{ fontWeight: "700", color: CLARO_DARK }}>{cat}</span>
                  <div style={{ color: CLARO_MUTED, marginTop: "2px" }}>{(subEstadosPorCategoria[cat] || []).join(" · ")}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ MODAL: HISTORIAL COMPLETO DE UNA OT ══════════════ */}
      {historialAbierto && (
        <div
          onClick={() => setHistorialAbierto(null)}
          style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: "24px" }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: "white", borderRadius: "16px", padding: "28px", width: "1360px", maxWidth: "96vw", maxHeight: "88vh", overflowY: "auto", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "18px" }}>
              <div>
                <div style={{ fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", display: "flex", alignItems: "center", gap: "6px" }}><Ic name="history" size={13} />Evolución día a día — OT</div>
                <div style={{ fontSize: "20px", fontWeight: "800", color: CLARO_DARK, letterSpacing: "-0.01em" }}>{historialAbierto}</div>
              </div>
              <button onClick={() => setHistorialAbierto(null)} style={{ border: "none", background: "#f1f5f9", borderRadius: "8px", width: "28px", height: "28px", cursor: "pointer", fontSize: "14px", color: CLARO_MUTED }}>✕</button>
            </div>

            <div style={{ fontSize: "12.5px", color: CLARO_MUTED, marginBottom: "14px" }}>
              Un registro por cada corte en el que esta OT estuvo en el tablero, desde el primer corte en que apareció hasta el corte más reciente.
              {puedeEditar && evolucionDatos.length >= 2 && " El botón \"Copiar a hoy →\" trae la Categoría, Sub-estado y Observaciones del corte anterior al corte de hoy."}
            </div>
            <TablaEvolucionOt
              filas={evolucionDatos}
              cargando={evolucionCargando}
              onCopiar={copiarDesdeHistorial}
              puedeCopiar={puedeEditar}
              copiando={copiandoDesdeHistorial}
            />

            <div style={{ marginTop: "26px", paddingTop: "18px", borderTop: `1px solid ${BORDER}` }}>
              <div style={{ fontSize: "11px", fontWeight: "700", color: CLARO_MUTED, textTransform: "uppercase", marginBottom: "10px" }}>Cambios manuales registrados</div>
              <LineaTiempo eventos={historialDatos} cargando={historialCargando} />
            </div>
          </div>
        </div>
      )}

      {/* ══════════════ TAB: HISTÓRICO ══════════════ */}
      {tab === "historico" && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={card}>
            <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Evolución del backlog por corte</h3>
            <LineaHistorico
              puntos={historico}
              campos={[
                { key: "total_backlog", label: "Total backlog", color: CAT_COLORS[0] },
                { key: "cumplen_sla", label: "Cumplen SLA", color: GOOD },
                { key: "no_cumplen_sla", label: "No cumplen SLA", color: CRITICAL },
              ]}
            />
          </div>
          <div style={{ ...card, overflowX: "auto", boxShadow: SHADOW_SM }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead><tr><Th>Corte</Th><Th>Total backlog</Th><Th>Nuevas</Th><Th>Salieron</Th><Th>Cumplen SLA</Th><Th>No cumplen SLA</Th><Th>% Cumplimiento</Th><Th>Antigüedad promedio</Th></tr></thead>
              <tbody>
                {historico.map((h) => (
                  <tr key={h.fecha_corte}>
                    <Td style={{ fontWeight: "700" }}>{formatoFechaGuion(h.fecha_corte)}</Td>
                    <Td>{h.total_backlog}</Td>
                    <Td style={{ color: "#2a78d6", fontWeight: "700" }}>+{h.nuevas ?? 0}</Td>
                    <Td style={{ color: GOOD, fontWeight: "700" }}>-{h.salieron ?? 0}</Td>
                    <Td><Tag text={h.cumplen_sla} color={GOOD} /></Td>
                    <Td><Tag text={h.no_cumplen_sla} color={CRITICAL} /></Td>
                    <Td style={{ fontWeight: "700", color: Number(h.pct_cumplimiento) >= 80 ? GOOD : CRITICAL }}>{h.pct_cumplimiento ?? 0}%</Td>
                    <Td>{h.antiguedad_promedio} d</Td>
                  </tr>
                ))}
                {historico.length === 0 && !cargando && (
                  <tr><td colSpan={8} style={{ padding: "24px", textAlign: "center", color: CLARO_MUTED, fontSize: "13px" }}>Aún no hay cortes cargados.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ══════════════ TAB: RESUMEN GERENCIAL ══════════════ */}
      {tab === "resumen" && resumen && (
        <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
          <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
            <KpiCard label="Total backlog" value={kpis.total_backlog ?? 0} icon="bars" />
            <KpiCard label="% Cumplimiento SLA" value={`${pctCumple}%`} color={Number(pctCumple) >= 80 ? GOOD : CRITICAL} icon="target" />
            <KpiCard label="Antigüedad promedio" value={`${kpis.antiguedad_promedio ?? 0} d`} icon="clock" />
            <KpiCard label="No cumplen SLA" value={kpis.no_cumplen_sla ?? 0} color={CRITICAL} icon="trendDown" />
          </div>

          {/* Resumen (condensado) del Pronóstico de cierre de mes: mismo
              cálculo que la pestaña "Pronóstico", solo que aquí se muestra
              únicamente el bloque ejecutivo de 2 cifras — el detalle completo
              (las 7 tarjetas + la tabla OT por OT) sigue solo en Pronóstico. */}
          {proyeccion.filas.length > 0 && (() => {
            const conMeta = proyeccion.filas.filter((f) => f.estado_proyeccion !== "sin_meta").length;
            const cumpleHoy = proyeccion.filas.filter((f) => f.estado_proyeccion === "en_riesgo" || f.estado_proyeccion === "atencion" || f.estado_proyeccion === "cumple_proyectado").length;
            const cumpleProyectado = proyeccion.filas.filter((f) => f.estado_proyeccion === "cumple_proyectado").length;
            const noCumpleProyectado = conMeta - cumpleProyectado;
            const pctHoy = conMeta > 0 ? ((cumpleHoy / conMeta) * 100).toFixed(1) : "0.0";
            const pctProyectado = conMeta > 0 ? ((cumpleProyectado / conMeta) * 100).toFixed(1) : "0.0";
            const pctNoCumpleProyectado = conMeta > 0 ? ((noCumpleProyectado / conMeta) * 100).toFixed(1) : "0.0";
            return (
              <div>
                <h3 style={{ margin: "0 0 8px 0", fontSize: "14px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}>
                  <Ic name="trendUp" color={CLARO_RED} />Pronóstico de cierre de mes
                </h3>
                <ResumenEjecutivoPronostico
                  noCumple={noCumpleProyectado}
                  pctNoCumple={pctNoCumpleProyectado}
                  cumpleProyectado={cumpleProyectado}
                  pctProyectado={pctProyectado}
                  pctHoy={pctHoy}
                  fechaObjetivo={proyeccion.fecha_objetivo}
                />
              </div>
            );
          })()}

          {/* Evolución día a día del cumplimiento, integrada en una sola
              gráfica gerencial: barras apiladas Cumple/No cumple + línea de %
              + badges de Nuevas/Salieron — ya no hace falta la tabla aparte
              de "Resumen por corte", toda esa información está aquí. */}
          <div style={{ ...card, overflowX: "auto" }}>
            <h3 style={{ margin: "0 0 4px 0", fontSize: "14px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="layers" color={CLARO_RED} />Cumplimiento SLA día a día</h3>
            <div style={{ fontSize: "12px", color: CLARO_MUTED, marginBottom: "12px" }}>
              Backlog por corte (Cumple/No cumple SLA), % de cumplimiento y movimiento (▲ nuevas, ▼ salieron).
            </div>
            <ComboChartSlaDiaDia historico={historico} />
          </div>

          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "16px" }}>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Cumplimiento SLA</h3>
              <DonutSLA cumplen={kpis.cumplen_sla} noCumplen={kpis.no_cumplen_sla} />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Backlog por rango de antigüedad</h3>
              <BarraHorizontal datos={porAntiguedadAgrupado} maxItems={10} colorFn={() => CAT_COLORS[0]} />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Backlog por aliado técnico</h3>
              <BarraHorizontal datos={(resumen.por_aliado || []).map((r) => ({ label: r.aliado_tecnico, total: r.total }))} />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Backlog por jefe de zona</h3>
              <BarraHorizontal datos={(resumen.por_jefe || []).map((r) => ({ label: r.jefe, total: r.total }))} />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Backlog por categoría de validación</h3>
              <BarraHorizontal
                datos={(resumen.por_categoria || []).map((r) => ({ label: r.categoria, total: r.total }))}
                colorFn={(d) => CATEGORIA_COLOR[d.label] || CLARO_MUTED}
              />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Top causas (diagnóstico)</h3>
              <BarraHorizontal datos={(resumen.top_causas || []).map((r) => ({ label: r.diagnostico, total: r.total }))} colorFn={() => CAT_COLORS[1]} />
            </div>
            <div style={card}>
              <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", color: CLARO_DARK }}>Observaciones más frecuentes (contratistas)</h3>
              <BarraHorizontal datos={(resumen.top_observaciones || []).map((r) => ({ label: r.observacion, total: r.total }))} colorFn={() => CAT_COLORS[2]} />
            </div>
          </div>

          <div style={{ ...card, overflowX: "auto", boxShadow: SHADOW_SM }}>
            <h3 style={{ margin: "0 0 14px 0", fontSize: "14px", fontWeight: "700", color: CLARO_DARK, display: "flex", alignItems: "center", gap: "7px" }}><Ic name="folder" color={CLARO_RED} />Backlog por municipio — cumplimiento e impacto</h3>
            <TablaPivoteMunicipio
              datos={resumen.por_municipio}
              colorPct={colorPct}
              pctTexto={pctTexto}
              pctNum={pctNum}
              colorImpacto={colorImpacto}
              pctImpactoTexto={pctImpactoTexto}
              pctImpactoNum={pctImpactoNum}
            />
          </div>
        </div>
      )}
    </div>
  );
}

export default BacklogR3;
